import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  login,
  signup,
  getCurrentUser,
  logout,
  forgotPassword,
  resetPassword,
  getFiles,
  getReports,
  uploadFile,
  analyseRiskV2,
  deleteFile,
  connectGithub,
  getGithubStatus,
  disconnectGithub,
  connectJira,
  getJiraStatus,
  disconnectJira,
  fetchJiraStory,
  listJiraQueries,
  saveJiraQuery,
  deleteJiraQuery,
  fetchJiraDefects,
  previewDocument,
  generateTestCases,
  generateTestAutomation,
  generateTestData,
  submitFeedback,
  getFeedback,
  adminListUsers,
  adminApproveUser,
  adminRejectUser,
  adminDisableUser,
  adminEnableUser,
  adminDeleteUser,
  adminUpdateUserPermissions,
  adminGetFeedbackStats,
  adminGetGuardrailsStats,
  getKbCapacity
} from './services/api';
import { stopMarketingAnalytics, resumeMarketingAnalytics } from './services/googleAnalytics';
import { initAzureMonitor, setUserContext, clearUserContext, trackPageView } from './services/azureMonitor';
import EmergeAILogo from './assets/emergeai-logo.svg';
import { jsPDF } from 'jspdf';
import GitHubOrgRepoDropdown from './components/GitHubOrgRepoDropdown';
import MultiAgentResultsView from './components/MultiAgentResultsView';


// Thumbs up/down on a generated artifact (risk report, test cases, or automation specs).
// Driven entirely by props: `value` is { verdict, reason } | undefined, `onSubmit(verdict, reason)`
// is called on every click so the parent owns persistence and optimistic state.
const FEEDBACK_REASONS = ['Wrong', 'Incomplete', 'Not relevant', 'Too generic'];

const FeedbackWidget = ({ value, onSubmit }) => {
  const [verdict, setVerdict] = useState(value?.verdict || null);
  const [reason, setReason] = useState(value?.reason || null);
  const [comment, setComment] = useState(value?.comment || '');
  const [savedStatus, setSavedStatus] = useState(!!value?.verdict);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setVerdict(value?.verdict || null);
    setReason(value?.reason || null);
    setComment(value?.comment || '');
    setSavedStatus(!!value?.verdict);
  }, [value?.verdict, value?.reason, value?.comment]);

  const isDirty = 
    verdict !== (value?.verdict || null) ||
    reason !== (value?.reason || null) ||
    comment !== (value?.comment || '');

  const handleVerdictClick = async (newVerdict) => {
    const targetVerdict = verdict === newVerdict ? null : newVerdict;
    const targetReason = targetVerdict === 'down' ? reason : null;
    setVerdict(targetVerdict);
    setReason(targetReason);
    setSavedStatus(false);
    if (targetVerdict) {
      setSaving(true);
      try {
        await onSubmit(targetVerdict, targetReason, comment || null);
        setSavedStatus(true);
      } catch (err) {
        console.error(err);
      } finally {
        setSaving(false);
      }
    }
  };

  const handleReasonClick = async (r) => {
    const targetReason = reason === r ? null : r;
    setReason(targetReason);
    setSavedStatus(false);
    if (verdict) {
      setSaving(true);
      try {
        await onSubmit(verdict, targetReason, comment || null);
        setSavedStatus(true);
      } catch (err) {
        console.error(err);
      } finally {
        setSaving(false);
      }
    }
  };

  const handleSave = async () => {
    if (!verdict) return;
    setSaving(true);
    try {
      await onSubmit(verdict, verdict === 'down' ? reason : null, comment || null);
      setSavedStatus(true);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-2.5 p-3 rounded-xl bg-slate-50/80 border border-slate-200/80 my-2">
      <div className="flex items-center gap-2 flex-wrap justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wide">
            Was this output helpful?
          </span>
          <button
            type="button"
            onClick={() => handleVerdictClick('up')}
            aria-label="Thumbs up"
            className={`px-3 py-1 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 border ${
              verdict === 'up'
                ? 'bg-emerald-500 text-white border-emerald-600 shadow-sm'
                : 'bg-white text-[var(--color-text-muted)] border-slate-200 hover:bg-slate-100 hover:text-slate-800'
            }`}
          >
            <span>👍</span>
            <span>Yes</span>
          </button>
          <button
            type="button"
            onClick={() => handleVerdictClick('down')}
            aria-label="Thumbs down"
            className={`px-3 py-1 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 border ${
              verdict === 'down'
                ? 'bg-rose-500 text-white border-rose-600 shadow-sm'
                : 'bg-white text-[var(--color-text-muted)] border-slate-200 hover:bg-slate-100 hover:text-slate-800'
            }`}
          >
            <span>👎</span>
            <span>No</span>
          </button>

          {verdict === 'down' && (
            <div className="flex items-center gap-1 flex-wrap ml-1">
              {FEEDBACK_REASONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => handleReasonClick(r)}
                  className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium border transition-colors ${
                    reason === r
                      ? 'bg-[var(--color-navy)] text-white border-[var(--color-navy)] shadow-xs'
                      : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          )}
        </div>

        {savedStatus && !isDirty && (
          <span className="text-[11px] font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full flex items-center gap-1">
            <span>✓</span> Saved
          </span>
        )}
      </div>

      {verdict && (
        <div className="flex items-center gap-2 flex-wrap pt-1">
          <input
            type="text"
            value={comment}
            onChange={(e) => {
              setComment(e.target.value);
              setSavedStatus(false);
            }}
            placeholder="Add specific feedback or comments (optional)..."
            maxLength={2000}
            className="flex-1 min-w-[240px] px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-[12px] text-slate-800 focus:outline-none focus:ring-1 focus:ring-[var(--color-accent)] focus:border-[var(--color-accent)]"
          />
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className={`px-3.5 py-1.5 rounded-lg text-[12px] font-bold transition-all shadow-xs flex items-center gap-1 ${
              savedStatus && !isDirty
                ? 'bg-slate-100 text-slate-500 border border-slate-200 cursor-default'
                : 'bg-[var(--color-accent)] text-white hover:bg-[var(--color-navy)] active:scale-95'
            }`}
          >
            {saving ? 'Saving…' : savedStatus && !isDirty ? 'Saved ✓' : 'Save Feedback'}
          </button>
        </div>
      )}
    </div>
  );
};

const EmergeLogo = ({ className = "w-10 h-10", dark = false, iconOnly = false }) => (
  <a href="https://emergeaitech.com/" target="_blank" rel="noopener noreferrer" className={`${iconOnly ? "inline-block" : "flex items-center space-x-3"} hover:opacity-80 transition-opacity`}>
    <svg className={className} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="emergeGrad" x1="0%" y1="100%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#00f2fe" />
          <stop offset="100%" stopColor="#3b82f6" />
        </linearGradient>
      </defs>
      {/* Center dot and line */}
      <circle cx="50" cy="32" r="5" fill="url(#emergeGrad)" />
      <path d="M26 32 L46 32" stroke="url(#emergeGrad)" strokeWidth="6" strokeLinecap="round" />
      {/* Top dot and line */}
      <circle cx="50" cy="16" r="5" fill="url(#emergeGrad)" />
      <path d="M38 16 L46 16" stroke="url(#emergeGrad)" strokeWidth="6" strokeLinecap="round" />
      {/* Bottom dot and line */}
      <circle cx="50" cy="48" r="5" fill="url(#emergeGrad)" />
      <path d="M38 48 L46 48" stroke="url(#emergeGrad)" strokeWidth="6" strokeLinecap="round" />
      {/* Inner C curve */}
      <path d="M38 16 C16 16 16 48 38 48" stroke="url(#emergeGrad)" strokeWidth="6" strokeLinecap="round" />
      {/* Outer gear boundary */}
      <path d="M34 6 C6 6 6 58 34 58" stroke="url(#emergeGrad)" strokeWidth="6" strokeLinecap="round" />
      {/* Outer gear teeth */}
      <path d="M14 10 C10 6 4 10 8 14 M6 24 C0 24 0 30 6 30 M6 34 C0 34 0 40 6 40 M14 54 C10 58 4 54 8 50" stroke="url(#emergeGrad)" strokeWidth="5" strokeLinecap="round" />
    </svg>
    {!iconOnly && (
      <div className="flex flex-col pt-1">
        <div className={`flex items-baseline ${dark ? 'text-white' : 'text-slate-800'}`}>
          <span className="text-3xl font-light tracking-wide leading-none" style={{fontFamily: "system-ui, -apple-system, sans-serif"}}>EmergeAI</span>
          <span className="text-[10px] font-medium ml-1 relative -top-3">™</span>
        </div>
        <div className="w-full h-[1.5px] bg-gradient-to-r from-[#00f2fe] to-[#3b82f6] mt-1 mb-1"></div>
        <span className="text-[14px] text-[#3b82f6] font-medium tracking-wide leading-none">Technologies</span>
      </div>
    )}
  </a>
);

// Small "?" badge that reveals an explanation on hover — used next to controls whose behavior
// isn't obvious from the label alone (e.g. why the category tab matters, what code impact does).
const InfoTooltip = ({ text }) => (
  <span className="relative inline-flex items-center group/tooltip ml-1.5 align-middle">
    <span className="w-[15px] h-[15px] rounded-full bg-[var(--color-border)] text-[var(--color-text-muted)] text-[10px] font-bold flex items-center justify-center cursor-help group-hover/tooltip:bg-[var(--color-accent)] group-hover/tooltip:text-white transition-colors">
      ?
    </span>
    <span className="pointer-events-none absolute left-1/2 bottom-full -translate-x-1/2 mb-2 w-[240px] px-3 py-2 rounded-lg bg-[var(--color-navy)] text-white text-[12px] leading-snug font-normal normal-case tracking-normal opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all duration-150 z-30 shadow-lg">
      {text}
      <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[var(--color-navy)]"></span>
    </span>
  </span>
);

function App() {
  // Authentication State
  const authPath = window.location.pathname.replace(/\/+$/, '') || '/';
  const isDedicatedAuthPage = ['/app', '/login', '/register'].includes(authPath);
  const initialAuthMode = authPath === '/register' ? 'signup' : 'login';
  const [user, setUser] = useState(null);
  const [landingView, setLandingView] = useState('home'); // 'home' | 'insights'
  // Front Door exposes the product at /app. Open authentication immediately on that
  // entry point instead of leaving visitors on the marketing page with no obvious form.
  const [showAuthForm, setShowAuthForm] = useState(isDedicatedAuthPage);
  const [authMode, setAuthMode] = useState(initialAuthMode); // 'login' | 'signup'
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authOrganization, setAuthOrganization] = useState('');
  const [authName, setAuthName] = useState('');
  const [authPhone, setAuthPhone] = useState('');
  const [authDesignation, setAuthDesignation] = useState('');
  const [authError, setAuthError] = useState(null);
  const [authNotice, setAuthNotice] = useState(null); // success/info messages (e.g. reset email sent)
  const [authLoading, setAuthLoading] = useState(false);
  const [resetToken, setResetToken] = useState(null); // present when user arrives via emailed reset link
  const [appInitializing, setAppInitializing] = useState(true);

  // Workspace Files State (loaded from backend). Active Targets requirements can hold up to 5
  // concurrent files (see backend/main.py _ingest_text_document); bug_report/test_case still
  // replace on upload, so those arrays never exceed length 1.
  const [uploadedFiles, setUploadedFiles] = useState({
    requirement: [],
    bug_report: [],
    test_case: [],
  });

  const [previousFiles, setPreviousFiles] = useState({
    requirement: [],
    bug_report: [],
    test_case: [],
  });

  // History / Reports State
  const [pastReports, setPastReports] = useState([]);
  const [selectedReportId, setSelectedReportId] = useState(null);

  // Admin: user access management & feedback analytics
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState(null);
  const [adminActionId, setAdminActionId] = useState(null); // user id with an in-flight approve/disable
  const [adminShowDeleted, setAdminShowDeleted] = useState(false); // deleted accounts are hidden by default
  const [adminFeedbackStats, setAdminFeedbackStats] = useState(null);
  const [adminFeedbackLoading, setAdminFeedbackLoading] = useState(false);
  const [adminGuardrailsStats, setAdminGuardrailsStats] = useState(null);
  const [adminGuardrailsLoading, setAdminGuardrailsLoading] = useState(false);
  const [adminSubTab, setAdminSubTab] = useState('uam'); // 'uam' | 'feedback' | 'observability'
  const [uamSearchQuery, setUamSearchQuery] = useState('');
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [addUserForm, setAddUserForm] = useState({ name: '', email: '', organization: '', designation: '', password: '', can_access_feedback: true, can_access_observability: true });
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [addUserMsg, setAddUserMsg] = useState(null);
  const [pendingPermissions, setPendingPermissions] = useState({});
  const [savingPermissions, setSavingPermissions] = useState(false);

  // UI Navigation & Controls
  const [workspaceTab, setWorkspaceTab] = useState('current'); // 'current' | 'previous' | 'history'
  const [activeView, setActiveView] = useState('dashboard'); // 'dashboard' | 'reports' | 'integrations' — top nav destination
  const [selectedType, setSelectedType] = useState('requirement'); // 'requirement' | 'bug_report' | 'test_case'
  const [customInstruction, setCustomInstruction] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // Warns when a filename hints at a different artifact category than the one currently
  // selected (e.g. uploading "bug_report.csv" while the Requirements tab is active) — the
  // category is set by whichever tab is active at upload time, not inferred from the file.
  const [categoryMismatchConfirm, setCategoryMismatchConfirm] = useState(null); // { file, isPrevious, suggestedType }
  const [pendingPreviousFiles, setPendingPreviousFiles] = useState([]);

  // Analysis state
  const [isAnalysing, setIsAnalysing] = useState(false);
  const [saveToHistorical, setSaveToHistorical] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);

  const [activeTab, setActiveTab] = useState('summary'); // 'summary' | 'matrix'

  // KB storage-limit modal — Flow 1 (automatic save pre-analysis warning).
  // Set to { capacityStatus } to open the modal; null to close it.
  // The modal's Continue/Cancel actions drive the analysis execution directly.
  const [kbLimitModal, setKbLimitModal] = useState(null);

  // KB storage-limit modal — Flow 2 (manual Historical KB upload pre-check).
  // Set to { capacityStatus, allowedFiles, blockedFiles, onProceed, onManage, onCancel }.
  const [kbManualLimitModal, setKbManualLimitModal] = useState(null);

  // Post-analysis KB save summary toast.
  // Set to { saved: [...], skipped_limit: [...], skipped_duplicate: [...] } to display.
  const [kbSaveSummary, setKbSaveSummary] = useState(null);

  // Sorting for Matrix Table
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });

  // File input reference for resetting selection
  const fileInputRef = useRef(null);

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { type, isPrevious, fileId, filename }

  // Historical document scope selection (shown before running analysis, when historical files exist)
  const [showHistoricalSelector, setShowHistoricalSelector] = useState(false);
  const [selectedHistoricalIds, setSelectedHistoricalIds] = useState(new Set());

  // GitHub code-impact connection (MVP): link one repo, searched live at analysis time.
  const [githubStatus, setGithubStatus] = useState(null); // { connected, username, avatar_url, repo_full_name }
  const [githubTokenInput, setGithubTokenInput] = useState('');
  const [githubRepoInput, setGithubRepoInput] = useState('');
  const [githubConnecting, setGithubConnecting] = useState(false);
  const [githubError, setGithubError] = useState(null);
  const [includeCodeImpact, setIncludeCodeImpact] = useState(false);

  // Jira Cloud connection: pull a story into the Dashboard as the current requirement.
  const [jiraStatus, setJiraStatus] = useState(null); // { connected, base_url, account_email, display_name }
  const [jiraUrlInput, setJiraUrlInput] = useState('');
  const [jiraEmailInput, setJiraEmailInput] = useState('');
  const [jiraTokenInput, setJiraTokenInput] = useState('');
  const [jiraConnecting, setJiraConnecting] = useState(false);
  const [jiraError, setJiraError] = useState(null);
  const [jiraStoryInput, setJiraStoryInput] = useState('');
  const [jiraFetching, setJiraFetching] = useState(false);
  const [jiraFetchError, setJiraFetchError] = useState(null);
  // JQL defect fetch (Bug Reports slot) + saved queries (max 5, server-enforced)
  const [jiraSavedQueries, setJiraSavedQueries] = useState([]);
  const [selectedSavedQueryId, setSelectedSavedQueryId] = useState('');
  // 'upload' | 'jira' — which source is shown for the current category. Reset to
  // 'upload' whenever the category or tab changes so a stale Jira pane can't linger
  // somewhere it doesn't apply (e.g. requirement + Historical KB has no Jira source).
  const [docSourceMode, setDocSourceMode] = useState('upload');
  const [jiraJqlInput, setJiraJqlInput] = useState('');
  const [jiraQueryNameInput, setJiraQueryNameInput] = useState('');
  const [jiraJqlFetching, setJiraJqlFetching] = useState(false);
  const [jiraJqlSaving, setJiraJqlSaving] = useState(false);
  const [jiraJqlError, setJiraJqlError] = useState(null);
  const [jiraJqlNotice, setJiraJqlNotice] = useState(null);

  // Document preview: shows the extracted text of a stored file so its contents can be
  // checked against the source (Jira story/defects, or an uploaded document).
  const [previewDoc, setPreviewDoc] = useState(null); // { filename, text, ... } once loaded
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Agentic test generation (downstream of an analysis run). Phase reflects which agent is
  // actually running right now — 'cases' and 'automation' are genuine backend calls, not a
  // simulated progress bar, since Agent 1 and Agent 2 are now separate requests.
  const [testAssets, setTestAssets] = useState(null);
  const [testGenPhase, setTestGenPhase] = useState('idle'); // 'idle' | 'cases' | 'automation' | 'test-data' | 'done'
  const [testGenError, setTestGenError] = useState(null);
  const testGenLoading = testGenPhase === 'cases' || testGenPhase === 'automation' || testGenPhase === 'test-data';

  // Selection checkboxes for test asset generation (Test Cases, Playwright Scripts, Test Data)
  const [selectedTestAssets, setSelectedTestAssets] = useState({
    cases: true,
    playwright: true,
    testData: true,
  });

  const handleToggleTestGenOption = (optionKey) => {
    setSelectedTestAssets((prev) => ({
      ...prev,
      [optionKey]: !prev[optionKey],
    }));
  };

  const handleToggleAllTestGenOptions = () => {
    setSelectedTestAssets((prev) => {
      const allSelected = prev.cases && prev.playwright && prev.testData;
      if (allSelected) {
        return { cases: false, playwright: false, testData: false };
      } else {
        return { cases: true, playwright: true, testData: true };
      }
    });
  };

  const getTestGenStatusText = () => {
    if (!testGenLoading) return testAssets ? 'Regenerate' : 'Generate Test Assets';

    const selectedNames = [];
    if (selectedTestAssets.cases) selectedNames.push('Test Cases');
    if (selectedTestAssets.playwright) selectedNames.push('Playwright Scripts');
    if (selectedTestAssets.testData) selectedNames.push('Test Data');

    if (testGenPhase === 'cases') {
      if (selectedTestAssets.cases) {
        if (selectedNames.length === 1) {
          return `Writing test cases… ${testGenElapsed}s`;
        }
        return `Writing test cases (1 of ${selectedNames.length})… ${testGenElapsed}s`;
      } else {
        const targetLabel = selectedNames.join(' & ');
        return `Preparing & generating ${targetLabel.toLowerCase()}… ${testGenElapsed}s`;
      }
    }

    if (testGenPhase === 'automation') {
      if (selectedTestAssets.playwright && selectedTestAssets.testData) {
        return `Generating Playwright scripts & test data… ${testGenElapsed}s`;
      } else if (selectedTestAssets.playwright) {
        return `Writing Playwright scripts… ${testGenElapsed}s`;
      } else if (selectedTestAssets.testData) {
        return `Generating test data… ${testGenElapsed}s`;
      }
    }

    if (testGenPhase === 'test-data') {
      return `Generating test data… ${testGenElapsed}s`;
    }

    return `Generating… ${testGenElapsed}s`;
  };

  const getTestGenHoverText = () => {
    const selectedItems = [];
    if (selectedTestAssets.cases) selectedItems.push('Test Cases (Structured cases)');
    if (selectedTestAssets.playwright) selectedItems.push('Playwright Scripts (Automation specs)');
    if (selectedTestAssets.testData) selectedItems.push('Test Data (Synthetic QA datasets)');

    if (selectedItems.length === 0) return 'No assets selected';

    if (testGenLoading) {
      return `Currently generating:\n• ${selectedItems.join('\n• ')}`;
    }
    return `Selected to generate:\n• ${selectedItems.join('\n• ')}`;
  };

  // Thumbs up/down on the current report's generated artifacts, keyed by feature
  // ('risk_report' | 'test_cases' | 'automation'). Reset on a fresh run, fetched from
  // the server when a historical report is reopened.
  const [feedback, setFeedback] = useState({});

  const handleFeedback = (feature, verdict, reason = null, comment = null) => {
    if (!analysisResult?.report_id) return;
    setFeedback((prev) => ({ ...prev, [feature]: { verdict, reason, comment } })); // optimistic
    submitFeedback(analysisResult.report_id, feature, verdict, reason, comment).catch((err) => {
      console.error('Failed to submit feedback:', err);
    });
  };

  // Live elapsed-second counters for the analysis and test-generation buttons, so the user has a
  // sense of progress during long-running LLM calls rather than a static "please wait".
  const [analysisElapsed, setAnalysisElapsed] = useState(0);
  const [testGenElapsed, setTestGenElapsed] = useState(0);

  useEffect(() => {
    if (!isAnalysing) return;
    setAnalysisElapsed(0);
    const interval = setInterval(() => setAnalysisElapsed((s) => s + 1), 1000);
    return () => clearInterval(interval);
  }, [isAnalysing]);

  useEffect(() => {
    if (!testGenLoading) return;
    setTestGenElapsed(0);
    const interval = setInterval(() => setTestGenElapsed((s) => s + 1), 1000);
    return () => clearInterval(interval);
  }, [testGenLoading]);

  // If the user arrived via an emailed reset link, open the reset-password view
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenFromLink = params.get('reset_token');
    if (tokenFromLink) {
      setResetToken(tokenFromLink);
      setAuthMode('reset');
      setShowAuthForm(true);
    }
  }, []);

  // Initialize session on startup
  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      getCurrentUser()
        .then((userData) => {
          setUser(userData);
          loadWorkspaceData();
        })
        .catch(() => {
          // Token expired or server restarted
          handleLogout();
        })
        .finally(() => {
          setAppInitializing(false);
        });
    } else {
      setAppInitializing(false);
    }
  }, []);

  // Google Analytics is scoped to the logged-out marketing pages only — the dashboard
  // renders customers' own requirements, bug reports, and risk analyses, which must
  // never be reported to a third-party analytics tool. The gtag.js tag itself always
  // loads (public/index.html, so Google's installation checker can detect it); this
  // Initialize Azure Monitor Application Insights on mount
  useEffect(() => {
    initAzureMonitor();
  }, []);

  useEffect(() => {
    if (appInitializing) return;
    if (user) {
      stopMarketingAnalytics();
      setUserContext(user.id, user.organization);
    } else {
      resumeMarketingAnalytics();
      clearUserContext();
    }
  }, [user, appInitializing]);

  useEffect(() => {
    trackPageView(activeView);
  }, [activeView]);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });
    
    // Add small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    }, 100);
    return () => { clearTimeout(timer); observer.disconnect(); };
  }, [user, showAuthForm, appInitializing, analysisResult, activeView]);

  // Fetch file logs and report runs from SQLite
  const loadWorkspaceData = () => {
    getFiles()
      .then((data) => {
        // Map current files. Most types replace (one entry), but requirement accumulates
        // up to 5 (see backend cap in _ingest_text_document).
        const currentMapped = { requirement: [], bug_report: [], test_case: [] };
        data.current.forEach((file) => {
          currentMapped[file.artifact_type].push(file);
        });
        setUploadedFiles(currentMapped);

        // Map historical files
        const historicalMapped = { requirement: [], bug_report: [], test_case: [] };
        data.historical.forEach((file) => {
          historicalMapped[file.artifact_type].push(file);
        });
        setPreviousFiles(historicalMapped);
      })
      .catch((err) => console.error("Failed to load user files:", err));

    getReports()
      .then((reportsList) => {
        setPastReports(reportsList);
      })
      .catch((err) => console.error("Failed to load user reports history:", err));

    getGithubStatus()
      .then((data) => setGithubStatus(data))
      .catch((err) => console.error("Failed to load GitHub connection status:", err));

    getJiraStatus()
      .then((data) => {
        setJiraStatus(data);
        if (data?.connected) {
          listJiraQueries()
            .then((q) => setJiraSavedQueries(q.queries || []))
            .catch((err) => console.error("Failed to load saved Jira queries:", err));
        }
      })
      .catch((err) => console.error("Failed to load Jira connection status:", err));
  };

  const handleGithubConnect = (e) => {
    e.preventDefault();
    if (!githubTokenInput.trim() || !githubRepoInput.trim()) {
      setGithubError('Enter both a token and select a repository.');
      return;
    }
    setGithubError(null);
    setGithubConnecting(true);
    connectGithub(githubTokenInput.trim(), githubRepoInput.trim())
      .then((data) => {
        setGithubStatus(data);
        setGithubTokenInput('');
        setGithubRepoInput('');
      })
      .catch((err) => {
        setGithubError(err.response?.data?.detail || 'Could not connect to GitHub. Check the token and repository name.');
      })
      .finally(() => setGithubConnecting(false));
  };

  const handleGithubDisconnect = () => {
    disconnectGithub()
      .then(() => {
        setGithubStatus({ connected: false });
        setIncludeCodeImpact(false);
      })
      .catch((err) => console.error("Failed to disconnect GitHub:", err));
  };

  const handleJiraConnect = (e) => {
    e.preventDefault();
    if (!jiraUrlInput.trim() || !jiraEmailInput.trim() || !jiraTokenInput.trim()) {
      setJiraError('Enter the site URL, account email, and API token.');
      return;
    }
    setJiraError(null);
    setJiraConnecting(true);
    connectJira(jiraUrlInput.trim(), jiraEmailInput.trim(), jiraTokenInput.trim())
      .then((data) => {
        setJiraStatus(data);
        setJiraTokenInput('');
      })
      .catch((err) => {
        setJiraError(err.response?.data?.detail || 'Could not connect to Jira. Check the URL, email, and token.');
      })
      .finally(() => setJiraConnecting(false));
  };

  const handleJiraDisconnect = () => {
    disconnectJira()
      .then(() => setJiraStatus({ connected: false }))
      .catch((err) => console.error("Failed to disconnect Jira:", err));
  };

  // Pull a Jira story in as the current requirement, then refresh the dashboard file list.
  const handleJiraFetchStory = () => {
    if (!jiraStoryInput.trim()) {
      setJiraFetchError('Enter a Jira story key or URL.');
      return;
    }
    if (workspaceTab !== 'previous' && uploadedFiles.requirement.length >= 5) {
      setJiraFetchError('Maximum 5 requirement files allowed in Active Targets. Please delete one before fetching another.');
      return;
    }
    setJiraFetchError(null);
    setJiraFetching(true);
    fetchJiraStory(jiraStoryInput.trim(), workspaceTab === 'previous')
      .then(() => {
        setJiraStoryInput('');
        loadWorkspaceData();
      })
      .catch((err) => {
        setJiraFetchError(err.response?.data?.detail || 'Could not fetch that Jira story.');
      })
      .finally(() => setJiraFetching(false));
  };

  // Run a JQL query and ingest the result set as a bug_report document. Destination
  // follows the active tab: current slot on Active Targets, Historical KB on previous.
  const handleJiraFetchDefects = (jqlOverride) => {
    const jql = (jqlOverride ?? jiraJqlInput).trim();
    if (!jql) {
      setJiraJqlError('Enter a JQL query.');
      return;
    }
    setJiraJqlError(null);
    setJiraJqlNotice(null);
    setJiraJqlFetching(true);
    fetchJiraDefects(jql, workspaceTab === 'previous')
      .then((data) => {
        setJiraJqlNotice(
          `Fetched ${data.issue_count} issue${data.issue_count === 1 ? '' : 's'}` +
          (data.has_more ? ' (more matched; first 50 included)' : '') +
          ` into ${workspaceTab === 'previous' ? 'Historical KB' : 'the current bug report'}.`
        );
        loadWorkspaceData();
      })
      .catch((err) => {
        setJiraJqlError(err.response?.data?.detail || 'Could not run that JQL query.');
      })
      .finally(() => setJiraJqlFetching(false));
  };

  const handleJiraSaveQuery = () => {
    const jql = jiraJqlInput.trim();
    if (!jql) {
      setJiraJqlError('Enter a JQL query to save.');
      return;
    }
    setJiraJqlError(null);
    setJiraJqlSaving(true);
    saveJiraQuery(jql, jiraQueryNameInput.trim() || null)
      .then((saved) => {
        setJiraSavedQueries((prev) => [...prev, saved]);
        setJiraQueryNameInput('');
      })
      .catch((err) => {
        setJiraJqlError(err.response?.data?.detail || 'Could not save the query.');
      })
      .finally(() => setJiraJqlSaving(false));
  };

  const handleJiraDeleteQuery = (queryId) => {
    deleteJiraQuery(queryId)
      .then(() => {
        setJiraSavedQueries((prev) => prev.filter((q) => q.id !== queryId));
        setSelectedSavedQueryId((prev) => (prev === queryId ? '' : prev));
      })
      .catch((err) => setJiraJqlError(err.response?.data?.detail || 'Could not delete the query.'));
  };

  // Picking a saved query only fills the JQL box — the user still clicks Fetch to run it,
  // so selecting a query by accident (or to review/edit it first) doesn't trigger a fetch.
  const handleSelectSavedQuery = (queryId) => {
    setSelectedSavedQueryId(queryId);
    if (!queryId) return;
    const q = jiraSavedQueries.find((x) => x.id === queryId);
    if (!q) return;
    setJiraJqlInput(q.jql);
  };

  const handlePreviewDocument = (file) => {
    if (!file?.document_id) return;
    setPreviewError(null);
    setPreviewLoading(true);
    // Open immediately with what we know so the modal isn't a blank flash.
    setPreviewDoc({ filename: file.filename, text: null });
    previewDocument(file.document_id)
      .then((data) => setPreviewDoc(data))
      .catch((err) => setPreviewError(err.response?.data?.detail || 'Could not load this document.'))
      .finally(() => setPreviewLoading(false));
  };

  const closePreview = () => {
    setPreviewDoc(null);
    setPreviewError(null);
  };

  // Auth Submit Handlers
  const handleAuthSubmit = (e) => {
    e.preventDefault();
    setAuthError(null);
    setAuthNotice(null);

    // Forgot password: only an email is needed; backend always answers generically.
    if (authMode === 'forgot') {
      if (!authEmail.trim()) {
        setAuthError('Please enter your email address.');
        return;
      }
      setAuthLoading(true);
      forgotPassword(authEmail.trim())
        .then((data) => {
          setAuthNotice(data.message || 'If an account exists for this email, a reset link has been sent.');
        })
        .catch((err) => {
          setAuthError(err.response?.data?.detail || 'Could not send reset email. Please try again.');
        })
        .finally(() => setAuthLoading(false));
      return;
    }

    // Reset password: user arrived via emailed link carrying the token.
    if (authMode === 'reset') {
      if (!authPassword.trim() || authPassword.length < 8) {
        setAuthError('Password must be at least 8 characters.');
        return;
      }
      setAuthLoading(true);
      resetPassword(resetToken, authPassword)
        .then((data) => {
          setAuthNotice(data.message || 'Password updated. You can now log in.');
          setAuthMode('login');
          setAuthPassword('');
          setResetToken(null);
          // Drop the token from the URL so a refresh doesn't reopen the reset view
          window.history.replaceState({}, '', window.location.pathname);
        })
        .catch((err) => {
          setAuthError(err.response?.data?.detail || 'Reset failed. The link may have expired.');
        })
        .finally(() => setAuthLoading(false));
      return;
    }

    // Login / signup
    if (!authEmail.trim() || !authPassword.trim()) {
      setAuthError('Please enter both email and password.');
      return;
    }
    if (authMode === 'signup') {
      if (!authName.trim()) { setAuthError('Please enter your name.'); return; }
      if (!authOrganization.trim()) { setAuthError('Please enter your organization name.'); return; }
      if (!authPhone.trim()) { setAuthError('Please enter your phone number.'); return; }
      if (!authDesignation.trim()) { setAuthError('Please enter your designation.'); return; }
      if (authPassword.length < 8) { setAuthError('Password must be at least 8 characters.'); return; }
    }
    setAuthLoading(true);

    if (authMode === 'signup') {
      // Signup is an access request: no session starts; the user logs in after approval.
      signup(authEmail.trim(), authPassword, authName.trim(), authOrganization.trim(), authPhone.trim(), authDesignation.trim())
        .then((data) => {
          setAuthNotice(data.message || "Your access request has been submitted. You'll be able to log in once it's approved.");
          setAuthMode('login');
          setAuthPassword('');
        })
        .catch((err) => {
          const detail = err.response?.data?.detail;
          const message = Array.isArray(detail)
            ? (detail[0]?.msg || 'Please check your input.')
            : (detail || 'Signup failed. Please verify your details.');
          setAuthError(message);
        })
        .finally(() => setAuthLoading(false));
      return;
    }

    login(authEmail.trim(), authPassword)
      .then((data) => {
        setUser(data.user);
        loadWorkspaceData();
      })
      .catch((err) => {
        const detail = err.response?.data?.detail;
        // Pydantic validation errors arrive as an array of objects
        const message = Array.isArray(detail)
          ? (detail[0]?.msg || 'Please check your input.')
          : (detail || 'Authentication failed. Please verify credentials.');
        setAuthError(message);
      })
      .finally(() => {
        setAuthLoading(false);
      });
  };

  const loadAdminUsers = (includeDeleted = adminShowDeleted) => {
    setAdminLoading(true);
    setAdminError(null);
    adminListUsers(includeDeleted)
      .then((data) => setAdminUsers(data.users || []))
      .catch((err) => setAdminError(err.response?.data?.detail || 'Could not load users.'))
      .finally(() => setAdminLoading(false));
  };

  const loadAdminFeedbackStats = () => {
    setAdminFeedbackLoading(true);
    adminGetFeedbackStats()
      .then((data) => setAdminFeedbackStats(data))
      .catch((err) => console.error('Failed to load admin feedback stats:', err))
      .finally(() => setAdminFeedbackLoading(false));
  };

  // Load guardrails stats for the AI Observability dashboard
  const loadAdminGuardrailsStats = () => {
    setAdminGuardrailsLoading(true);
    adminGetGuardrailsStats()
      .then((data) => setAdminGuardrailsStats(data))
      .catch((err) => console.error('Failed to load guardrails stats:', err))
      .finally(() => setAdminGuardrailsLoading(false));
  };

  // Refresh the user list & feedback analytics whenever the admin opens the Admin view, or toggles
  // whether deleted accounts are shown.
  useEffect(() => {
    if (['admin', 'uam', 'feedback', 'observability'].includes(activeView) && user?.is_admin) {
      loadAdminUsers(adminShowDeleted);
      loadAdminFeedbackStats();
      loadAdminGuardrailsStats();
    } else if (activeView === 'feedback' && user?.can_access_feedback) {
      loadAdminFeedbackStats();
    } else if (activeView === 'observability' && user?.can_access_observability) {
      loadAdminGuardrailsStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, user, adminShowDeleted]);

  const ADMIN_ACTIONS = {
    approve: adminApproveUser,
    reject: adminRejectUser,
    disable: adminDisableUser,
    enable: adminEnableUser,
    delete: adminDeleteUser,
  };

  const handleAdminAction = (targetUser, action) => {
    if (action === 'delete' && !window.confirm(
      `Delete ${targetUser.email}?\n\nThey'll lose access immediately and disappear from this list. ` +
      `Their data is kept, so you can restore the account later from "Show deleted".`
    )) return;

    setAdminActionId(targetUser.id);
    setAdminError(null);
    ADMIN_ACTIONS[action](targetUser.id)
      .then(() => loadAdminUsers(adminShowDeleted))
      .catch((err) => setAdminError(err.response?.data?.detail || `Could not ${action} ${targetUser.email}.`))
      .finally(() => setAdminActionId(null));
  };

  const handleTogglePermission = (targetUser, featureKey) => {
    if (targetUser.id === user?.id) return;
    const currentFeedback = pendingPermissions[targetUser.id]?.can_access_feedback ?? (targetUser.can_access_feedback !== false);
    const currentObservability = pendingPermissions[targetUser.id]?.can_access_observability ?? (targetUser.can_access_observability !== false);

    const updated = {
      can_access_feedback: featureKey === 'feedback' ? !currentFeedback : currentFeedback,
      can_access_observability: featureKey === 'observability' ? !currentObservability : currentObservability,
    };

    const isSameAsOriginal =
      updated.can_access_feedback === (targetUser.can_access_feedback !== false) &&
      updated.can_access_observability === (targetUser.can_access_observability !== false);

    const nextPending = { ...pendingPermissions };
    if (isSameAsOriginal) {
      delete nextPending[targetUser.id];
    } else {
      nextPending[targetUser.id] = updated;
    }
    setPendingPermissions(nextPending);
  };

  const handleSavePermissions = async () => {
    const uids = Object.keys(pendingPermissions);
    if (uids.length === 0) return;
    setSavingPermissions(true);
    setAdminError(null);
    try {
      for (const uid of uids) {
        await adminUpdateUserPermissions(uid, pendingPermissions[uid]);
      }
      setAdminUsers((prev) =>
        prev.map((u) =>
          pendingPermissions[u.id] ? { ...u, ...pendingPermissions[u.id] } : u
        )
      );
      setPendingPermissions({});
      setAdminError(null);
    } catch (err) {
      setAdminError(err.response?.data?.detail || 'Failed to save permission changes.');
    } finally {
      setSavingPermissions(false);
    }
  };

  const handleDiscardPermissions = () => {
    setPendingPermissions({});
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!addUserForm.email || !addUserForm.password) {
      setAddUserMsg({ type: 'error', text: 'Email and password are required.' });
      return;
    }
    setAddUserLoading(true);
    setAddUserMsg(null);
    try {
      const createdUser = await signup(
        addUserForm.email,
        addUserForm.password,
        addUserForm.name,
        addUserForm.organization || 'Organization',
        addUserForm.designation || 'Team Member'
      );
      
      // If permissions were toggled off, update new user's permissions
      if (createdUser?.id && (addUserForm.can_access_feedback === false || addUserForm.can_access_observability === false)) {
        await adminUpdateUserPermissions(createdUser.id, {
          can_access_feedback: addUserForm.can_access_feedback,
          can_access_observability: addUserForm.can_access_observability,
        });
      }

      setAddUserMsg({ type: 'success', text: `User ${addUserForm.email} created successfully!` });
      setAddUserForm({ name: '', email: '', organization: '', designation: '', password: '', can_access_feedback: true, can_access_observability: true });
      loadAdminUsers(adminShowDeleted);
      setTimeout(() => {
        setShowAddUserModal(false);
        setAddUserMsg(null);
      }, 1500);
    } catch (err) {
      setAddUserMsg({ type: 'error', text: err.response?.data?.detail || 'Failed to create user.' });
    } finally {
      setAddUserLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    setUser(null);
    setUploadedFiles({ requirement: [], bug_report: [], test_case: [] });
    setPreviousFiles({ requirement: [], bug_report: [], test_case: [] });
    setPastReports([]);
    setAnalysisResult(null);
    setSelectedReportId(null);
    setAuthEmail('');
    setAuthPassword('');
    setAuthOrganization('');
    setAuthName('');
    setAuthPhone('');
    setAuthDesignation('');
    setAuthNotice(null);
  };

  // File drag-drop visual handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // Keywords that hint at a filename's likely artifact category. Used only as a soft warning
  // before upload — the category actually stored is always whichever tab was selected.
  const CATEGORY_KEYWORDS = {
    requirement: ['requirement', 'requirements', 'spec', 'specs'],
    bug_report: ['bug', 'bugs', 'defect', 'defects', 'issue', 'issues'],
    test_case: ['test', 'tests', 'testcase', 'testcases'],
  };
  const CATEGORY_LABELS = {
    requirement: 'Requirements',
    bug_report: 'Bug Reports',
    test_case: 'Test Cases',
  };

  // Returns the artifact type a filename hints at, if it clearly hints at one OTHER than the
  // currently selected category. Returns null when the name matches the selected category (even
  // if it also loosely matches another) or matches no known keyword at all.
  const detectCategoryMismatch = (filename, currentType) => {
    const lower = filename.toLowerCase();
    const matchesSelected = CATEGORY_KEYWORDS[currentType].some(kw => lower.includes(kw));
    if (matchesSelected) return null;
    for (const [type, keywords] of Object.entries(CATEGORY_KEYWORDS)) {
      if (type === currentType) continue;
      if (keywords.some(kw => lower.includes(kw))) return type;
    }
    return null;
  };

  // Upload file pipeline
  const validateAndUploadFile = (file) => {
    if (!file) return;

    const allowedExtensions = ['.pdf', '.docx', '.xlsx', '.xls', '.csv'];
    const fileName = file.name.toLowerCase();
    const isValid = allowedExtensions.some(ext => fileName.endsWith(ext));

    if (!isValid) {
      setUploadError('Only PDF, DOCX, XLSX, and CSV files are accepted.');
      return;
    }

    // Validate file size (5 MB limit)
    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      setUploadError(`File exceeds 5 MB limit. Your file is ${(file.size / (1024 * 1024)).toFixed(1)} MB.`);
      return;
    }

    setUploadError(null);
    const isPrevious = (workspaceTab === 'previous');

    const suggestedType = detectCategoryMismatch(file.name, selectedType);
    if (suggestedType) {
      setCategoryMismatchConfirm({ file, isPrevious, suggestedType });
      return;
    }

    stageOrUploadFile(file, isPrevious);
  };

  // The actual staging/upload logic, separated so it can run either immediately (no mismatch
  // detected) or after the user confirms past a category mismatch warning.
  //
  // Flow 2 addition: when isPrevious is true and the target type is at capacity, we fetch
  // the live KB status and show the KBManualLimitModal instead of a silent static error.
  const stageOrUploadFile = (file, isPrevious) => {
    if (isPrevious) {
      // Deduplicate checks first (these don't need a server round-trip)
      const alreadyPending = pendingPreviousFiles.some(
        item => item.file.name === file.name && item.type === selectedType
      );
      if (alreadyPending) {
        setUploadError('This file is already selected for upload in this category.');
        return;
      }
      const alreadyInKB = previousFiles[selectedType].some(
        existing => existing.filename === file.name
      );
      if (alreadyInKB) {
        setUploadError('This file already exists in your Reference Knowledge Base. Delete it first if you want to re-upload.');
        return;
      }

      // Check if Historical KB category is at the 10-file limit.
      // Count committed files + already-pending files of the same type.
      const pendingCountForType = pendingPreviousFiles.filter(item => item.type === selectedType).length;
      const committedCount = previousFiles[selectedType].length;
      const projectedCount = committedCount + pendingCountForType;

      if (projectedCount >= 10) {
        // Fetch live capacity so the modal can show accurate numbers for all types.
        getKbCapacity()
          .then((capacityStatus) => {
            setKbManualLimitModal({
              capacityStatus,
              allowedFiles: [],        // nothing can be added right now
              blockedFiles: [{ file, type: selectedType }],
              onProceed: null,         // no allowed files to upload
              onManage: () => {
                setKbManualLimitModal(null);
                setWorkspaceTab('previous');
              },
              onCancel: () => setKbManualLimitModal(null),
            });
          })
          .catch(() => {
            // Fallback: surface the plain error if the capacity check fails
            setUploadError(`Maximum 10 files allowed per category in Historical KB. Please delete one before uploading.`);
          });
        return;
      }

      // Space is available — stage the file
      setPendingPreviousFiles(prev => [...prev, { file, type: selectedType }]);
    } else {
      // Requirements accumulate up to 5 in Active Targets; other types still replace.
      if (selectedType === 'requirement' && uploadedFiles.requirement.length >= 5) {
        setUploadError('Maximum 5 requirement files allowed in Active Targets. Please delete one before uploading.');
        return;
      }
      setUploadingFile(true);
      uploadFile(file, selectedType, false)
        .then((data) => {
          setUploadedFiles(prev => ({
            ...prev,
            [selectedType]: selectedType === 'requirement' ? [...prev[selectedType], data] : [data]
          }));
          loadWorkspaceData(); // Refresh list to reflect backend storage sync
        })
        .catch((err) => {
          console.error("File upload failed:", err);
          setUploadError(err.response?.data?.detail || 'Failed to upload document.');
        })
        .finally(() => {
          setUploadingFile(false);
        });
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndUploadFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileBrowse = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      validateAndUploadFile(e.target.files[0]);
      // Reset file input so re-uploading the same file triggers onChange again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Show delete confirmation dialog
  const handleRemoveFile = (type, isPrevious = false, fileId = null) => {
    let targetFileId = fileId;
    let filename = '';
    if (!isPrevious) {
      const file = uploadedFiles[type].find(f => f.document_id === fileId) || uploadedFiles[type][0];
      targetFileId = fileId || file?.document_id || null;
      filename = file?.filename || 'Unknown file';
    } else if (fileId) {
      const file = previousFiles[type].find(f => f.document_id === fileId);
      filename = file?.filename || 'Unknown file';
    }

    setDeleteConfirm({ type, isPrevious, fileId: targetFileId, filename });
  };

  // Perform the actual deletion after confirmation
  const confirmDeleteFile = () => {
    if (!deleteConfirm) return;

    const { type, isPrevious, fileId } = deleteConfirm;
    if (fileId) {
      deleteFile(fileId)
        .then(() => {
          loadWorkspaceData();
          if (!isPrevious) {
            setUploadedFiles(prev => ({ ...prev, [type]: prev[type].filter(f => f.document_id !== fileId) }));
          }
          setUploadError(null);
        })
        .catch((err) => {
          console.error("Failed to delete file:", err);
          setUploadError(err.response?.data?.detail || 'Failed to delete file.');
        })
        .finally(() => {
          setDeleteConfirm(null);
        });
    }
  };

  // Upload pending references (Historical KB batch upload).
  //
  // Flow 2 batch addition: fetches live capacity before uploading. Splits the pending
  // list into allowed (space available) and blocked (type at capacity). If any are
  // blocked, shows the KBManualLimitModal so the user can choose to upload only the
  // allowed ones, navigate to manage the KB, or cancel the whole batch.
  const handleUploadPreviousDocuments = async () => {
    if (pendingPreviousFiles.length === 0) return;

    // Fetch live capacity to classify the pending batch
    let capacityStatus;
    try {
      capacityStatus = await getKbCapacity();
    } catch {
      // If capacity check fails, proceed optimistically and let the backend report errors
      capacityStatus = null;
    }

    if (capacityStatus) {
      // Classify each pending item against live counts.
      // We compute effective counts as we iterate (a batch may have multiple files of the
      // same type, and each successive one raises the effective count by 1).
      const effectiveCounts = {
        requirement: capacityStatus.requirement.current_count,
        bug_report:  capacityStatus.bug_report.current_count,
        test_case:   capacityStatus.test_case.current_count,
      };
      const allowed = [];
      const blocked = [];

      for (const item of pendingPreviousFiles) {
        if (effectiveCounts[item.type] < 10) {
          allowed.push(item);
          effectiveCounts[item.type] += 1;
        } else {
          blocked.push(item);
        }
      }

      if (blocked.length > 0) {
        // Some files can't be uploaded — show the modal.
        setKbManualLimitModal({
          capacityStatus,
          allowedFiles: allowed,
          blockedFiles: blocked,
          onProceed: allowed.length > 0
            ? async () => {
                setKbManualLimitModal(null);
                // Upload only the allowed subset
                setUploadingFile(true);
                setUploadError(null);
                try {
                  for (const item of allowed) {
                    await uploadFile(item.file, item.type, true);
                  }
                  // Remove uploaded items from staging; keep the blocked ones cleared too
                  setPendingPreviousFiles([]);
                  loadWorkspaceData();
                } catch (err) {
                  console.error("Failed to upload reference documents:", err);
                  setUploadError(err.response?.data?.detail || 'Failed to upload reference files.');
                } finally {
                  setUploadingFile(false);
                }
              }
            : null,
          onManage: () => {
            setKbManualLimitModal(null);
            setWorkspaceTab('previous');
          },
          onCancel: () => setKbManualLimitModal(null),
        });
        return;
      }
      // All items fit — fall through to normal upload
    }

    // All items are within capacity — upload normally
    setUploadingFile(true);
    setUploadError(null);

    try {
      for (const item of pendingPreviousFiles) {
        await uploadFile(item.file, item.type, true);
      }
      setPendingPreviousFiles([]);
      loadWorkspaceData();
    } catch (err) {
      console.error("Failed to upload reference documents:", err);
      setUploadError(err.response?.data?.detail || 'Failed to upload reference files.');
    } finally {
      setUploadingFile(false);
    }
  };

  // Run AI Risk Prediction
  const totalHistoricalCount = Object.values(previousFiles).flat().length;

  // Entry point for the "Analyze Release Risks" button. If any Historical KB files exist,
  // open a selector so the user can scope which ones feed the analysis (defaulting to all,
  // preserving prior behavior for anyone who just clicks through). Otherwise run immediately.
  //
  // Flow 1 addition: when saveToHistorical is checked, we pre-validate KB capacity
  // BEFORE the analysis starts. If any type is full, we show the KBLimitModal and let
  // the user decide to continue (skipping full types) or cancel.
  const handleRunAnalysis = () => {
    const activeTypes = Object.entries(uploadedFiles)
      .filter(([_, files]) => files.length > 0)
      .map(([type]) => type);

    if (activeTypes.length === 0 || !canRunAnalysis) return;

    if (saveToHistorical) {
      // Pre-check KB capacity. If anything is full, show a modal before proceeding.
      getKbCapacity()
        .then((capacityStatus) => {
          if (capacityStatus.any_full) {
            // Store the historical-selector ids we want to use once the user confirms.
            const historicalIds = totalHistoricalCount > 0
              ? new Set(Object.values(previousFiles).flat().map(f => f.document_id))
              : new Set();

            setKbLimitModal({
              capacityStatus,
              // onContinue proceeds with analysis — full types simply won't be saved.
              onContinue: () => {
                setKbLimitModal(null);
                if (totalHistoricalCount > 0) {
                  setSelectedHistoricalIds(historicalIds);
                  setShowHistoricalSelector(true);
                } else {
                  executeAnalysis([]);
                }
              },
              onCancel: () => setKbLimitModal(null),
            });
          } else {
            // All types have space — proceed normally.
            if (totalHistoricalCount > 0) {
              setSelectedHistoricalIds(new Set(Object.values(previousFiles).flat().map(f => f.document_id)));
              setShowHistoricalSelector(true);
            } else {
              executeAnalysis([]);
            }
          }
        })
        .catch(() => {
          // If the capacity check itself fails, fall through and let the analysis
          // handle the error — don't block the user entirely.
          if (totalHistoricalCount > 0) {
            setSelectedHistoricalIds(new Set(Object.values(previousFiles).flat().map(f => f.document_id)));
            setShowHistoricalSelector(true);
          } else {
            executeAnalysis([]);
          }
        });
    } else {
      // saveToHistorical is off — no pre-check needed.
      if (totalHistoricalCount > 0) {
        setSelectedHistoricalIds(new Set(Object.values(previousFiles).flat().map(f => f.document_id)));
        setShowHistoricalSelector(true);
      } else {
        executeAnalysis([]);
      }
    }
  };

  const executeAnalysis = (historicalDocumentIds) => {
    const activeTypes = Object.entries(uploadedFiles)
      .filter(([_, files]) => files.length > 0)
      .map(([type]) => type);

    setShowHistoricalSelector(false);
    setIsAnalysing(true);
    setAnalysisError(null);
    setSelectedReportId(null); // Deselect historical views to focus on active run
    setKbSaveSummary(null);   // Clear any previous save summary toast

    const currentDocumentIds = Object.values(uploadedFiles)
      .flat()
      .map(file => file.document_id);

    analyseRiskV2(activeTypes, customInstruction, currentDocumentIds, historicalDocumentIds, saveToHistorical, includeCodeImpact && githubStatus?.connected)
      .then((data) => {
        setAnalysisResult(data);
        setActiveTab('summary');
        setTestAssets(null); // stale test assets belong to the previous analysis
        setTestGenError(null);
        setTestGenPhase('idle');
        setFeedback({}); // fresh report_id, no feedback recorded yet

        // If the user opted into auto-save, display a toast summarising what was
        // saved vs skipped (due to capacity limits or duplicate filenames).
        if (saveToHistorical && data.kb_save_summary) {
          setKbSaveSummary(data.kb_save_summary);
        }

        loadWorkspaceData(); // Refresh history lists
      })
      .catch((err) => {
        console.error("AI Analysis failed:", err);
        setAnalysisError(err.response?.data?.detail || 'AI Query execution failed.');
      })
      .finally(() => {

        setIsAnalysing(false);
      });
  };


  const handleGenerateTests = () => {
    if (!analysisResult?.report_id) {
      setTestGenError('Test generation needs a fresh analysis run — please re-run the analysis first.');
      return;
    }
    if (!selectedTestAssets.cases && !selectedTestAssets.playwright && !selectedTestAssets.testData) {
      setTestGenError('Please select at least one asset type (Test Cases, Playwright Scripts, or Test Data) to generate.');
      return;
    }
    setTestGenError(null);
    setTestAssets(null);
    setTestGenPhase('cases');
    let casesSucceeded = false;

    generateTestCases(analysisResult.report_id)
      .then((casesData) => {
        casesSucceeded = true;
        setTestAssets({
          test_cases: selectedTestAssets.cases ? casesData.test_cases : [],
          locator_strategy: casesData.locator_strategy,
          grounded_code_files: casesData.grounded_code_files,
          automation: null,
          test_data: null,
        });

        const tasks = [];
        const taskTypes = [];

        if (selectedTestAssets.playwright) {
          tasks.push(generateTestAutomation(analysisResult.report_id, casesData.test_cases));
          taskTypes.push('automation');
        }
        if (selectedTestAssets.testData) {
          tasks.push(generateTestData(analysisResult.report_id, casesData.test_cases));
          taskTypes.push('testData');
        }

        if (tasks.length === 0) {
          setTestGenPhase('done');
          return null;
        }

        if (selectedTestAssets.playwright && selectedTestAssets.testData) {
          setTestGenPhase('automation');
        } else if (selectedTestAssets.playwright) {
          setTestGenPhase('automation');
        } else if (selectedTestAssets.testData) {
          setTestGenPhase('test-data');
        }

        return Promise.all(tasks).then((results) => ({ results, taskTypes }));
      })
      .then((res) => {
        if (!res) return;
        const { results, taskTypes } = res;
        setTestAssets((prev) => {
          if (!prev) return prev;
          const updated = { ...prev };
          taskTypes.forEach((type, idx) => {
            if (type === 'automation') {
              updated.automation = results[idx].automation;
            } else if (type === 'testData') {
              updated.test_data = results[idx].test_data;
            }
          });
          return updated;
        });
        setTestGenPhase('done');
      })
      .catch((err) => {
        setTestGenError(err.response?.data?.detail || 'Test generation failed. Please try again.');
        setTestGenPhase(casesSucceeded ? 'done' : 'idle');
      });
  };

  const downloadTextFile = (filename, content, mimeType = 'text/plain') => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Escapes a value for a CSV field: wraps in quotes and doubles any internal quotes, since test
  // case steps/expected results routinely contain commas and newlines that would otherwise break
  // column alignment when opened in Excel/Sheets or imported into a test management tool.
  const toCsvField = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

  const testCasesToCsv = (testCases) => {
    const header = ['ID', 'Requirement Ref', 'Title', 'Priority', 'Type', 'Preconditions', 'Steps', 'Expected Result', 'Addresses', 'UI Testable'];
    const rows = testCases.map((tc) => [
      tc.id,
      tc.requirement_ref,
      tc.title,
      tc.priority,
      tc.type,
      (tc.preconditions || []).join('\n'),
      (tc.steps || []).map((s, i) => `${i + 1}. ${s}`).join('\n'),
      tc.expected_result,
      tc.addresses,
      tc.ui_testable ? 'Yes' : 'No',
    ]);
    return [header, ...rows].map((row) => row.map(toCsvField).join(',')).join('\r\n');
  };

  // Builds a shareable PDF of the current analysis (summary, risks, coverage gaps, recurring
  // bugs, impacted code, sources) entirely client-side — no backend round trip.
  const downloadAnalysisPdf = () => {
    if (!analysisResult) return;
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const marginX = 15;
    const pageWidth = 210;
    const pageHeight = 297;
    const maxWidth = pageWidth - marginX * 2;
    let y = 20;

    const ensureSpace = (needed) => {
      if (y + needed > pageHeight - 15) {
        doc.addPage();
        y = 20;
      }
    };

    const addHeading = (text) => {
      ensureSpace(10);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor(30, 41, 59);
      doc.text(text, marginX, y);
      y += 7;
    };

    const addParagraph = (text) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(51, 65, 85);
      const lines = doc.splitTextToSize(text || '(none)', maxWidth);
      lines.forEach((line) => {
        ensureSpace(6);
        doc.text(line, marginX, y);
        y += 5.5;
      });
      y += 3;
    };

    const addList = (items, emptyLabel) => {
      if (!items || items.length === 0) {
        addParagraph(emptyLabel);
        return;
      }
      items.forEach((item, idx) => {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(51, 65, 85);
        const lines = doc.splitTextToSize(`${idx + 1}. ${item}`, maxWidth);
        lines.forEach((line) => {
          ensureSpace(6);
          doc.text(line, marginX, y);
          y += 5.5;
        });
        y += 2;
      });
      y += 2;
    };

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(15, 23, 42);
    doc.text('RiskRadar Analysis Report', marginX, y);
    y += 9;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text(`Generated: ${new Date().toLocaleString()}`, marginX, y);
    y += 8;

    const level = (analysisResult.risk_level || 'low').toLowerCase();
    const riskColors = { high: [220, 38, 38], medium: [234, 88, 12], amber: [234, 88, 12], low: [22, 163, 74] };
    const [r, g, b] = riskColors[level] || riskColors.low;
    doc.setFillColor(r, g, b);
    doc.roundedRect(marginX, y - 5, 34, 8, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(`${level.toUpperCase()} RISK`, marginX + 4, y);
    y += 12;

    addHeading('Executive Summary');
    addParagraph(analysisResult.summary);

    addHeading('Requirements At Risk');
    addList(analysisResult.risks, 'No specific risks identified.');

    addHeading('Coverage Gaps');
    addList(analysisResult.coverage_gaps, 'No coverage gaps identified.');

    addHeading('Recurring Bug Patterns');
    addList(analysisResult.recurring_bugs, 'No repeating bug patterns detected.');

    // Coverage Intersection Matrix — same derived rows the Matrix tab renders.
    if (coverageMatrixData.length > 0) {
      addHeading('Coverage Intersection Matrix');
      const colWidths = [28, 100, 26, 26]; // ID, Description, Coverage, Bugs — sums to maxWidth
      const colX = [marginX, marginX + 28, marginX + 128, marginX + 154];
      const drawMatrixHeader = () => {
        doc.setFillColor(241, 245, 249);
        doc.rect(marginX, y - 4.5, maxWidth, 7, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(51, 65, 85);
        ['Requirement', 'Description', 'Test Coverage', 'Bug Signals'].forEach((h, i) => {
          doc.text(h, colX[i] + 2, y);
        });
        y += 6;
      };
      drawMatrixHeader();
      coverageMatrixData.forEach((row) => {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        const descLines = doc.splitTextToSize(row.title || '', colWidths[1] - 4);
        const rowHeight = Math.max(descLines.length, 1) * 4.5 + 3;
        if (y + rowHeight > pageHeight - 15) {
          doc.addPage();
          y = 20;
          drawMatrixHeader();
        }
        doc.setDrawColor(226, 232, 240);
        doc.line(marginX, y + rowHeight - 4.5, marginX + maxWidth, y + rowHeight - 4.5);
        doc.setTextColor(51, 65, 85);
        doc.text(row.id, colX[0] + 2, y);
        doc.text(descLines, colX[1] + 2, y);
        if (row.coverage) {
          doc.setTextColor(22, 163, 74);
          doc.text('Covered', colX[2] + 2, y);
        } else {
          doc.setTextColor(220, 38, 38);
          doc.text('Uncovered', colX[2] + 2, y);
        }
        doc.setTextColor(row.bugCount > 0 ? 220 : 100, row.bugCount > 0 ? 38 : 116, row.bugCount > 0 ? 38 : 139);
        doc.text(String(row.bugCount), colX[3] + 2, y);
        y += rowHeight;
      });
      y += 5;
    }

    if (analysisResult.impacted_files?.length > 0) {
      addHeading('Impacted Code Areas');
      addList(analysisResult.impacted_files, '');
    }

    addHeading('Sources');
    addParagraph((analysisResult.sources || []).join(', ') || 'N/A');

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(`RiskRadar · Page ${i} of ${pageCount}`, marginX, pageHeight - 8);
    }

    doc.save(`riskradar-analysis-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const toggleHistoricalSelection = (documentId) => {
    setSelectedHistoricalIds(prev => {
      const next = new Set(prev);
      if (next.has(documentId)) next.delete(documentId);
      else next.add(documentId);
      return next;
    });
  };

  // Select a report from SQLite history list
  const handleSelectReport = (report) => {
    setSelectedReportId(report.id);
    setAnalysisResult({
      report_id: report.id, // lets "Generate Test Assets" work on historical reports too
      risk_level: report.risk_level,
      summary: report.summary,
      risks: report.risks,
      coverage_gaps: report.coverage_gaps,
      recurring_bugs: report.recurring_bugs,
      sources: report.sources
    });
    setActiveTab('summary');
    setTestAssets(null);
    setTestGenError(null);
    setTestGenPhase('idle');
    setActiveView('dashboard'); // jump back to the main view so the loaded report is visible

    setFeedback({});
    getFeedback(report.id).then(setFeedback).catch((err) => {
      console.error('Failed to load feedback for report:', err);
    });
  };



  // Coverage Matrix parsing logic
  const coverageMatrixData = useMemo(() => {
    if (!analysisResult || !analysisResult.risks) return [];

    return analysisResult.risks.map((riskStr, index) => {
      const idMatch = riskStr.match(/(REQ-\d+|REQ\d+|Requirement\s*\d+)/i);
      let id = idMatch ? idMatch[0].toUpperCase() : `REQ-${index + 1}`;

      let restOfText = riskStr;
      if (idMatch) {
        restOfText = riskStr.replace(idMatch[0], '');
      }
      restOfText = restOfText.replace(/^[:\s-]+/, '');

      // The LLM phrases risk lines in a few shapes; extract a human title from each:
      //   "REQ-301 (Investor Login Authentication): High - ..." → parenthetical name
      //   "REQ-301: High - Authentication partial implementation: ..." → clause after severity
      //   "REQ-301 Checkout Stock Validation: High - ..." → leading clause
      const parenMatch = restOfText.match(/^\(([^)]+)\)/);
      const withoutSeverity = restOfText.replace(/^(high|medium|low|critical)\s*[-–—:]\s*/i, '');
      // Split on colon, open paren, or a spaced dash — NOT bare hyphens, which would break
      // hyphenated words like "Same-day" mid-word.
      let title = parenMatch ? parenMatch[1].trim() : withoutSeverity.split(/[:(]|\s[-–—]\s/)[0].trim();
      if (!title) {
        title = `Requirement Coverage Analysis ${index + 1}`;
      } else if (title.length > 55) {
        title = title.slice(0, 52).trimEnd() + '…';
      }

      const isUncovered = analysisResult.coverage_gaps && analysisResult.coverage_gaps.some(gap => 
        gap.toLowerCase().includes(id.toLowerCase()) || 
        gap.toLowerCase().includes(title.toLowerCase())
      );
      const coverage = !isUncovered;

      let bugCount = 0;
      if (analysisResult.recurring_bugs) {
        analysisResult.recurring_bugs.forEach(bug => {
          if (bug.toLowerCase().includes(id.toLowerCase()) || bug.toLowerCase().includes(title.toLowerCase())) {
            bugCount++;
          }
        });
      }

      if (bugCount === 0) {
        const bugPatternMatch = restOfText.match(/(\d+)\s*(bug|defect|issue|error)/i);
        if (bugPatternMatch) {
          bugCount = parseInt(bugPatternMatch[1], 10);
        } else if (/bug|defect|issue|error/i.test(restOfText)) {
          bugCount = 1;
        }
      }

      return { id, title, coverage, bugCount, details: restOfText.trim() };
    });
  }, [analysisResult]);

  const sortedCoverageMatrix = useMemo(() => {
    if (!sortConfig.key) return coverageMatrixData;

    return [...coverageMatrixData].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (typeof aVal === 'string') {
        return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      } else if (typeof aVal === 'boolean') {
        return sortConfig.direction === 'asc' 
          ? (aVal === bVal ? 0 : aVal ? -1 : 1)
          : (aVal === bVal ? 0 : aVal ? 1 : -1);
      } else {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
    });
  }, [coverageMatrixData, sortConfig]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return (
        <svg className="w-3 h-3 text-slate-300 ml-1.5 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortConfig.direction === 'asc' ? (
      <svg className="w-3 h-3 text-indigo-600 ml-1.5 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-3 h-3 text-indigo-600 ml-1.5 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  const hasAtLeastOneFile = Object.values(uploadedFiles).some(files => files.length > 0);

  // Whether a Jira source applies to the current category at all. Both story fetch and
  // JQL defect fetch support Active Targets and Historical KB. Test cases have no Jira source.
  const jiraConnectableForCurrentSelection =
    selectedType === 'requirement' ? true :
    selectedType === 'bug_report' ? true :
    false;
  const jiraAvailableForCurrentSelection = !!jiraStatus?.connected && jiraConnectableForCurrentSelection;

  // Analysis requires a current requirement doc plus either a bug report (current or historical)
  // or a connected GitHub repo with code impact analysis enabled — cross-referencing a
  // requirement against real code is a meaningful analysis even with no bug history yet.
  const hasRequirement = uploadedFiles.requirement.length > 0;
  const hasBugReport = uploadedFiles.bug_report.length > 0 || previousFiles.bug_report.length > 0;
  const hasCodeRepoAlternative = includeCodeImpact && githubStatus?.connected;
  const canRunAnalysis = hasRequirement && (hasBugReport || hasCodeRepoAlternative);
  const analysisBlockReason = !hasRequirement && !hasBugReport && !hasCodeRepoAlternative
    ? 'Upload a Requirements document (Active Targets) and a Bug Report (Active Targets or Historical KB) to run the analysis.'
    : !hasRequirement
      ? 'Upload a Requirements document to Active Targets to run the analysis.'
      : !hasBugReport && !hasCodeRepoAlternative
        ? 'Upload a Bug Report (Active Targets or Historical KB), or connect a GitHub repo and enable code impact analysis, to run the analysis.'
        : null;

  // LOADING INITIAL STATE SCREEN
  if (appInitializing) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center relative overflow-hidden font-sans">
        {/* Glow Blobs */}
        <div className="absolute top-1/4 left-1/4 w-[350px] h-[350px] bg-indigo-500/10 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-[350px] h-[350px] bg-orange-500/10 rounded-full blur-[100px] pointer-events-none" />

        <div className="flex flex-col items-center space-y-6 z-10">
          <div className="relative flex items-center justify-center">
            {/* Spinning ring */}
            <div className="animate-spin h-14 w-14 rounded-full border-[3px] border-orange-500/10 border-t-orange-500 shadow-[0_0_15px_rgba(249,115,22,0.3)]" />
            {/* Innermost pulsing dot */}
            <div className="absolute h-5 w-5 rounded-full bg-indigo-500 animate-pulse shadow-[0_0_10px_rgba(99,102,241,0.8)]" />
          </div>
          <div className="text-center space-y-1.5">
            <h3 className="text-white font-display font-extrabold tracking-wide text-lg">Initializing Secure Sandbox</h3>
            <p className="text-slate-400 font-medium text-xs">Syncing multi-tenant vector database...</p>
          </div>
        </div>
      </div>
    );
  }


  // SIGN IN & SIGN UP SCREEN
  if (!user) {
    return (
      <div className="min-h-screen bg-slate-950 font-sans text-slate-100 flex flex-col relative">


        <header className="fixed top-0 left-0 w-full bg-white border-b border-slate-200 shadow-sm py-4 px-6 md:px-12 flex justify-between items-center z-50">
          <div className="flex items-center space-x-3">
            {/* Logo graphic */}
            <a href="/" onClick={(e) => { e.preventDefault(); setLandingView('home'); window.scrollTo(0, 0); }} className="flex items-center hover:opacity-80 transition-opacity">
              <img src="/risk.svg" onError={(e) => { e.target.onerror = null; e.target.src="/risk.png"; e.target.className="h-10 md:h-12 w-auto object-contain"; }} alt="Risk Radar Logo" className="h-8 md:h-10 w-auto" />
            </a>
          </div>
          
          {/* Nav links and Login button */}
          <div className="flex items-center space-x-6 md:space-x-8">
            <nav className="hidden md:flex items-center space-x-6">
              <div className="relative group flex items-center h-full">
                <span className="cursor-pointer flex items-center space-x-1 text-sm font-bold text-blue-600 hover:text-blue-500 transition-colors" style={{fontFamily: 'Inter, sans-serif'}}>
                  <span>Who We Are</span>
                  <svg className="w-4 h-4 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
                
                {/* Dropdown Menu */}
                <div className="absolute top-full pt-4 -left-4 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="bg-white border border-slate-200 shadow-[0px_10px_30px_rgba(0,0,0,0.08)] rounded-xl overflow-hidden flex flex-col py-2">
                    <a href="https://emergeaitech.com" target="_blank" rel="noopener noreferrer" className="px-5 py-3 text-sm text-slate-700 font-semibold hover:bg-slate-50 hover:text-blue-600 transition-colors border-b border-slate-50">About Us</a>
                    <a href="#insights" onClick={(e) => { e.preventDefault(); setLandingView('insights'); window.scrollTo(0, 0); }} className="px-5 py-3 text-sm text-slate-700 font-semibold hover:bg-slate-50 hover:text-blue-600 transition-colors border-b border-slate-50">Insights from our leaders</a>
                    <a href="#innovation-team" onClick={(e) => { e.preventDefault(); setLandingView('team'); window.scrollTo(0, 0); }} className="px-5 py-3 text-sm text-slate-700 font-semibold hover:bg-slate-50 hover:text-blue-600 transition-colors">Innovation Team</a>
                  </div>
                </div>
              </div>

              <a href="#innovation" onClick={(e) => { if (landingView !== 'home') { e.preventDefault(); setLandingView('home'); setTimeout(() => document.getElementById('innovation')?.scrollIntoView(), 100); } }} className="text-sm font-bold text-blue-600 hover:text-blue-500 transition-colors" style={{fontFamily: 'Inter, sans-serif'}}>Innovation</a>
            </nav>
            <button 
              onClick={() => setShowAuthForm(true)}
              className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2 rounded-lg font-bold text-sm transition-colors shadow-lg"
            >
              Login
            </button>
          </div>
        </header>

        {landingView === 'home' ? (
          <>
        {/* Hero Section */}
        <main className="h-[65vh] min-h-[700px] pt-16 relative flex flex-col items-center justify-center overflow-hidden bg-[#0a0f2c] w-full">
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            style={{
              position: 'absolute',
              top: 0, left: 0,
              width: '100%', height: '100%',
              objectFit: 'cover',
              zIndex: 0,
              pointerEvents: 'none'
            }}
          >
            <source src="/videos/hero-bg.webm" type="video/webm" />
            <source src="/videos/hero-bg.mp4" type="video/mp4" />
          </video>

          <div style={{
            position: 'absolute',
            top: 0, left: 0,
            width: '100%', height: '100%',
            background: 'linear-gradient(135deg, rgba(10,15,44,0.75) 0%, rgba(10,15,44,0.55) 100%)',
            zIndex: 1,
            pointerEvents: 'none'
          }} />

          <div className="relative z-10 text-center px-4 max-w-5xl mx-auto space-y-6">
            <div className="flex flex-col items-center justify-center space-y-3">
              <span className="text-xs md:text-sm font-semibold tracking-widest uppercase text-white/75" style={{fontFamily: 'Inter, sans-serif'}}>
                Powered by Agentic AI
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.15]">
                EmergeAI Risk Radar
              </h1>
            </div>
            <p className="text-xl md:text-2xl text-slate-200 font-medium tracking-wide">
              The AI-powered software risk assessment tool for release pipelines.
            </p>
          </div>
        </main>

        {/* Features Section (Second Page on Scroll) */}
        <section id="innovation" className="bg-white text-slate-900 py-24 px-6 md:px-12 flex flex-col items-center min-h-[80vh] w-full relative z-10">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center text-[#3b82f6] mb-16">
            Innovation by EmergeAI Technologies
          </h2>
          
          <div className="max-w-7xl w-full grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Box: Main Text */}
            <div className="bg-[#eef8ff] rounded-2xl p-8 md:p-12 shadow-sm border border-blue-50 flex flex-col items-center justify-center text-center h-full">
              <a href="/" className="hover:opacity-80 transition-opacity">
                <img src="/risk.svg" onError={(e) => { e.target.onerror = null; e.target.src="/risk.png"; e.target.className="h-[56px] w-auto mb-8 object-contain"; }} alt="Risk Radar Logo" className="h-[48px] w-auto mb-8" />
              </a>
              <h4 className="text-2xl md:text-3xl font-bold text-[#84cc16] mb-6">
                EmergeAI Risk Radar
              </h4>
              <p className="text-slate-600 leading-relaxed text-base md:text-lg font-medium">
                EmergeAI Risk Radar is an AI-powered software risk assessment tool built for QA and release teams. It analyzes requirements, bug reports, and code changes together — grounded in your own project documentation through retrieval-augmented generation — to flag release risk before it reaches production and auto-generate the test cases needed to close the gaps.
              </p>
            </div>

            {/* Right Box: Grid of 4 small boxes */}
            <div className="bg-[#eef8ff] rounded-2xl p-8 md:p-12 shadow-sm border border-blue-50 flex flex-col items-center justify-center text-center reveal h-full">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 w-full h-full">
                
                {/* Box 1 */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 md:p-8 flex flex-col justify-center items-center text-center hover:shadow-xl hover:border-blue-200 hover:-translate-y-1 transition-all duration-300 group">
                  <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold text-slate-800 mb-2">RAG-Powered</h4>
                  <p className="text-slate-500 leading-relaxed text-sm font-medium">Contextual answers from your docs</p>
                </div>

                {/* Box 2 */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 md:p-8 flex flex-col justify-center items-center text-center hover:shadow-xl hover:border-blue-200 hover:-translate-y-1 transition-all duration-300 group">
                  <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold text-slate-800 mb-2">Multi-format</h4>
                  <p className="text-slate-500 leading-relaxed text-sm font-medium">PDF, DOCX, XLSX ready</p>
                </div>

                {/* Box 3 */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 md:p-8 flex flex-col justify-center items-center text-center hover:shadow-xl hover:border-blue-200 hover:-translate-y-1 transition-all duration-300 group">
                  <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold text-slate-800 mb-2">Enterprise Ready</h4>
                  <p className="text-slate-500 leading-relaxed text-sm font-medium">Secure & robust architecture</p>
                </div>

                {/* Box 4 */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 md:p-8 flex flex-col justify-center items-center text-center hover:shadow-xl hover:border-blue-200 hover:-translate-y-1 transition-all duration-300 group">
                  <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <h4 className="text-xl font-bold text-slate-800 mb-2">AI Insights</h4>
                  <p className="text-slate-500 leading-relaxed text-sm font-medium">Predictive risk scoring models</p>
                </div>

              </div>
            </div>
          </div>
        </section>

        {/* Product Specs Section */}
        <section className="bg-[#f8f9fc] py-24 px-6 md:px-12 flex flex-col items-center w-full relative z-10">
          <div className="max-w-7xl w-full">
            <div className="text-center mb-20 reveal">
              <p className="text-blue-600 font-bold text-base md:text-lg tracking-widest uppercase mb-4">The Product</p>
              <h2 className="text-4xl md:text-6xl font-extrabold text-slate-900 mb-6 font-display" style={{fontFamily: 'Sora, sans-serif'}}>EmergeAI Risk Radar</h2>
              <p className="text-xl md:text-2xl text-slate-600 max-w-3xl mx-auto font-medium" style={{fontFamily: 'Inter, sans-serif'}}>
                Agentic AI-powered QA Risk Assessment Tool for Modern Software release pipelines. Built by EmergeAI Technologies.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mb-16">
              {/* Card 1 */}
              <div className="bg-white rounded-2xl p-8 md:p-10 shadow-[0px_6px_24px_rgba(0,0,0,0.06)] hover:-translate-y-2 hover:shadow-2xl transition-all duration-300 reveal border border-slate-100" style={{transitionDelay: '0.1s'}}>
                <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 mb-6">
                  <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Sora, sans-serif'}}>Risk Assessment Engine</h3>
                <p className="text-base text-slate-600 leading-relaxed" style={{fontFamily: 'Inter, sans-serif'}}>
                  Analyzes release pipelines and predicts QA risk scores using AI, helping teams prioritize testing efforts and reduce production defects.
                </p>
              </div>

              {/* Card 2 */}
              <div className="bg-white rounded-2xl p-8 md:p-10 shadow-[0px_6px_24px_rgba(0,0,0,0.06)] hover:-translate-y-2 hover:shadow-2xl transition-all duration-300 reveal border border-slate-100" style={{transitionDelay: '0.2s'}}>
                <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 mb-6">
                  <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Sora, sans-serif'}}>Multi-format Document Ingestion</h3>
                <p className="text-base text-slate-600 leading-relaxed" style={{fontFamily: 'Inter, sans-serif'}}>
                  Supports PDF, DOCX, XLSX and more. Upload release notes, test plans, and QA reports for instant AI-powered analysis.
                </p>
              </div>

              {/* Card 3 */}
              <div className="bg-white rounded-2xl p-8 md:p-10 shadow-[0px_6px_24px_rgba(0,0,0,0.06)] hover:-translate-y-2 hover:shadow-2xl transition-all duration-300 reveal border border-slate-100" style={{transitionDelay: '0.3s'}}>
                <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 mb-6">
                  <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Sora, sans-serif'}}>Intelligent RAG Pipeline</h3>
                <p className="text-base text-slate-600 leading-relaxed" style={{fontFamily: 'Inter, sans-serif'}}>
                  Retrieval-Augmented Generation with ChromaDB vector store — answers QA questions grounded in your actual project documentation.
                </p>
              </div>

              {/* Card 4 */}
              <div className="bg-white rounded-2xl p-8 md:p-10 shadow-[0px_6px_24px_rgba(0,0,0,0.06)] hover:-translate-y-2 hover:shadow-2xl transition-all duration-300 reveal border border-slate-100" style={{transitionDelay: '0.4s'}}>
                <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 mb-6">
                  <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Sora, sans-serif'}}>Enterprise Security</h3>
                <p className="text-base text-slate-600 leading-relaxed" style={{fontFamily: 'Inter, sans-serif'}}>
                  JWT authentication, email OTP verification, rate limiting, and encrypted data handling built in from the ground up.
                </p>
              </div>

              {/* Card 5 */}
              <div className="bg-white rounded-2xl p-8 md:p-10 shadow-[0px_6px_24px_rgba(0,0,0,0.06)] hover:-translate-y-2 hover:shadow-2xl transition-all duration-300 reveal border border-slate-100" style={{transitionDelay: '0.5s'}}>
                <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 mb-6">
                  <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Sora, sans-serif'}}>FastAPI Backend</h3>
                <p className="text-base text-slate-600 leading-relaxed" style={{fontFamily: 'Inter, sans-serif'}}>
                  High-performance Python backend with SQLAlchemy, SQLite, and LangChain — designed for speed, reliability and easy deployment.
                </p>
              </div>

              {/* Card 6 */}
              <div className="bg-white rounded-2xl p-8 md:p-10 shadow-[0px_6px_24px_rgba(0,0,0,0.06)] hover:-translate-y-2 hover:shadow-2xl transition-all duration-300 reveal border border-slate-100" style={{transitionDelay: '0.6s'}}>
                <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 mb-6">
                  <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Sora, sans-serif'}}>Built for Technical Teams</h3>
                <p className="text-base text-slate-600 leading-relaxed" style={{fontFamily: 'Inter, sans-serif'}}>
                  Designed specifically for technical teams who need fast, reliable risk insights without complexity.
                </p>
              </div>
            </div>
          </div>
        </section>
        </>
        ) : landingView === 'insights' ? (
          <main className="who-page flex-1 w-full pt-16 flex flex-col items-center text-slate-900 pb-24">
             {/* Banner */}
             <div className="who-hero w-full py-20 md:py-28 text-center relative overflow-hidden">
               <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[28rem] h-[28rem] who-hero-glow rounded-full pointer-events-none" />
               <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
               <div className="relative z-10 px-6">
                 <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight" style={{fontFamily: 'Sora, sans-serif'}}>
                   Insights From Our Leaders
                 </h1>
                 <div className="who-section-divider mx-auto mt-6" />
               </div>
             </div>

             {/* Voices Section */}
             <div className="relative z-10 w-full who-section py-16 md:py-20">
               <div className="max-w-5xl mx-auto px-6 text-center">
                 <h2 className="text-3xl md:text-4xl font-bold text-[#0a0f2c] mb-3" style={{fontFamily: 'Sora, sans-serif'}}>Voices of Our Leaders</h2>
                 <div className="who-section-divider mx-auto mb-12 md:mb-14" />
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 max-w-4xl mx-auto items-stretch">
                 
                 {/* Card 1: Ira Agarwal */}
                 <div className="who-profile-card group">
                   <div className="who-avatar-zone who-avatar-zone--insights">
                     <div className="who-avatar-ring">
                       <div className="who-avatar who-avatar--insights">
                         <img src="/iramam.jpeg" alt="Ira Agarwal" className="object-top group-hover:scale-[1.03] transition-transform duration-500 ease-out" />
                       </div>
                     </div>
                   </div>
                   <div className="who-card-body text-center">
                     <h3 className="text-2xl font-bold text-[#0a0f2c] mb-2" style={{fontFamily: 'Sora, sans-serif'}}>Ira Agarwal</h3>
                     <p className="text-[11px] font-bold text-[#3b82f6] uppercase tracking-[0.14em] mb-5">Founder & Board Chair</p>
                     <div className="who-quote mt-auto">
                       <span className="who-quote-mark" aria-hidden="true">&ldquo;</span>
                       <p className="text-slate-600 text-sm md:text-[0.9375rem] leading-relaxed italic relative z-10 pt-2" style={{fontFamily: 'Inter, sans-serif'}}>
                         "EmergeAI Risk Radar is an exceptional Agentic AI Platform with the potential to transform the software quality landscape. Its advanced capabilities empower businesses to innovate, streamline operations, and drive sustainable growth through cutting-edge automation and intelligent decision-making."
                       </p>
                     </div>
                   </div>
                 </div>

                 {/* Card 2: Hanish Suri */}
                 <div className="who-profile-card group">
                   <div className="who-avatar-zone who-avatar-zone--insights">
                     <div className="who-avatar-ring">
                       <div className="who-avatar who-avatar--insights">
                         <img src="/hanishsir.jpeg" alt="Hanish Suri" className="object-top group-hover:scale-[1.03] transition-transform duration-500 ease-out" />
                       </div>
                     </div>
                   </div>
                   <div className="who-card-body text-center">
                     <h3 className="text-2xl font-bold text-[#0a0f2c] mb-2" style={{fontFamily: 'Sora, sans-serif'}}>Hanish Suri</h3>
                     <p className="text-[11px] font-bold text-[#3b82f6] uppercase tracking-[0.14em] mb-5">Senior Technical Advisor</p>
                     <div className="who-quote mt-auto">
                       <span className="who-quote-mark" aria-hidden="true">&ldquo;</span>
                       <p className="text-slate-600 text-sm md:text-[0.9375rem] leading-relaxed italic relative z-10 pt-2" style={{fontFamily: 'Inter, sans-serif'}}>
                         "EmergeAI serves as a genuine partner—uplifting innovations, speeding meaningful outcomes, and guiding organizations toward lasting advancement. With its agentic approach, this platform is reshaping the way QA teams envision, create, and achieve future success."
                       </p>
                     </div>
                   </div>
                 </div>

                 </div>
               </div>
             </div>
          </main>
        ) : landingView === 'team' ? (
          <main className="who-page flex-1 w-full pt-16 flex flex-col items-center text-slate-900 pb-24">
             {/* Banner */}
             <div className="who-hero w-full py-20 md:py-28 text-center relative overflow-hidden">
               <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[28rem] h-[28rem] who-hero-glow rounded-full pointer-events-none" />
               <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
               <div className="relative z-10 px-6">
                 <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight" style={{fontFamily: 'Sora, sans-serif'}}>
                   Innovation Team
                 </h1>
                 <div className="who-section-divider mx-auto mt-6" />
               </div>
             </div>

             {/* Combined Team Section — single row, photo + name + designation only */}
             <div className="relative z-10 w-full who-section py-8 md:py-10">
               <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8 text-center">
                 <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8 items-start">

                   {/* Ira Agarwal */}
                   <div className="who-profile-card group">
                     <div className="who-avatar-zone">
                       <div className="who-avatar-ring">
                         <div className="who-avatar">
                           <img src="/iramam.jpeg" alt="Ira Agarwal" className="object-top group-hover:scale-[1.03] transition-transform duration-500 ease-out" />
                         </div>
                       </div>
                     </div>
                     <div className="who-card-body text-center">
                       <h3 className="text-xl font-bold text-[#0a0f2c] mb-2" style={{fontFamily: 'Sora, sans-serif'}}>Ira Agarwal</h3>
                       <p className="text-[11px] font-bold text-[#3b82f6] uppercase tracking-[0.14em]">Founder & Board Chair</p>
                     </div>
                   </div>

                   {/* Hanish Suri */}
                   <div className="who-profile-card group">
                     <div className="who-avatar-zone">
                       <div className="who-avatar-ring">
                         <div className="who-avatar">
                           <img src="/hanishsir.jpeg" alt="Hanish Suri" className="object-top group-hover:scale-[1.03] transition-transform duration-500 ease-out" />
                         </div>
                       </div>
                     </div>
                     <div className="who-card-body text-center">
                       <h3 className="text-xl font-bold text-[#0a0f2c] mb-2" style={{fontFamily: 'Sora, sans-serif'}}>Hanish Suri</h3>
                       <p className="text-[11px] font-bold text-[#3b82f6] uppercase tracking-[0.14em]">Senior Technical Advisor</p>
                     </div>
                   </div>

                   {/* Mohit Sharma */}
                   <div className="who-profile-card group">
                     <div className="who-avatar-zone">
                       <div className="who-avatar-ring">
                         <div className="who-avatar">
                           <img src="/MohitSharma.jpg" onError={(e) => { e.target.onerror = null; e.target.src="https://ui-avatars.com/api/?name=Mohit+Sharma&background=0D8ABC&color=fff"; }} alt="Mohit Sharma" className="object-top group-hover:scale-[1.03] transition-transform duration-500 ease-out" />
                         </div>
                       </div>
                     </div>
                     <div className="who-card-body text-center">
                       <h3 className="text-xl font-bold text-[#0a0f2c] mb-2" style={{fontFamily: 'Sora, sans-serif'}}>Mohit Sharma</h3>
                       <p className="text-[11px] font-bold text-[#3b82f6] uppercase tracking-[0.14em]">AI Developer</p>
                     </div>
                   </div>

                   {/* Tushar */}
                   <div className="who-profile-card group">
                     <div className="who-avatar-zone">
                       <div className="who-avatar-ring">
                         <div className="who-avatar">
                           <img src="/Tushar.jpeg" onError={(e) => { e.target.onerror = null; e.target.src="https://ui-avatars.com/api/?name=Tushar+Jangra&background=0D8ABC&color=fff"; }} alt="Tushar Jangra" className="object-center scale-110 group-hover:scale-[1.13] transition-transform duration-500 ease-out" />
                         </div>
                       </div>
                     </div>
                     <div className="who-card-body text-center">
                       <h3 className="text-xl font-bold text-[#0a0f2c] mb-2" style={{fontFamily: 'Sora, sans-serif'}}>Tushar Jangra</h3>
                       <p className="text-[11px] font-bold text-[#3b82f6] uppercase tracking-[0.14em]">AI Developer</p>
                     </div>
                   </div>

                 </div>
               </div>
             </div>
          </main>
        ) : null}


        {/* Footer */}
        <footer className="w-full relative z-10 reveal flex flex-col bg-[#f0f2f5]">
          {/* Main Footer Content */}
          <div className="border-t border-slate-200 py-16 px-6 md:px-12 w-full">
            <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:flex-wrap justify-between items-start gap-x-8 gap-y-12 md:gap-y-8 space-y-12 md:space-y-0 text-left">
              
              {/* Logo Column */}
              <div className="flex-1 max-w-sm">
                <a href="/" className="hover:opacity-80 transition-opacity flex items-center mb-2">
                  <img src="/risk.svg" onError={(e) => { e.target.onerror = null; e.target.src="/risk.png"; e.target.className="h-[44px] w-auto object-contain"; }} alt="Risk Radar Logo" className="h-[44px] w-auto" />
                </a>
              </div>

              {/* Company Column */}
              <div className="flex flex-col space-y-4">
                <h4 className="text-[#3b82f6] font-bold text-[18px] mb-2">Company</h4>
                <a href="https://emergeaitech.com" target="_blank" rel="noopener noreferrer" className="text-[16px] text-slate-700 font-semibold hover:text-[#3b82f6] transition-colors">About Us</a>
              </div>

              {/* Legal Column */}
              <div className="flex flex-col space-y-4 md:pl-8 md:border-l md:border-slate-300">
                <h4 className="text-[#3b82f6] font-bold text-[18px] mb-2">Legal</h4>
                <a href="#terms" className="text-[16px] text-slate-700 font-semibold hover:text-[#3b82f6] transition-colors">Terms of Use</a>
                <a href="#privacy" className="text-[16px] text-slate-700 font-semibold hover:text-[#3b82f6] transition-colors">Privacy Policy</a>
              </div>

              {/* Connect Column */}
              <div className="flex flex-col space-y-4 md:pl-8 md:border-l md:border-slate-300">
                <h4 className="text-[#3b82f6] font-bold text-[18px] mb-2">Connect with us</h4>
                <a href="mailto:info@emergeaitech.com" className="text-[16px] text-slate-700 font-semibold hover:text-[#3b82f6] transition-colors">info@emergeaitech.com</a>
              </div>

              {/* Support Column */}
              <div className="flex flex-col space-y-4 md:pl-8 md:border-l md:border-slate-300">
                <h4 className="text-[#3b82f6] font-bold text-[18px] mb-2">Support</h4>
                <a href="mailto:techsupport@emergeaitech.com" className="text-[16px] text-slate-700 font-semibold hover:text-[#3b82f6] transition-colors">techsupport@emergeaitech.com</a>
              </div>
            </div>
          </div>

          {/* Bottom Footer Bar */}
          <div className="bg-[#282828] min-h-[60px] px-6 w-full flex items-center justify-center">
            <p className="text-[14px] md:text-base text-gray-300 font-medium tracking-wide m-0 leading-none">
              EmergeAI Risk Radar &copy; 2026. All Rights Reserved.
            </p>
          </div>
        </footer>


        {/* Auth Modal Overlay */}
        {showAuthForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-md">
            {/* Modal Container */}
            <div className="bg-white rounded-[24px] shadow-[0_24px_80px_rgba(2,6,23,0.55)] w-full max-w-4xl max-h-[calc(100vh-2rem)] flex flex-col md:flex-row overflow-hidden relative border border-white/20">
               
               {/* Close button */}
               {!isDedicatedAuthPage && (
                 <button onClick={() => setShowAuthForm(false)} aria-label="Close sign-in" className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 z-20 transition-colors bg-white/80 backdrop-blur rounded-full p-1 shadow-sm">
                   <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                 </button>
               )}

               {/* Left Side - Dark Branding */}
               <div className="hidden md:flex md:w-[45%] bg-[#020617] p-10 flex-col justify-between relative overflow-hidden text-white">
                 {/* Dark side background effects */}
                 <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/20 blur-[60px] rounded-full pointer-events-none transform translate-x-1/2 -translate-y-1/2" />
                 <div className="absolute bottom-0 left-0 w-64 h-64 bg-cyan-600/20 blur-[60px] rounded-full pointer-events-none transform -translate-x-1/2 translate-y-1/2" />
                 
                 <div className="relative z-10">
                   <div className="mb-16">
                     <img src="/risk.svg" onError={(e) => { e.target.onerror = null; e.target.src="/risk.png"; e.target.className="h-16 w-auto object-contain brightness-0 invert"; }} alt="Risk Radar Logo" className="h-16 w-auto brightness-0 invert" />
                   </div>

                   <h2 className="text-4xl font-bold mb-6 leading-[1.2] text-white">
                     Welcome<br/>back to<br/>EmergeAI
                   </h2>
                   
                   <p className="text-slate-300 text-sm leading-relaxed mb-8 max-w-[200px]">
                     Quality and Safety Assessment for Software Deployment Pipelines.
                   </p>
                 </div>

                 <div className="relative z-10 text-xs text-slate-500 font-medium">
                   Copyright 2026 EmergeAI. All rights reserved.
                 </div>
               </div>

               {/* Right Side - Form */}
               <div className="w-full md:w-[55%] p-8 md:p-14 bg-white flex flex-col justify-center overflow-y-auto">
                 <form onSubmit={handleAuthSubmit} className="space-y-6 max-w-sm w-full mx-auto">
                    <div className="space-y-2 pr-8">
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-600">Secure workspace</p>
                      <h1 className="text-3xl font-bold tracking-tight text-slate-950" style={{fontFamily: 'Sora, sans-serif'}}>
                        {authMode === 'login' ? 'Sign in to Risk Radar'
                          : authMode === 'signup' ? 'Request workspace access'
                          : authMode === 'forgot' ? 'Reset your password'
                          : 'Choose a new password'}
                      </h1>
                      <p className="text-sm leading-relaxed text-slate-500">
                        {authMode === 'login' ? 'Continue to your AI-powered release risk workspace.'
                          : authMode === 'signup' ? 'Tell us about yourself. An administrator will review your request.'
                          : authMode === 'forgot' ? 'We will send recovery instructions to your registered email.'
                          : 'Use at least eight characters for your new password.'}
                      </p>
                    </div>

                    {!isDedicatedAuthPage && (authMode === 'login' || authMode === 'signup') && (
                      <div className="grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1" role="tablist" aria-label="Authentication mode">
                        <button
                          type="button"
                          role="tab"
                          aria-selected={authMode === 'login'}
                          onClick={() => { setAuthMode('login'); setAuthError(null); setAuthNotice(null); }}
                          className={`rounded-lg px-3 py-2.5 text-sm font-bold transition-all ${authMode === 'login' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                        >
                          Sign In
                        </button>
                        <button
                          type="button"
                          role="tab"
                          aria-selected={authMode === 'signup'}
                          onClick={() => { setAuthMode('signup'); setAuthError(null); setAuthNotice(null); }}
                          className={`rounded-lg px-3 py-2.5 text-sm font-bold transition-all ${authMode === 'signup' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                        >
                          Request Access
                        </button>
                      </div>
                    )}
                    {authMode === 'forgot' && (
                      <p className="text-sm text-slate-500">
                        Enter your account email and we'll send you a password reset link.
                      </p>
                    )}
                    {authMode === 'reset' && (
                      <p className="text-sm text-slate-500">
                        Choose a new password for your account.
                      </p>
                    )}

                    {authMode !== 'reset' && (
                      <div>
                        <label className="block text-xs font-bold text-blue-500 mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          required
                          autoComplete="email"
                          value={authEmail}
                          onChange={(e) => setAuthEmail(e.target.value)}
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                        />
                      </div>
                    )}

                    {authMode === 'signup' && (
                      <>
                        <div>
                          <label className="block text-xs font-bold text-blue-500 mb-2">
                            Full Name *
                          </label>
                          <input
                            type="text"
                            required
                            autoComplete="name"
                            value={authName}
                            onChange={(e) => setAuthName(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-blue-500 mb-2">
                            Organization Name *
                          </label>
                          <input
                            type="text"
                            required
                            autoComplete="organization"
                            value={authOrganization}
                            onChange={(e) => setAuthOrganization(e.target.value)}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-bold text-blue-500 mb-2">
                              Phone Number *
                            </label>
                            <input
                              type="tel"
                              required
                              autoComplete="tel"
                              value={authPhone}
                              onChange={(e) => setAuthPhone(e.target.value)}
                              className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-blue-500 mb-2">
                              Designation *
                            </label>
                            <input
                              type="text"
                              required
                              autoComplete="organization-title"
                              value={authDesignation}
                              onChange={(e) => setAuthDesignation(e.target.value)}
                              className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {authMode !== 'forgot' && (
                      <div>
                        <label className="block text-xs font-bold text-blue-500 mb-2">
                          {authMode === 'reset' ? 'New Password *' : 'Password *'}
                        </label>
                        <input
                          type="password"
                          required
                          autoComplete={authMode === 'login' ? 'current-password' : 'new-password'}
                          value={authPassword}
                          onChange={(e) => setAuthPassword(e.target.value)}
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
                        />
                        {(authMode === 'signup' || authMode === 'reset') && (
                          <p className="text-[11px] text-slate-400 mt-1.5">Minimum 8 characters.</p>
                        )}
                      </div>
                    )}

                    {authMode === 'login' && (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <input type="checkbox" id="remember" className="rounded border-slate-300 text-blue-500 focus:ring-blue-500 w-4 h-4" />
                          <label htmlFor="remember" className="text-sm font-medium text-blue-500">Remember Me</label>
                        </div>
                        <button
                          type="button"
                          onClick={() => { setAuthMode('forgot'); setAuthError(null); setAuthNotice(null); }}
                          className="text-sm font-medium text-slate-500 hover:text-blue-500 transition-colors"
                        >
                          Forgot password?
                        </button>
                      </div>
                    )}

                    {authError && (
                      <div className="text-xs font-semibold text-red-500 mt-2 bg-red-50 p-2 rounded border border-red-100">
                        {authError}
                      </div>
                    )}
                    {authNotice && (
                      <div className="text-xs font-semibold text-emerald-600 mt-2 bg-emerald-50 p-2 rounded border border-emerald-100">
                        {authNotice}
                      </div>
                    )}

                    <div className="pt-2">
                      <button
                        type="submit"
                        disabled={authLoading}
                        className="w-full py-3.5 px-4 rounded-lg text-sm font-bold text-white bg-blue-500 hover:bg-blue-600 transition-colors flex justify-center items-center shadow-md shadow-blue-500/20"
                      >
                        {authLoading ? (
                          <div className="animate-spin h-5 w-5 border-2 border-t-transparent rounded-full" />
                        ) : (
                          authMode === 'login' ? 'Log In'
                          : authMode === 'signup' ? 'Request Access'
                          : authMode === 'forgot' ? 'Send Reset Link'
                          : 'Set New Password'
                        )}
                      </button>
                    </div>

                    <div className="mt-4 text-center text-xs">
                      {authMode === 'forgot' || authMode === 'reset' ? (
                        <button
                          type="button"
                          onClick={() => {
                            setAuthMode('login');
                            setAuthError(null);
                            setAuthNotice(null);
                          }}
                          className="font-medium text-slate-500 hover:text-blue-500 transition-colors"
                        >
                          Back to Log In
                        </button>
                      ) : isDedicatedAuthPage && authMode === 'login' ? (
                        <a
                          href="/register"
                          className="font-medium text-slate-500 hover:text-blue-500 transition-colors"
                        >
                          Don't have an account? Request Access
                        </a>
                      ) : isDedicatedAuthPage ? (
                        <a
                          href="/login"
                          className="font-medium text-slate-500 hover:text-blue-500 transition-colors"
                        >
                          Already registered? Sign In
                        </a>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setAuthMode(authMode === 'login' ? 'signup' : 'login');
                            setAuthError(null);
                            setAuthNotice(null);
                          }}
                          className="font-medium text-slate-500 hover:text-blue-500 transition-colors"
                        >
                          {authMode === 'login'
                            ? "Don't have an account? Request Access"
                            : "Already registered? Sign In"}
                        </button>
                      )}
                    </div>
                 </form>
               </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // LOGGED IN DASHBOARD
  return (
    <div className="min-h-screen bg-[var(--color-bg-alt)] font-['Inter'] text-[var(--color-text-primary)]">
      {/* TOP NAVBAR */}
      <header className="fixed top-0 left-0 right-0 h-[64px] bg-white border-b border-[var(--color-border)] shadow-[0px_2px_12px_rgba(0,0,0,0.06)] z-50 flex justify-between items-center px-4 md:px-6">
        <div className="flex items-center">
          <a href="/" className="flex items-center hover:opacity-80 transition-opacity">
            <img src="/risk.svg" onError={(e) => { e.target.onerror = null; e.target.src="/risk.png"; }} alt="Risk Radar Logo" style={{ height: '36px' }} />
          </a>
          <div className="hidden md:block w-px h-[24px] bg-[var(--color-border)] mx-4"></div>
          <span className="hidden md:block font-['Sora'] text-[16px] font-semibold text-[var(--color-navy)]">EmergeAI Risk Radar</span>
          <nav className="hidden md:flex items-center ml-8 space-x-1">
            {[
              { key: 'dashboard', label: 'Dashboard' },
              { key: 'reports', label: 'Historical Reports' },
              { key: 'integrations', label: 'Integrations' },
            ].map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setActiveView(item.key)}
                className={`px-3 py-2 rounded-lg text-[13px] font-bold transition-colors duration-200 ${
                  activeView === item.key
                    ? 'bg-[var(--color-accent-light)] text-[var(--color-accent)]'
                    : 'text-[var(--color-text-secondary)] hover:text-[var(--color-navy)] hover:bg-[var(--color-bg-alt)]'
                }`}
              >
                {item.label}
              </button>
            ))}

            {/* Non-admin: Feedback & Observability nav items (shown when access granted via UAM) */}
            {!user?.is_admin && user?.can_access_feedback && (
              <button
                type="button"
                onClick={() => setActiveView('feedback')}
                className={`px-3 py-2 rounded-lg text-[13px] font-bold transition-colors duration-200 ${
                  activeView === 'feedback'
                    ? 'bg-[var(--color-accent-light)] text-[var(--color-accent)]'
                    : 'text-[var(--color-text-secondary)] hover:text-[var(--color-navy)] hover:bg-[var(--color-bg-alt)]'
                }`}
              >
                💬 Feedback
              </button>
            )}
            {!user?.is_admin && user?.can_access_observability && (
              <button
                type="button"
                onClick={() => setActiveView('observability')}
                className={`px-3 py-2 rounded-lg text-[13px] font-bold transition-colors duration-200 ${
                  activeView === 'observability'
                    ? 'bg-[var(--color-accent-light)] text-[var(--color-accent)]'
                    : 'text-[var(--color-text-secondary)] hover:text-[var(--color-navy)] hover:bg-[var(--color-bg-alt)]'
                }`}
              >
                🛡️ AI Observability
              </button>
            )}

            {/* Admin Hover Dropdown Sub-menu */}
            {user?.is_admin && (
              <div className="relative group">
                <button
                  type="button"
                  onClick={() => {
                    setActiveView(adminSubTab || 'uam');
                  }}
                  className={`px-3.5 py-2 rounded-lg text-[13px] font-bold transition-colors duration-200 flex items-center gap-1.5 cursor-pointer ${
                    ['admin', 'uam', 'feedback', 'observability'].includes(activeView)
                      ? 'bg-[var(--color-accent-light)] text-[var(--color-accent)]'
                      : 'text-[var(--color-text-secondary)] hover:text-[var(--color-navy)] hover:bg-[var(--color-bg-alt)]'
                  }`}
                >
                  <span>Admin</span>
                  <span className="text-[10px] opacity-70 transition-transform duration-200 group-hover:rotate-180">▼</span>
                  {adminUsers.some((u) => u.status === 'pending') && (
                    <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse ml-0.5" />
                  )}
                </button>

                {/* Sub-menu Dropdown List */}
                <div className="absolute left-0 top-full pt-1.5 hidden group-hover:block z-50 min-w-[240px] animate-in fade-in slide-in-from-top-1 duration-150">
                  <div className="bg-white rounded-xl shadow-xl border border-slate-200/90 py-1.5 px-1 space-y-0.5">
                    {[
                      { key: 'uam', label: 'User Access Management', desc: 'Users, roles & access permissions', icon: '👥' },
                      { key: 'feedback', label: 'Feedback & Reviews', desc: 'Output satisfaction & ratings', icon: '💬' },
                      { key: 'observability', label: 'AI Observability', desc: 'Guardrail metrics & safety logs', icon: '🛡️' },
                    ].map((sub) => {
                      const isSubActive = activeView === sub.key || (['admin', 'uam', 'feedback', 'observability'].includes(activeView) && adminSubTab === sub.key);
                      return (
                        <button
                          key={sub.key}
                          type="button"
                          onClick={() => {
                            setActiveView(sub.key);
                            setAdminSubTab(sub.key);
                          }}
                          className={`w-full text-left px-3 py-2.5 rounded-lg text-xs font-bold transition-all flex items-start gap-2.5 cursor-pointer ${
                            isSubActive
                              ? 'bg-[var(--color-accent-light)] text-[var(--color-accent)] font-extrabold'
                              : 'text-slate-700 hover:bg-slate-100/80 hover:text-slate-900'
                          }`}
                        >
                          <span className="text-sm mt-0.5">{sub.icon}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <span className="font-extrabold">{sub.label}</span>
                              {sub.key === 'uam' && adminUsers.some((u) => u.status === 'pending') && (
                                <span className="text-[9px] font-bold px-1.5 py-0.2 bg-amber-100 text-amber-700 rounded-full">Action</span>
                              )}
                            </div>
                            <div className="text-[10px] text-slate-500 font-normal mt-0.5 truncate">{sub.desc}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </nav>
        </div>

        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <div className="w-[32px] h-[32px] rounded-full bg-[var(--color-navy)] flex items-center justify-center text-white text-sm font-bold">
              {user?.email?.charAt(0)?.toUpperCase()}
            </div>
            <span className="hidden md:block font-['Inter'] text-[14px] text-[var(--color-text-secondary)]">{user?.email}</span>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            title="Sign Out"
            className="text-[var(--color-text-muted)] hover:text-red-500 transition-colors duration-200 focus:outline-none bg-transparent border-none p-0 cursor-pointer flex items-center"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>
        </div>
      </header>

      {/* MAIN CONTENT AREA */}
      <main className="pt-[64px] pb-12 w-full">
        <div className="max-w-[1100px] mx-auto px-4 md:px-8 mt-8">
          
          {/* PAGE HEADER */}
          <div className="mb-8 dashboard-reveal reveal">
            <div className="text-[11px] text-[var(--color-accent)] font-bold uppercase tracking-[2px] mb-2">
              {['admin', 'uam', 'feedback', 'observability'].includes(activeView)
                ? (activeView === 'uam' || adminSubTab === 'uam'
                    ? 'USER ACCESS MANAGEMENT'
                    : activeView === 'feedback' || adminSubTab === 'feedback'
                    ? 'FEEDBACK & REVIEWS'
                    : 'AI OBSERVABILITY')
                : 'QA RISK ASSESSMENT'}
            </div>
            <h1 className="font-['Sora'] text-[28px] font-bold text-[var(--color-navy)] mb-2">
              {activeView === 'reports'
                ? 'Historical Reports'
                : activeView === 'integrations'
                ? 'Integrations'
                : activeView === 'uam' || (activeView === 'admin' && adminSubTab === 'uam')
                ? 'User Access Management'
                : activeView === 'feedback' || (activeView === 'admin' && adminSubTab === 'feedback')
                ? 'Feedback & Reviews'
                : activeView === 'observability' || (activeView === 'admin' && adminSubTab === 'observability')
                ? 'AI Observability'
                : 'EmergeAI Risk Radar'}
            </h1>
            <p className="font-['Inter'] text-[14px] text-[var(--color-text-secondary)]">
              {activeView === 'reports'
                ? 'Browse and reopen past risk assessments.'
                : activeView === 'integrations'
                ? 'Connect external sources so RiskRadar can pull context directly instead of a manual upload.'
                : activeView === 'uam' || (activeView === 'admin' && adminSubTab === 'uam')
                ? 'Review access requests, manage active users, and control account permissions.'
                : activeView === 'feedback' || (activeView === 'admin' && adminSubTab === 'feedback')
                ? 'Analyze output satisfaction ratings, feedback metrics, and user reviews.'
                : activeView === 'observability' || (activeView === 'admin' && adminSubTab === 'observability')
                ? 'Monitor AI performance, response latency, token costs, grounding quality, and safety logs.'
                : 'Upload your release documents and get AI-powered risk analysis'}
            </p>

            <div className="h-px bg-[var(--color-border)] mt-6 mb-8 w-full"></div>
          </div>

          {activeView === 'dashboard' && (
            <div className="flex items-center justify-between mb-8 dashboard-reveal reveal">
              {[
                { key: 'upload', label: 'Upload Documents', done: hasAtLeastOneFile },
                { key: 'analyze', label: 'Analyze', done: !!analysisResult },
                { key: 'review', label: 'Review Results', done: !!analysisResult },
                { key: 'tests', label: 'Generate Tests', done: !!testAssets },
              ].map((step, idx, arr) => {
                const isCurrent = !step.done && (idx === 0 || arr[idx - 1].done);
                return (
                  <React.Fragment key={step.key}>
                    <div className="flex flex-col items-center flex-shrink-0">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-bold border-2 transition-colors ${
                        step.done ? 'bg-[var(--color-accent)] border-[var(--color-accent)] text-white'
                          : isCurrent ? 'border-[var(--color-accent)] text-[var(--color-accent)] bg-white'
                            : 'border-[var(--color-border)] text-[var(--color-text-muted)] bg-white'
                      }`}>
                        {step.done ? '✓' : idx + 1}
                      </div>
                      <span className={`mt-2 text-[11px] font-bold text-center whitespace-nowrap ${step.done || isCurrent ? 'text-[var(--color-navy)]' : 'text-[var(--color-text-muted)]'}`}>
                        {step.label}
                      </span>
                    </div>
                    {idx < arr.length - 1 && (
                      <div className={`flex-1 h-[2px] mx-2 mb-5 ${arr[idx].done ? 'bg-[var(--color-accent)]' : 'bg-[var(--color-border)]'}`}></div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}

          {activeView === 'dashboard' && (
          <div className="grid grid-cols-1 gap-8">
            {/* WORKSPACE CONFIGURATION */}
            <section className="bg-white rounded-[16px] p-6 md:p-8 shadow-[var(--color-card-shadow)] dashboard-reveal reveal">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
                <h3 className="font-['Sora'] text-[18px] font-bold text-[var(--color-navy)]">Workspace Configuration</h3>
                <div className="flex bg-[var(--color-bg-alt)] border border-[var(--color-border)] p-1 rounded-xl mt-4 sm:mt-0">
                  <button
                    onClick={() => { setWorkspaceTab('current'); setUploadError(null); setDocSourceMode('upload'); }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${workspaceTab === 'current' ? 'bg-[var(--color-accent)] text-white shadow-sm' : 'text-[var(--color-text-secondary)] hover:text-[var(--color-navy)] hover:bg-white/60'}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${workspaceTab === 'current' ? 'bg-white' : 'bg-[var(--color-text-secondary)]'}`}></span>
                    Active Targets
                  </button>
                  <button
                    onClick={() => { setWorkspaceTab('previous'); setUploadError(null); setDocSourceMode('upload'); }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${workspaceTab === 'previous' ? 'bg-[#7c3aed] text-white shadow-sm' : 'text-[var(--color-text-secondary)] hover:text-[var(--color-navy)] hover:bg-white/60'}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${workspaceTab === 'previous' ? 'bg-white' : 'bg-[var(--color-text-secondary)]'}`}></span>
                    Historical KB
                  </button>
                </div>
              </div>

              {/* Category Selector */}
              <div className="mb-8">
                <label className="block text-[12px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wide mb-3">
                  1. Select Artifact Category
                  <InfoTooltip text="This category is how the file gets stored and analyzed — it isn't detected from the file's name or content, so double-check it matches before uploading." />
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { id: 'requirement', label: 'Requirements' },
                    { id: 'bug_report', label: 'Bug Reports' },
                    { id: 'test_case', label: 'Test Cases' }
                  ].map((item) => (
                    <button
                      key={item.id}
                      onClick={() => { setSelectedType(item.id); setDocSourceMode('upload'); }}
                      className={`py-3 px-4 rounded-xl font-bold text-sm border transition-all duration-200 hover:-translate-y-[2px] ${selectedType === item.id ? 'bg-[var(--color-accent-light)] text-[var(--color-accent)] border-[var(--color-accent)]' : 'bg-white text-[var(--color-text-secondary)] border-[var(--color-border)] hover:border-[var(--color-accent)]'}`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* File Upload Zone — one source visible at a time (Upload vs Jira), chosen via
                  the toggle, rather than always stacking every source underneath the other. */}
              <div className="mb-8">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                  <label className="block text-[12px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wide">
                    2. Upload Document Context
                  </label>
                  {jiraAvailableForCurrentSelection && (
                    <div className="inline-flex rounded-lg border border-[var(--color-border)] overflow-hidden flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => setDocSourceMode('upload')}
                        className={`px-3 py-1.5 text-[12px] font-bold transition-colors ${docSourceMode === 'upload' ? 'bg-[var(--color-accent)] text-white' : 'bg-white text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-alt)]'}`}
                      >
                        Upload file
                      </button>
                      <button
                        type="button"
                        onClick={() => setDocSourceMode('jira')}
                        className={`px-3 py-1.5 text-[12px] font-bold transition-colors border-l border-[var(--color-border)] ${docSourceMode === 'jira' ? 'bg-[var(--color-accent)] text-white' : 'bg-white text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-alt)]'}`}
                      >
                        {selectedType === 'bug_report' ? 'Pull from JIRA (JQL)' : 'Pull from JIRA'}
                      </button>
                    </div>
                  )}
                </div>

                {docSourceMode === 'upload' && (
                  <>
                    <div
                      onDragEnter={handleDrag}
                      onDragOver={handleDrag}
                      onDragLeave={handleDrag}
                      onDrop={handleDrop}
                      className={`relative border-2 border-dashed rounded-[16px] p-8 flex flex-col items-center justify-center transition-all duration-200 cursor-pointer ${dragActive ? 'border-[var(--color-accent)] bg-[var(--color-accent-light)]' : 'border-[var(--color-border)] hover:border-[var(--color-accent)] bg-white'}`}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        id="file-browse"
                        className="hidden"
                        accept=".pdf,.docx,.xlsx,.xls,.csv"
                        onChange={handleFileBrowse}
                        disabled={uploadingFile}
                      />
                      <label htmlFor="file-browse" className="cursor-pointer flex flex-col items-center text-center">
                        <svg className="w-10 h-10 text-[var(--color-text-muted)] mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <span className="font-['Inter'] text-[14px] text-[var(--color-text-secondary)] mb-3">
                          {uploadingFile ? "Uploading..." : `Drag & drop ${workspaceTab === 'previous' ? 'reference ' : ''}files here, or click to browse`}
                        </span>
                        <span className="px-3 py-1 rounded-full bg-[var(--color-accent-light)] text-[var(--color-accent)] text-[11px] font-bold">
                          PDF • DOCX • XLSX • CSV
                        </span>
                      </label>
                    </div>
                    {uploadError && (
                      <div className="mt-3 text-[13px] text-red-600 font-semibold">{uploadError}</div>
                    )}
                    <div className="mt-3 text-[12px] text-[var(--color-text-muted)] space-y-1">
                      <div>📁 Max file size: <span className="font-semibold">5 MB</span></div>
                      {workspaceTab === 'previous' && (
                        <div>📚 {selectedType.replace('_', ' ')}: <span className="font-semibold">{previousFiles[selectedType].length}/10</span> files</div>
                      )}
                      {workspaceTab !== 'previous' && selectedType === 'requirement' && (
                        <div>📄 Requirements: <span className="font-semibold">{uploadedFiles.requirement.length}/5</span> files</div>
                      )}
                    </div>
                    {jiraStatus && !jiraStatus.connected && jiraConnectableForCurrentSelection && (
                      <div className="mt-4 pt-4 border-t border-dashed border-[var(--color-border)] text-[12px] text-[var(--color-text-muted)]">
                        <span className="mr-1">📋</span> Want to pull {selectedType === 'bug_report' ? 'defects' : 'requirements'} from JIRA?{' '}
                        <button type="button" onClick={() => setActiveView('integrations')} className="font-bold text-[var(--color-accent)] hover:underline">
                          Connect it in Integrations
                        </button>.
                      </div>
                    )}
                  </>
                )}

                {docSourceMode === 'jira' && jiraAvailableForCurrentSelection && (
                  <>
                    {selectedType === 'requirement' && (
                      <div>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={jiraStoryInput}
                            onChange={(e) => setJiraStoryInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleJiraFetchStory(); } }}
                            placeholder="Story key (e.g. ABC-123) or story URL"
                            disabled={jiraFetching}
                            className="flex-1 px-3 py-2 text-[13px] rounded-lg border border-[var(--color-border)] bg-white focus:ring-2 focus:ring-[var(--color-accent-light)] focus:border-[var(--color-accent)] transition-all outline-none disabled:bg-[var(--color-bg-alt)]"
                          />
                          <button
                            type="button"
                            onClick={handleJiraFetchStory}
                            disabled={jiraFetching || !jiraStoryInput.trim()}
                            className="px-4 py-2 rounded-lg text-[13px] font-bold text-white bg-[var(--color-navy)] hover:bg-[#1a2352] transition-colors disabled:opacity-60 whitespace-nowrap"
                          >
                            {jiraFetching ? 'Fetching…' : 'Fetch Story'}
                          </button>
                        </div>
                        <p className="mt-1.5 text-[11px] text-[var(--color-text-muted)]">
                          Fetches the story's summary and description and loads it as a requirement — {workspaceTab === 'previous' ? 'added to the Historical KB (up to 10)' : 'added to Active Targets (up to 5)'}.
                        </p>
                        {jiraFetchError && (
                          <div className="mt-2 text-[12px] text-red-600 font-semibold">{jiraFetchError}</div>
                        )}
                      </div>
                    )}

                    {selectedType === 'bug_report' && (
                      <div>
                        {jiraSavedQueries.length > 0 && (
                          <div className="flex gap-2 items-center mb-2">
                            <select
                              value={selectedSavedQueryId}
                              onChange={(e) => handleSelectSavedQuery(e.target.value)}
                              disabled={jiraJqlFetching}
                              className="flex-1 px-3 py-2 text-[12px] rounded-lg border border-[var(--color-border)] bg-white focus:ring-2 focus:ring-[var(--color-accent-light)] focus:border-[var(--color-accent)] transition-all outline-none disabled:bg-[var(--color-bg-alt)]"
                            >
                              <option value="">Saved queries ({jiraSavedQueries.length}/5)</option>
                              {jiraSavedQueries.map((q) => (
                                <option key={q.id} value={q.id}>{q.name}</option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={() => selectedSavedQueryId && handleJiraDeleteQuery(selectedSavedQueryId)}
                              disabled={!selectedSavedQueryId}
                              title="Delete selected saved query"
                              className="flex-shrink-0 text-[var(--color-text-muted)] hover:text-red-500 disabled:opacity-30 disabled:hover:text-[var(--color-text-muted)] transition-colors p-2"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </button>
                          </div>
                        )}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={jiraJqlInput}
                            onChange={(e) => setJiraJqlInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleJiraFetchDefects(); } }}
                            placeholder={'e.g. project = KAN AND type = Bug AND status != Done'}
                            disabled={jiraJqlFetching}
                            className="flex-1 px-3 py-2 text-[13px] font-mono rounded-lg border border-[var(--color-border)] bg-white focus:ring-2 focus:ring-[var(--color-accent-light)] focus:border-[var(--color-accent)] transition-all outline-none disabled:bg-[var(--color-bg-alt)]"
                          />
                          <button
                            type="button"
                            onClick={() => handleJiraFetchDefects()}
                            disabled={jiraJqlFetching || !jiraJqlInput.trim()}
                            className="px-4 py-2 rounded-lg text-[13px] font-bold text-white bg-[var(--color-navy)] hover:bg-[#1a2352] transition-colors disabled:opacity-60 whitespace-nowrap"
                          >
                            {jiraJqlFetching ? 'Fetching…' : 'Fetch Defects'}
                          </button>
                        </div>
                        <div className="flex gap-2 mt-2 items-center">
                          <input
                            type="text"
                            value={jiraQueryNameInput}
                            onChange={(e) => setJiraQueryNameInput(e.target.value)}
                            placeholder="Name (optional)"
                            maxLength={60}
                            disabled={jiraJqlSaving || jiraSavedQueries.length >= 5}
                            className="w-48 px-3 py-1.5 text-[12px] rounded-lg border border-[var(--color-border)] bg-white focus:ring-2 focus:ring-[var(--color-accent-light)] focus:border-[var(--color-accent)] transition-all outline-none disabled:bg-[var(--color-bg-alt)]"
                          />
                          <button
                            type="button"
                            onClick={handleJiraSaveQuery}
                            disabled={jiraJqlSaving || !jiraJqlInput.trim() || jiraSavedQueries.length >= 5}
                            title={jiraSavedQueries.length >= 5 ? 'Limit of 5 saved queries reached — delete one first' : 'Validate and save this JQL for reuse'}
                            className="px-3 py-1.5 rounded-lg text-[12px] font-bold text-[var(--color-accent)] border border-[var(--color-accent)] hover:bg-[var(--color-accent-light)] transition-colors disabled:opacity-50"
                          >
                            {jiraJqlSaving ? 'Saving…' : 'Save query'}
                          </button>
                          {jiraSavedQueries.length === 0 && (
                            <span className="text-[11px] text-[var(--color-text-muted)]">0/5 saved</span>
                          )}
                        </div>
                        <p className="mt-1.5 text-[11px] text-[var(--color-text-muted)]">
                          Runs the JQL (up to 50 issues) and loads the results as one bug report document — {workspaceTab === 'previous' ? 'added to the Historical KB' : 'replacing the current bug report'}.
                        </p>
                        {jiraJqlNotice && (
                          <div className="mt-2 text-[12px] text-emerald-600 font-semibold">{jiraJqlNotice}</div>
                        )}
                        {jiraJqlError && (
                          <div className="mt-2 text-[12px] text-red-600 font-semibold">{jiraJqlError}</div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Document List */}
              {(hasAtLeastOneFile || Object.values(previousFiles).some(arr => arr.length > 0) || pendingPreviousFiles.length > 0) && (
                <div className="mb-8 space-y-4">
                  {pendingPreviousFiles.length > 0 && (
                    <div className="space-y-2">
                      <span className="block text-[11px] font-bold text-[var(--color-orange)] uppercase tracking-wide">Pending Reference Uploads:</span>
                      <div className="flex flex-col gap-2">
                        {pendingPreviousFiles.map((item, index) => (
                          <div key={index} className="flex items-center justify-between p-3 rounded-xl border border-[var(--color-border)] bg-white group hover:-translate-y-[2px] transition-transform duration-200 shadow-sm">
                            <div className="flex items-center space-x-3">
                              <span className="font-bold text-[10px] bg-[var(--color-bg-alt)] px-2 py-1 rounded text-[var(--color-text-secondary)] uppercase">{item.type.split('_')[0]}</span>
                              <span className="text-[14px] font-semibold text-[var(--color-navy)] truncate max-w-[250px] md:max-w-[400px]">{item.file.name}</span>
                            </div>
                            <button onClick={() => setPendingPreviousFiles(prev => prev.filter((_, idx) => idx !== index))} className="opacity-0 group-hover:opacity-100 text-[var(--color-text-muted)] hover:text-red-500 transition-all p-1">
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {hasAtLeastOneFile && (
                    <div className="space-y-2">
                      <span className="block text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wide">Active Target Deployment Files:</span>
                      <div className="flex flex-col gap-2">
                        {Object.entries(uploadedFiles).map(([type, files]) => (
                          files.map((file) => (
                            <div key={`${type}-${file.document_id}`} className="flex items-center justify-between p-3 rounded-xl border border-[var(--color-border)] bg-white group hover:-translate-y-[2px] transition-transform duration-200 shadow-sm">
                              <div className="flex items-center space-x-3">
                                <span className="font-bold text-[10px] bg-[var(--color-accent-light)] px-2 py-1 rounded text-[var(--color-accent)] uppercase">{type.split('_')[0]}</span>
                                <span className="text-[14px] font-semibold text-[var(--color-navy)] truncate max-w-[250px] md:max-w-[400px]">{file.filename}</span>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <button
                                  type="button"
                                  onClick={() => handlePreviewDocument(file)}
                                  title="Preview extracted text"
                                  className="opacity-0 group-hover:opacity-100 text-[12px] font-bold text-[var(--color-accent)] hover:underline transition-all px-2 py-1"
                                >
                                  Preview
                                </button>
                                <button onClick={() => handleRemoveFile(type, false, file.document_id)} className="opacity-0 group-hover:opacity-100 text-[var(--color-text-muted)] hover:text-red-500 transition-all p-1">
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                </button>
                              </div>
                            </div>
                          ))
                        ))}
                      </div>
                    </div>
                  )}

                  {Object.values(previousFiles).some(arr => arr.length > 0) && (
                    <div className="space-y-2">
                      <span className="block text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wide">Reference Knowledge Base (RAG):</span>
                      <div className="flex flex-col gap-2">
                        {Object.entries(previousFiles).map(([type, files]) => (
                          files.map(file => (
                            <div key={`${type}-${file.document_id}`} className="flex items-center justify-between p-3 rounded-xl border border-[var(--color-border)] bg-white group hover:-translate-y-[2px] transition-transform duration-200 shadow-sm">
                              <div className="flex items-center space-x-3">
                                <span className="font-bold text-[10px] bg-[var(--color-bg-alt)] px-2 py-1 rounded text-[var(--color-text-secondary)] uppercase">Prev {type.split('_')[0]}</span>
                                <span className="text-[14px] font-semibold text-[var(--color-navy)] truncate max-w-[250px] md:max-w-[400px]">{file.filename}</span>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <button
                                  type="button"
                                  onClick={() => handlePreviewDocument(file)}
                                  title="Preview extracted text"
                                  className="opacity-0 group-hover:opacity-100 text-[12px] font-bold text-[var(--color-accent)] hover:underline transition-all px-2 py-1"
                                >
                                  Preview
                                </button>
                                <button onClick={() => handleRemoveFile(type, true, file.document_id)} className="opacity-0 group-hover:opacity-100 text-[var(--color-text-muted)] hover:text-red-500 transition-all p-1">
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                </button>
                              </div>
                            </div>
                          ))
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Custom Instructions */}
              {workspaceTab === 'current' && (
                <div className="mb-8">
                  <label htmlFor="custom-instruction" className="block text-[12px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wide mb-3">
                    3. Query Options / Custom Instruction (Optional)
                  </label>
                  <textarea
                    id="custom-instruction"
                    rows={3}
                    value={customInstruction}
                    onChange={(e) => setCustomInstruction(e.target.value)}
                    placeholder="e.g. Focus on financial modules, verify boundary value tests..."
                    className="w-full px-4 py-3 text-[14px] rounded-xl border border-[var(--color-border)] bg-white focus:bg-white focus:ring-2 focus:ring-[var(--color-accent-light)] focus:border-[var(--color-accent)] transition-all duration-200 outline-none resize-none placeholder-[var(--color-text-muted)] font-medium"
                  />
                  <label className="flex items-start space-x-2 mt-4 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={saveToHistorical}
                      onChange={(e) => setSaveToHistorical(e.target.checked)}
                      className="mt-0.5 rounded border-[var(--color-border)] text-[var(--color-accent)] focus:ring-[var(--color-accent)] w-4 h-4"
                    />
                    <span className="text-[13px] text-[var(--color-text-secondary)] font-medium group-hover:text-[var(--color-navy)] transition-colors">
                      Save current files to Historical KB after this analysis
                      <span className="block text-[11px] text-[var(--color-text-muted)] font-normal mt-0.5">
                        Archives today's requirements/bug reports/test cases as reference context for future releases.
                      </span>
                    </span>
                  </label>
                  {githubStatus?.connected && (
                    <label className="flex items-start space-x-2 mt-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={includeCodeImpact}
                        onChange={(e) => setIncludeCodeImpact(e.target.checked)}
                        className="mt-0.5 rounded border-[var(--color-border)] text-[var(--color-accent)] focus:ring-[var(--color-accent)] w-4 h-4"
                      />
                      <span className="text-[13px] text-[var(--color-text-secondary)] font-medium group-hover:text-[var(--color-navy)] transition-colors">
                        Include code impact analysis from linked repo
                        <span className="block text-[11px] text-[var(--color-text-muted)] font-normal mt-0.5">
                          Searches {githubStatus.repo_full_name} live for files potentially impacted by the current requirements. Nothing is cloned or stored.
                        </span>
                      </span>
                    </label>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col items-center">
                {workspaceTab === 'previous' ? (
                  <button
                    type="button"
                    onClick={handleUploadPreviousDocuments}
                    disabled={pendingPreviousFiles.length === 0 || uploadingFile}
                    className={`w-full py-4 rounded-[12px] text-[15px] font-bold transition-all duration-200 flex items-center justify-center space-x-2 shadow-sm hover:-translate-y-[2px] ${pendingPreviousFiles.length === 0 || uploadingFile ? 'bg-[var(--color-border)] text-[var(--color-text-muted)] cursor-not-allowed hover:translate-y-0' : 'bg-[var(--color-navy)] text-white hover:bg-[#1a2352]'}`}
                  >
                    {uploadingFile ? "Uploading to Sandbox..." : `Upload Reference Documents (${pendingPreviousFiles.length})`}
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={handleRunAnalysis}
                      disabled={!canRunAnalysis || isAnalysing}
                      className={`w-full py-4 rounded-[12px] text-[15px] font-bold transition-all duration-200 flex items-center justify-center space-x-2 shadow-sm hover:-translate-y-[2px] ${!canRunAnalysis || isAnalysing ? 'bg-[var(--color-border)] text-[var(--color-text-muted)] cursor-not-allowed hover:translate-y-0' : 'bg-[var(--color-orange)] text-white hover:bg-[#ea580c]'}`}
                    >
                      {isAnalysing
                        ? `Running Multi-Agent Risk Evaluation… ${analysisElapsed}s`
                        : "Analyze Release Risks"}
                    </button>

                    {analysisBlockReason && (
                      <div className="mt-3 text-[13px] text-[var(--color-text-secondary)] font-medium text-center">
                        {analysisBlockReason}
                      </div>
                    )}
                  </>
                )}
                {analysisError && (
                  <div className="mt-4 text-[13px] text-red-600 font-semibold">{analysisError}</div>
                )}
              </div>
            </section>

            {/* RESULTS PRESENTATION */}
            {analysisResult && (
              <section className="bg-white rounded-[16px] shadow-[var(--color-card-shadow)] overflow-hidden dashboard-reveal reveal">
                <div className="flex border-b border-[var(--color-border)] bg-[var(--color-bg-alt)]">
                  <button
                    onClick={() => setActiveTab('summary')}
                    className={`flex-1 py-4 text-[14px] font-bold transition-colors duration-200 ${activeTab === 'summary' ? 'bg-white text-[var(--color-navy)] border-b-2 border-[var(--color-accent)]' : 'text-[var(--color-text-secondary)] hover:text-[var(--color-navy)]'}`}
                  >
                    Risk Summary Report
                  </button>
                  <button
                    onClick={() => setActiveTab('matrix')}
                    className={`flex-1 py-4 text-[14px] font-bold transition-colors duration-200 ${activeTab === 'matrix' ? 'bg-white text-[var(--color-navy)] border-b-2 border-[var(--color-accent)]' : 'text-[var(--color-text-secondary)] hover:text-[var(--color-navy)]'}`}
                  >
                    Coverage Intersection Matrix
                  </button>
                </div>

                <div className="p-6 md:p-8">
                  {activeTab === 'summary' && (
                    <MultiAgentResultsView
                      analysisResult={analysisResult}
                      downloadAnalysisPdf={downloadAnalysisPdf}
                    />
                  )}


                  {activeTab === 'matrix' && (
                    <div className="overflow-x-auto rounded-[16px] border border-[var(--color-border)]">
                      <table className="w-full border-collapse text-left text-[14px]">
                        <thead className="bg-[var(--color-bg-alt)] border-b border-[var(--color-border)]">
                          <tr>
                            <th onClick={() => requestSort('id')} className="py-4 px-5 font-bold text-[var(--color-text-secondary)] text-[12px] uppercase tracking-wide cursor-pointer hover:bg-white transition-colors">
                              Requirement ID {getSortIcon('id')}
                            </th>
                            <th onClick={() => requestSort('title')} className="py-4 px-5 font-bold text-[var(--color-text-secondary)] text-[12px] uppercase tracking-wide cursor-pointer hover:bg-white transition-colors">
                              Title {getSortIcon('title')}
                            </th>
                            <th onClick={() => requestSort('coverage')} className="py-4 px-5 font-bold text-[var(--color-text-secondary)] text-[12px] uppercase tracking-wide cursor-pointer hover:bg-white transition-colors text-center">
                              Coverage {getSortIcon('coverage')}
                            </th>
                            <th onClick={() => requestSort('bugCount')} className="py-4 px-5 font-bold text-[var(--color-text-secondary)] text-[12px] uppercase tracking-wide cursor-pointer hover:bg-white transition-colors text-center">
                              Bug Count {getSortIcon('bugCount')}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[var(--color-border)] bg-white">
                          {sortedCoverageMatrix.length > 0 ? (
                            sortedCoverageMatrix.map((row, index) => {
                              const isHighRiskRow = !row.coverage && row.bugCount > 0;
                              return (
                                <tr key={index} className={isHighRiskRow ? "bg-[#fee2e2]/30" : "hover:bg-[var(--color-bg-alt)] transition-colors"}>
                                  <td className="py-4 px-5 font-mono font-bold text-[13px] text-[var(--color-accent)]">{row.id}</td>
                                  <td className="py-4 px-5 font-medium text-[var(--color-text-primary)] max-w-[280px] truncate" title={row.title}>{row.title}</td>
                                  <td className="py-4 px-5 text-center">
                                    {row.coverage ? (
                                      <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-[#dcfce7] text-[#16a34a] font-bold text-[12px]">✓</span>
                                    ) : (
                                      <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-[#fee2e2] text-[#dc2626] font-bold text-[12px]">✗</span>
                                    )}
                                  </td>
                                  <td className="py-4 px-5 text-center font-bold text-[13px]">
                                    <span className={`px-3 py-1 rounded-full ${row.bugCount > 0 ? "bg-[#fee2e2] text-[#dc2626]" : "bg-[var(--color-bg-alt)] text-[var(--color-text-secondary)]"}`}>
                                      {row.bugCount}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan="4" className="py-10 text-center text-[13px] text-[var(--color-text-muted)] font-medium">No matrix entries resolved from analysis context.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Agentic test generation */}
                  <div className="mt-8 pt-6 border-t border-[var(--color-border)]">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div>
                        <h3 className="text-[14px] font-bold text-[var(--color-navy)]">Generate Test Assets</h3>
                        <p className="text-[12px] text-[var(--color-text-muted)] mt-0.5">
                          Select the test assets to generate for identified risks and coverage gaps.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleGenerateTests}
                        disabled={testGenLoading || !analysisResult?.report_id || (!selectedTestAssets.cases && !selectedTestAssets.playwright && !selectedTestAssets.testData)}
                        title={getTestGenHoverText()}
                        className={`px-5 py-3 rounded-xl text-[13px] font-bold transition-all ${testGenLoading || !analysisResult?.report_id || (!selectedTestAssets.cases && !selectedTestAssets.playwright && !selectedTestAssets.testData) ? 'bg-[var(--color-border)] text-[var(--color-text-muted)] cursor-not-allowed' : 'bg-[var(--color-navy)] text-white hover:bg-[#1a2352]'}`}
                      >
                        {getTestGenStatusText()}
                      </button>
                    </div>

                    {/* Asset Selection Checkboxes */}
                    <div className="mt-4 mb-2 p-3.5 bg-slate-50 border border-slate-200/90 rounded-xl flex flex-col gap-2.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-bold text-[var(--color-navy)] uppercase tracking-wider">
                          Select Assets to Generate:
                        </span>
                        <label className="flex items-center gap-1.5 text-[12px] font-bold text-[var(--color-accent)] cursor-pointer select-none hover:opacity-80 transition-opacity">
                          <input
                            type="checkbox"
                            checked={selectedTestAssets.cases && selectedTestAssets.playwright && selectedTestAssets.testData}
                            onChange={handleToggleAllTestGenOptions}
                            className="w-4 h-4 rounded border-slate-300 text-[var(--color-accent)] focus:ring-[var(--color-accent)] cursor-pointer"
                          />
                          <span>All (Select All)</span>
                        </label>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                        <label className={`flex items-center gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-all ${
                          selectedTestAssets.cases
                            ? 'bg-white border-[var(--color-accent)] shadow-xs ring-1 ring-[var(--color-accent)]/20'
                            : 'bg-slate-100/70 border-slate-200/80 text-[var(--color-text-muted)]'
                        }`}>
                          <input
                            type="checkbox"
                            checked={selectedTestAssets.cases}
                            onChange={() => handleToggleTestGenOption('cases')}
                            className="w-4 h-4 rounded border-slate-300 text-[var(--color-accent)] focus:ring-[var(--color-accent)] cursor-pointer"
                          />
                          <div>
                            <span className="text-[12.5px] font-bold text-[var(--color-navy)]">
                              Test Cases
                            </span>
                            <p className="text-[10.5px] text-[var(--color-text-muted)]">Structured test cases</p>
                          </div>
                        </label>

                        <label className={`flex items-center gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-all ${
                          selectedTestAssets.playwright
                            ? 'bg-white border-[var(--color-accent)] shadow-xs ring-1 ring-[var(--color-accent)]/20'
                            : 'bg-slate-100/70 border-slate-200/80 text-[var(--color-text-muted)]'
                        }`}>
                          <input
                            type="checkbox"
                            checked={selectedTestAssets.playwright}
                            onChange={() => handleToggleTestGenOption('playwright')}
                            className="w-4 h-4 rounded border-slate-300 text-[var(--color-accent)] focus:ring-[var(--color-accent)] cursor-pointer"
                          />
                          <div>
                            <span className="text-[12.5px] font-bold text-[var(--color-navy)]">
                              Playwright Scripts
                            </span>
                            <p className="text-[10.5px] text-[var(--color-text-muted)]">Automation specs</p>
                          </div>
                        </label>

                        <label className={`flex items-center gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-all ${
                          selectedTestAssets.testData
                            ? 'bg-white border-[var(--color-accent)] shadow-xs ring-1 ring-[var(--color-accent)]/20'
                            : 'bg-slate-100/70 border-slate-200/80 text-[var(--color-text-muted)]'
                        }`}>
                          <input
                            type="checkbox"
                            checked={selectedTestAssets.testData}
                            onChange={() => handleToggleTestGenOption('testData')}
                            className="w-4 h-4 rounded border-slate-300 text-[var(--color-accent)] focus:ring-[var(--color-accent)] cursor-pointer"
                          />
                          <div>
                            <span className="text-[12.5px] font-bold text-[var(--color-navy)]">
                              Test Data
                            </span>
                            <p className="text-[10.5px] text-[var(--color-text-muted)]">Synthetic QA datasets</p>
                          </div>
                        </label>
                      </div>
                    </div>

                    {testGenError && (
                      <div className="mt-3 text-[13px] font-semibold text-red-500 bg-red-50 p-3 rounded-lg border border-red-100">{testGenError}</div>
                    )}

                    {testAssets && (
                      <div className="mt-5 space-y-6">
                        {/* Test cases */}
                        {selectedTestAssets.cases && testAssets.test_cases?.length > 0 && (
                          <>
                            <div className="border border-[var(--color-border)] rounded-[16px] overflow-hidden">
                              <div className="bg-[var(--color-bg-alt)] px-6 py-4 border-b border-[var(--color-border)] flex justify-between items-center gap-3">
                                <span className="text-[12px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wide">Test Cases</span>
                                <div className="flex items-center gap-3">
                                  <span className="text-[12px] font-bold text-[var(--color-navy)]">{testAssets.test_cases.length} Written</span>
                                  <button
                                    type="button"
                                    onClick={() => downloadTextFile('test_cases.csv', testCasesToCsv(testAssets.test_cases), 'text/csv')}
                                    className="text-[12px] font-bold text-[var(--color-accent)] hover:underline"
                                  >
                                    Download CSV ↓
                                  </button>
                                </div>
                              </div>
                              <div className="divide-y divide-[var(--color-border)] bg-white">
                                {testAssets.test_cases.map((tc) => (
                                  <div key={tc.id} className="p-4">
                                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                                      <span className="text-[11px] font-mono font-bold text-[var(--color-accent)]">{tc.id}</span>
                                      <span className="text-[14px] font-bold text-[var(--color-navy)]">{tc.title}</span>
                                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${tc.priority === 'P1' ? 'bg-[#fee2e2] text-[#dc2626]' : tc.priority === 'P2' ? 'bg-[#fef3c7] text-[#b45309]' : 'bg-[var(--color-bg-alt)] text-[var(--color-text-secondary)]'}`}>{tc.priority}</span>
                                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[var(--color-accent-light)] text-[var(--color-accent)]">{tc.type}</span>
                                      <span className="text-[11px] font-mono text-[var(--color-text-muted)]">{tc.requirement_ref}</span>
                                    </div>
                                    <div className="text-[12px] text-[var(--color-text-secondary)] mb-2 italic">Addresses: {tc.addresses}</div>
                                    <ol className="list-decimal ml-5 space-y-0.5">
                                      {(tc.steps || []).map((s, i) => (
                                        <li key={i} className="text-[13px] text-[var(--color-text-secondary)]">{s}</li>
                                      ))}
                                    </ol>
                                    <div className="mt-2 text-[13px]"><span className="font-bold text-[var(--color-navy)]">Expected:</span> <span className="text-[var(--color-text-secondary)]">{tc.expected_result}</span></div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            <FeedbackWidget
                              value={feedback.test_cases}
                              onSubmit={(verdict, reason, comment) => handleFeedback('test_cases', verdict, reason, comment)}
                            />
                          </>
                        )}

                        {/* Playwright automation */}
                        {selectedTestAssets.playwright && (
                          <>
                            <div className="border border-[var(--color-border)] rounded-[16px] overflow-hidden">
                              <div className="bg-[var(--color-bg-alt)] px-6 py-4 border-b border-[var(--color-border)] flex justify-between items-center flex-wrap gap-2">
                                <span className="text-[12px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wide">Playwright Specs</span>
                                <span className={`text-[11px] font-bold px-2 py-1 rounded-full ${testAssets.locator_strategy === 'code-grounded' ? 'bg-[#dcfce7] text-[#16a34a]' : 'bg-[#fef3c7] text-[#b45309]'}`}>
                                  {testAssets.locator_strategy === 'code-grounded' ? 'Locators grounded in repo code' : 'Locators assumed — verify TODOs'}
                                </span>
                              </div>
                              <div className="divide-y divide-[var(--color-border)] bg-white">
                                {!testAssets.automation ? (
                                  <div className="p-6 text-center text-[13px] text-[var(--color-text-muted)] font-medium">
                                    Writing Playwright specs… {testGenElapsed}s
                                  </div>
                                ) : (
                                  <>
                                    {testAssets.automation.error && (
                                      <div className="p-4 text-[13px] font-semibold text-red-500">{testAssets.automation.error}</div>
                                    )}
                                    {testAssets.automation.files.map((f) => (
                                      <div key={f.filename} className="p-4">
                                        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                                          <div>
                                            <code className="text-[13px] font-bold text-[var(--color-navy)]">{f.filename}</code>
                                            <span className="ml-2 text-[11px] text-[var(--color-text-muted)]">covers {(f.covers || []).join(', ')}</span>
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() => downloadTextFile(f.filename, f.content)}
                                            className="text-[12px] font-bold text-[var(--color-accent)] hover:underline"
                                          >
                                            Download ↓
                                          </button>
                                        </div>
                                        <pre className="bg-[#0f172a] text-[#e2e8f0] text-[12px] leading-relaxed rounded-xl p-4 overflow-x-auto max-h-[360px] overflow-y-auto"><code>{f.content}</code></pre>
                                        {f.notes && <div className="mt-2 text-[12px] text-[var(--color-text-muted)]">{f.notes}</div>}
                                      </div>
                                    ))}
                                    {testAssets.automation.skipped_cases?.length > 0 && (
                                      <div className="p-4 text-[12px] text-[var(--color-text-muted)]">
                                        Not automatable via UI: {testAssets.automation.skipped_cases.map(s => `${s.id} (${s.reason})`).join('; ')}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>

                            {testAssets.automation && (
                              <FeedbackWidget
                                value={feedback.automation}
                                onSubmit={(verdict, reason, comment) => handleFeedback('automation', verdict, reason, comment)}
                              />
                            )}
                          </>
                        )}

                        {/* Test Data */}
                        {selectedTestAssets.testData && (
                          <>
                            <div className="border border-[var(--color-border)] rounded-[16px] overflow-hidden">
                              <div className="bg-[var(--color-bg-alt)] px-6 py-4 border-b border-[var(--color-border)] flex justify-between items-center flex-wrap gap-2">
                                <span className="text-[12px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wide">Test Data</span>
                                {testAssets.test_data && !testAssets.test_data.error && (
                                  <div className="flex items-center gap-3">
                                    <span className="text-[12px] font-bold text-[var(--color-navy)]">
                                      {testAssets.test_data.datasets?.length ?? 0} dataset{(testAssets.test_data.datasets?.length ?? 0) !== 1 ? 's' : ''}
                                    </span>
                                    {testAssets.test_data.files?.filter(f => f.format === 'csv').map(f => (
                                      <button
                                        key={f.filename}
                                        type="button"
                                        onClick={() => downloadTextFile(f.filename, f.content, 'text/csv')}
                                        className="text-[12px] font-bold text-[var(--color-accent)] hover:underline"
                                      >
                                        Download All CSV ↓
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>

                              <div className="divide-y divide-[var(--color-border)] bg-white">
                                {!testAssets.test_data ? (
                                  <div className="p-6 text-center text-[13px] text-[var(--color-text-muted)] font-medium">
                                    Generating test data… {testGenElapsed}s
                                  </div>
                                ) : testAssets.test_data.error ? (
                                  <div className="p-4 text-[13px] font-semibold text-red-500">{testAssets.test_data.error}</div>
                                ) : testAssets.test_data.datasets?.length === 0 ? (
                                  <div className="p-6 text-center text-[13px] text-[var(--color-text-muted)] font-medium">No datasets generated.</div>
                                ) : (
                                  <>
                                    {testAssets.test_data.datasets.map((ds, idx) => (
                                      <details key={`${ds.test_case_id}-${ds.field}-${idx}`} className="group p-4">
                                        <summary className="flex items-center gap-2 cursor-pointer list-none">
                                          <span className="text-[11px] font-mono font-bold text-[var(--color-accent)]">{ds.test_case_id}</span>
                                          <span className="text-[13px] font-bold text-[var(--color-navy)] flex-1">{ds.field}</span>
                                          <div className="flex gap-1 flex-wrap">
                                            {ds.positive?.length > 0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#dcfce7] text-[#16a34a]">{ds.positive.length} positive</span>}
                                            {ds.negative?.length > 0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#fee2e2] text-[#dc2626]">{ds.negative.length} negative</span>}
                                            {ds.boundary?.length > 0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#fef3c7] text-[#b45309]">{ds.boundary.length} boundary</span>}
                                            {ds.security?.length > 0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#f3e8ff] text-[#7c3aed]">{ds.security.length} security</span>}
                                          </div>
                                          <span className="text-[11px] text-[var(--color-text-muted)] group-open:rotate-90 transition-transform">›</span>
                                        </summary>
                                        <div className="mt-3 space-y-2 pl-2">
                                          {[['positive', '#dcfce7', '#16a34a'], ['negative', '#fee2e2', '#dc2626'], ['boundary', '#fef3c7', '#b45309'], ['security', '#f3e8ff', '#7c3aed']].map(([cat, bg, fg]) =>
                                            ds[cat]?.length > 0 ? (
                                              <div key={cat}>
                                                <span className="text-[10px] font-bold uppercase tracking-wide" style={{color: fg}}>{cat}</span>
                                                <div className="mt-1 flex flex-wrap gap-1.5">
                                                  {ds[cat].map((val, vi) => (
                                                    <code key={vi} className="text-[11px] font-mono px-2 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-800">
                                                      {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                                                    </code>
                                                  ))}
                                                </div>
                                              </div>
                                            ) : null
                                          )}
                                        </div>
                                      </details>
                                    ))}

                                    {testAssets.test_data.files?.filter(f => f.format === 'json').length > 0 && (
                                      <div className="px-4 py-3 border-t border-[var(--color-border)] flex flex-wrap gap-2">
                                        {testAssets.test_data.files.filter(f => f.format === 'json').map(f => (
                                          <button
                                            key={f.filename}
                                            type="button"
                                            onClick={() => downloadTextFile(f.filename, f.content, 'application/json')}
                                            className="text-[11px] font-bold px-3 py-1.5 rounded-lg border border-[var(--color-border)] text-[var(--color-accent)] hover:bg-[var(--color-bg-alt)] transition-colors"
                                          >
                                            {f.filename} ↓
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>

                            {testAssets.test_data && !testAssets.test_data.error && (
                              <FeedbackWidget
                                value={feedback.test_data}
                                onSubmit={(verdict, reason, comment) => handleFeedback('test_data', verdict, reason, comment)}
                              />
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </section>
            )}

            {/* Workspace Controls (Dashboard-only — GitHub connection lives in Integrations now) */}
            <div className="mt-4 dashboard-reveal reveal">
              <section className="bg-white rounded-[16px] p-6 shadow-[var(--color-card-shadow)]">
                <h3 className="text-[12px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wide mb-4">Workspace Controls</h3>
                <button
                  onClick={loadWorkspaceData}
                  className="w-full flex items-center justify-between p-4 rounded-xl border border-[var(--color-border)] hover:border-[var(--color-accent)] hover:bg-[var(--color-bg-alt)] transition-all duration-200"
                >
                  <span className="font-bold text-[14px] text-[var(--color-accent)]">🔄 Sync Sandbox Database</span>
                  <span className="text-[12px] font-medium text-[var(--color-text-muted)]">Refresh</span>
                </button>
              </section>
            </div>
          </div>
          )}

          {/* HISTORICAL REPORTS VIEW */}
          {activeView === 'reports' && (
            <section className="bg-white rounded-[16px] p-6 shadow-[var(--color-card-shadow)] dashboard-reveal reveal">
              <div className="flex justify-between items-center pb-4 border-b border-[var(--color-border)] mb-4">
                <h3 className="text-[12px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wide">Past Assessments</h3>
                <span className="text-[11px] font-bold bg-[var(--color-bg-alt)] text-[var(--color-navy)] px-2 py-1 rounded-full">
                  {pastReports.length} Runs
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {pastReports.length > 0 ? (
                  pastReports.map((report) => {
                    const isSelected = selectedReportId === report.id;
                    const dateStr = new Date(report.created_at).toLocaleString();
                    const riskLevel = (report.risk_level || 'low').toLowerCase();
                    let riskStyle = "bg-[#dcfce7] text-[#16a34a]";
                    if (riskLevel === 'high') riskStyle = "bg-[#fee2e2] text-[#dc2626]";
                    if (riskLevel === 'medium' || riskLevel === 'amber') riskStyle = "bg-[#ffedd5] text-[#ea580c]";

                    return (
                      <div
                        key={report.id}
                        onClick={() => handleSelectReport(report)}
                        className={`p-4 rounded-xl border cursor-pointer transition-all duration-200 hover:-translate-y-[2px] ${isSelected ? "border-[var(--color-accent)] bg-[var(--color-bg-alt)] shadow-sm" : "border-[var(--color-border)] bg-white hover:border-[var(--color-accent)]"}`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full ${riskStyle}`}>
                            {report.risk_level}
                          </span>
                          <span className="text-[11px] text-[var(--color-text-muted)] font-medium">{dateStr}</span>
                        </div>
                        <p className="text-[13px] text-[var(--color-navy)] font-bold line-clamp-1">
                          {report.question.replace('What is at risk in this release? Identify high-risk requirements, coverage gaps, and recurring bug patterns. Specific User Instruction: ', '') || "Default Analysis Query"}
                        </p>
                        <p className="text-[12px] text-[var(--color-text-secondary)] font-medium mt-1 line-clamp-2 leading-relaxed">
                          {report.summary}
                        </p>
                      </div>
                    );
                  })
                ) : (
                  <div className="col-span-2 text-center py-10 text-[13px] text-[var(--color-text-muted)] font-medium">
                    No historical reports run yet — run your first analysis from the Dashboard tab above.
                  </div>
                )}
              </div>
            </section>
          )}

          {/* INTEGRATIONS VIEW */}
          {activeView === 'integrations' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 dashboard-reveal reveal">
              <section className="bg-white rounded-[16px] p-6 shadow-[var(--color-card-shadow)]">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[20px]">🐙</span>
                  <h3 className="text-[15px] font-bold text-[var(--color-navy)]">GitHub</h3>
                </div>
                <p className="text-[12px] text-[var(--color-text-muted)] mb-4">
                  Link a repository so analysis can search its code live for files impacted by your requirements — nothing is cloned or stored.
                </p>
                {githubStatus?.connected ? (
                  <div className="flex items-center justify-between p-3 rounded-xl border border-[var(--color-border)]">
                    <div className="flex items-center space-x-3 min-w-0">
                      {githubStatus.avatar_url && (
                        <img src={githubStatus.avatar_url} alt={githubStatus.username} className="w-8 h-8 rounded-full flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <div className="text-[13px] font-bold text-[var(--color-navy)] truncate">{githubStatus.username}</div>
                        <div className="text-[11px] text-[var(--color-text-muted)] truncate">{githubStatus.repo_full_name}</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleGithubDisconnect}
                      className="text-[12px] font-bold text-red-500 hover:text-red-600 flex-shrink-0 ml-2"
                    >
                      Disconnect
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleGithubConnect} className="space-y-2">
                    <label className="flex items-center text-[11px] font-bold text-[var(--color-text-muted)] uppercase tracking-wide">
                      Personal Access Token
                      <InfoTooltip text="Generate one at github.com/settings/tokens with the 'repo' scope. It's encrypted before storage." />
                    </label>
                    <input
                      type="password"
                      value={githubTokenInput}
                      onChange={(e) => setGithubTokenInput(e.target.value)}
                      placeholder="ghp_..."
                      className="w-full px-3 py-2 text-[13px] rounded-lg border border-[var(--color-border)] bg-white focus:ring-2 focus:ring-[var(--color-accent-light)] focus:border-[var(--color-accent)] transition-all outline-none"
                    />
                    <div className="pt-2">
                      <GitHubOrgRepoDropdown 
                        githubToken={githubTokenInput}
                        onSelect={(repo) => setGithubRepoInput(repo ? repo.full_name : '')} 
                      />
                    </div>
                    {githubError && (
                      <div className="text-[11px] font-semibold text-red-500 bg-red-50 p-2 rounded border border-red-100">
                        {githubError}
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={githubConnecting}
                      className="w-full py-2 rounded-lg text-[13px] font-bold text-white bg-[var(--color-navy)] hover:bg-[#1a2352] transition-colors"
                    >
                      {githubConnecting ? 'Connecting...' : 'Connect GitHub'}
                    </button>
                  </form>
                )}
              </section>

              <section className="bg-white rounded-[16px] p-6 shadow-[var(--color-card-shadow)]">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[20px]">📋</span>
                  <h3 className="text-[15px] font-bold text-[var(--color-navy)]">JIRA</h3>
                </div>
                <p className="text-[12px] text-[var(--color-text-muted)] mb-4">
                  Connect a Jira Cloud site so you can pull a story straight into the Dashboard as a requirement, instead of uploading a document.
                </p>
                {jiraStatus?.connected ? (
                  <div className="flex items-center justify-between p-3 rounded-xl border border-[var(--color-border)]">
                    <div className="min-w-0">
                      <div className="text-[13px] font-bold text-[var(--color-navy)] truncate">{jiraStatus.display_name || jiraStatus.account_email}</div>
                      <div className="text-[11px] text-[var(--color-text-muted)] truncate">{jiraStatus.base_url}</div>
                    </div>
                    <button
                      type="button"
                      onClick={handleJiraDisconnect}
                      className="text-[12px] font-bold text-red-500 hover:text-red-600 flex-shrink-0 ml-2"
                    >
                      Disconnect
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleJiraConnect} className="space-y-2">
                    <label className="block text-[11px] font-bold text-[var(--color-text-muted)] uppercase tracking-wide">Jira Site URL</label>
                    <input
                      type="text"
                      value={jiraUrlInput}
                      onChange={(e) => setJiraUrlInput(e.target.value)}
                      placeholder="https://your-site.atlassian.net"
                      className="w-full px-3 py-2 text-[13px] rounded-lg border border-[var(--color-border)] bg-white focus:ring-2 focus:ring-[var(--color-accent-light)] focus:border-[var(--color-accent)] transition-all outline-none"
                    />
                    <label className="block text-[11px] font-bold text-[var(--color-text-muted)] uppercase tracking-wide pt-1">Account Email</label>
                    <input
                      type="email"
                      value={jiraEmailInput}
                      onChange={(e) => setJiraEmailInput(e.target.value)}
                      placeholder="you@company.com"
                      className="w-full px-3 py-2 text-[13px] rounded-lg border border-[var(--color-border)] bg-white focus:ring-2 focus:ring-[var(--color-accent-light)] focus:border-[var(--color-accent)] transition-all outline-none"
                    />
                    <label className="flex items-center text-[11px] font-bold text-[var(--color-text-muted)] uppercase tracking-wide pt-1">
                      API Token
                      <InfoTooltip text="Create one at id.atlassian.com/manage-profile/security/api-tokens. It's encrypted before storage." />
                    </label>
                    <input
                      type="password"
                      value={jiraTokenInput}
                      onChange={(e) => setJiraTokenInput(e.target.value)}
                      placeholder="Jira API token"
                      className="w-full px-3 py-2 text-[13px] rounded-lg border border-[var(--color-border)] bg-white focus:ring-2 focus:ring-[var(--color-accent-light)] focus:border-[var(--color-accent)] transition-all outline-none"
                    />
                    {jiraError && (
                      <div className="text-[11px] font-semibold text-red-500 bg-red-50 p-2 rounded border border-red-100">
                        {jiraError}
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={jiraConnecting}
                      className="w-full py-2 rounded-lg text-[13px] font-bold text-white bg-[var(--color-navy)] hover:bg-[#1a2352] transition-colors disabled:opacity-60"
                    >
                      {jiraConnecting ? 'Connecting...' : 'Connect JIRA'}
                    </button>
                  </form>
                )}
              </section>
            </div>
          )}

          {(['admin', 'uam', 'feedback', 'observability'].includes(activeView) && user?.is_admin) ||
           (activeView === 'feedback' && user?.can_access_feedback) ||
           (activeView === 'observability' && user?.can_access_observability) ? (
            <div className="dashboard-reveal reveal space-y-6">
              {adminError && (
                <div className="p-3.5 rounded-xl bg-red-50 border border-red-200 text-[13px] text-red-600 font-semibold flex items-center justify-between">
                  <span>⚠️ {adminError}</span>
                  <button type="button" onClick={() => setAdminError(null)} className="text-red-400 hover:text-red-600 font-bold text-xs">✕</button>
                </div>
              )}



              {/* ═══════════════════ 1️⃣ PAGE 1 / SUB-MENU 1: USER ACCESS MANAGEMENT (UAM) ═══════════════════ */}
              {(activeView === 'uam' || (activeView === 'admin' && adminSubTab === 'uam')) && (
                <div className="space-y-6">
                  {/* UAM Stats Overview Bar */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="bg-white p-4 rounded-xl border border-[var(--color-border)] shadow-xs">
                      <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Total Registered</div>
                      <div className="text-2xl font-extrabold text-slate-900 mt-1">{adminUsers.length}</div>
                      <div className="text-[10px] text-slate-400 mt-0.5">all platform accounts</div>
                    </div>
                    <div className="bg-white p-4 rounded-xl border border-emerald-100 bg-emerald-50/30 shadow-xs">
                      <div className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider">Active Users</div>
                      <div className="text-2xl font-extrabold text-emerald-600 mt-1">
                        {adminUsers.filter((u) => u.status === 'active').length}
                      </div>
                      <div className="text-[10px] text-emerald-600/80 mt-0.5">approved & active</div>
                    </div>
                    <div className="bg-white p-4 rounded-xl border border-amber-100 bg-amber-50/30 shadow-xs">
                      <div className="text-[11px] font-bold text-amber-700 uppercase tracking-wider">Pending Access</div>
                      <div className="text-2xl font-extrabold text-amber-600 mt-1">
                        {adminUsers.filter((u) => u.status === 'pending').length}
                      </div>
                      <div className="text-[10px] text-amber-600/80 mt-0.5">awaiting admin approval</div>
                    </div>
                    <div className="bg-white p-4 rounded-xl border border-slate-200 bg-slate-50/50 shadow-xs">
                      <div className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">Disabled / Deleted</div>
                      <div className="text-2xl font-extrabold text-slate-700 mt-1">
                        {adminUsers.filter((u) => u.status === 'disabled' || u.status === 'deleted').length}
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5">restricted access</div>
                    </div>
                  </div>

                  {/* Pending Access Requests Alert Section */}
                  {adminUsers.some((u) => u.status === 'pending') && (
                    <section className="bg-white rounded-[16px] p-6 shadow-[var(--color-card-shadow)] border-l-4 border-amber-500">
                      <h3 className="font-['Sora'] text-[16px] font-bold text-[var(--color-navy)] mb-4 flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <span>⏳ Pending Access Requests</span>
                          <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                            {adminUsers.filter((u) => u.status === 'pending').length} Action Required
                          </span>
                        </span>
                      </h3>
                      <div className="space-y-3">
                        {adminUsers.filter((u) => u.status === 'pending').map((u) => (
                          <div key={u.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-xl border border-amber-200/80 bg-amber-50/30">
                            <div className="min-w-0">
                              <div className="text-[14px] font-bold text-[var(--color-navy)]">{u.name || '—'} <span className="font-normal text-slate-600">({u.email})</span></div>
                              <div className="text-[12px] text-slate-500 mt-0.5">
                                {u.designation || '—'} · {u.organization} · requested {u.requested_at ? new Date(u.requested_at).toLocaleDateString() : '—'}
                              </div>
                            </div>
                            <div className="flex-shrink-0 flex items-center gap-2">
                              <button
                                type="button"
                                disabled={adminActionId === u.id}
                                onClick={() => handleAdminAction(u, 'approve')}
                                className="px-4 py-2 rounded-lg text-[13px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 transition-colors disabled:opacity-50 shadow-xs cursor-pointer"
                              >
                                {adminActionId === u.id ? 'Working…' : '✓ Approve Access'}
                              </button>
                              <button
                                type="button"
                                disabled={adminActionId === u.id}
                                onClick={() => handleAdminAction(u, 'reject')}
                                className="px-4 py-2 rounded-lg text-[13px] font-bold text-red-600 border border-red-200 bg-white hover:bg-red-50 transition-colors disabled:opacity-50 cursor-pointer"
                              >
                                Reject
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  {/* Main Users Table Section */}
                  <section className="bg-white rounded-[16px] p-6 shadow-[var(--color-card-shadow)] border border-[var(--color-border)] space-y-4">
                    
                    {/* Unsaved Permission Changes Save Bar */}
                    {Object.keys(pendingPermissions).length > 0 && (
                      <div className="p-4 rounded-xl bg-indigo-50 border border-indigo-200 text-indigo-950 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-md animate-in fade-in slide-in-from-top-2 duration-200">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-indigo-600 text-white flex items-center justify-center font-bold text-base shadow-xs">
                            💾
                          </div>
                          <div>
                            <div className="text-xs font-extrabold text-indigo-950 flex items-center gap-2">
                              <span>Unsaved Permission Changes</span>
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-indigo-200 text-indigo-800">
                                {Object.keys(pendingPermissions).length} user{Object.keys(pendingPermissions).length === 1 ? '' : 's'} modified
                              </span>
                            </div>
                            <div className="text-[11px] text-indigo-700 mt-0.5">
                              Review your permission toggles below and click <strong>Save Permission Changes</strong> to apply.
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2.5 self-end sm:self-auto">
                          <button
                            type="button"
                            onClick={handleDiscardPermissions}
                            className="px-3.5 py-2 rounded-xl text-xs font-bold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 hover:text-slate-900 transition-all cursor-pointer shadow-2xs"
                          >
                            Cancel / Reset
                          </button>
                          <button
                            type="button"
                            disabled={savingPermissions}
                            onClick={handleSavePermissions}
                            className="px-4 py-2 rounded-xl text-xs font-extrabold text-white bg-indigo-600 hover:bg-indigo-700 transition-all shadow-md flex items-center gap-2 cursor-pointer disabled:opacity-50 scale-[1.02]"
                          >
                            {savingPermissions ? (
                              <span>Saving…</span>
                            ) : (
                              <>
                                <span>💾</span>
                                <span>Save Permission Changes</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
                      <div>
                        <h3 className="font-['Sora'] text-[18px] font-bold text-[var(--color-navy)]">
                          User Access & Permissions Directory
                        </h3>
                        <p className="text-[12px] text-slate-500 mt-0.5">Manage user status, account permissions, and API usage.</p>
                      </div>

                      <div className="flex items-center gap-3 flex-wrap">
                        {/* Search Input */}
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="Search name, email, org…"
                            value={uamSearchQuery}
                            onChange={(e) => setUamSearchQuery(e.target.value)}
                            className="px-3.5 py-1.5 pl-8 rounded-xl text-xs border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:border-[var(--color-accent)] w-56 transition-all"
                          />
                          <span className="absolute left-2.5 top-1.5 text-xs text-slate-400">🔍</span>
                        </div>

                        {/* Toggle Show Deleted */}
                        <label className="flex items-center gap-1.5 text-[12px] font-bold text-slate-600 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={adminShowDeleted}
                            onChange={(e) => setAdminShowDeleted(e.target.checked)}
                            className="accent-[var(--color-accent)] cursor-pointer"
                          />
                          Show deleted
                        </label>

                        {/* Add User Modal Button */}
                        <button
                          type="button"
                          onClick={() => setShowAddUserModal(true)}
                          className="px-3.5 py-1.5 rounded-xl text-[12px] font-bold text-white bg-[var(--color-accent)] hover:bg-indigo-700 transition-colors shadow-xs flex items-center gap-1.5 cursor-pointer"
                        >
                          <span>➕ Add New User</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => loadAdminUsers(adminShowDeleted)}
                          className="px-3 py-1.5 rounded-xl text-[12px] font-bold text-slate-700 border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer"
                        >
                          🔄 Refresh
                        </button>
                      </div>
                    </div>

                    {adminLoading ? (
                      <div className="py-12 flex justify-center">
                        <div className="animate-spin h-6 w-6 border-2 border-[var(--color-accent)] border-t-transparent rounded-full" />
                      </div>
                    ) : (
                      <div className="overflow-x-auto border border-slate-200 rounded-xl">
                        <table className="w-full min-w-[1100px] text-left text-[13px]">
                          <thead>
                            <tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-500">
                              <th className="py-3 px-4 font-bold">User Name & Email</th>
                              <th className="py-3 px-4 font-bold">Organization</th>
                              <th className="py-3 px-4 font-bold">Designation</th>
                              <th className="py-3 px-4 font-bold">Status</th>
                              <th className="py-3 px-4 font-bold">Page Access Permissions</th>
                              <th className="py-3 px-4 font-bold">Joined</th>
                              <th className="py-3 px-4 font-bold text-right">Tokens</th>
                              <th className="py-3 px-4 font-bold text-right">Account Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {adminUsers
                              .filter((u) => {
                                if (!uamSearchQuery) return true;
                                const q = uamSearchQuery.toLowerCase();
                                return (
                                  (u.name && u.name.toLowerCase().includes(q)) ||
                                  (u.email && u.email.toLowerCase().includes(q)) ||
                                  (u.organization && u.organization.toLowerCase().includes(q))
                                );
                              })
                              .map((u) => {
                                const isModified = pendingPermissions[u.id] !== undefined;
                                const isFeedbackActive = pendingPermissions[u.id]?.can_access_feedback ?? (u.can_access_feedback !== false);
                                const isObservabilityActive = pendingPermissions[u.id]?.can_access_observability ?? (u.can_access_observability !== false);

                                return (
                                  <tr key={u.id} className={`transition-colors ${isModified ? 'bg-indigo-50/40 hover:bg-indigo-50/60' : 'hover:bg-slate-50/60'}`}>
                                    <td className="py-3 px-4">
                                      <div className="font-bold text-[var(--color-navy)] flex items-center gap-1.5">
                                        <span>{u.name || '—'}</span>
                                        {u.is_admin && (
                                          <span className="text-[10px] font-extrabold px-2 py-0.5 rounded bg-indigo-100 text-indigo-700 uppercase">
                                            ADMIN
                                          </span>
                                        )}
                                        {isModified && (
                                          <span className="text-[9px] font-extrabold px-1.5 py-0.2 rounded bg-amber-100 text-amber-800 border border-amber-300">
                                            Unsaved
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-[12px] text-slate-500">{u.email}</div>
                                    </td>
                                    <td className="py-3 px-4 text-slate-700 font-medium">{u.organization || '—'}</td>
                                    <td className="py-3 px-4 text-slate-600">{u.designation || '—'}</td>
                                    <td className="py-3 px-4">
                                      <span className={`text-[11px] font-extrabold px-2.5 py-1 rounded-full inline-flex items-center gap-1 ${
                                        u.status === 'active' ? 'bg-emerald-100 text-emerald-700'
                                        : u.status === 'pending' ? 'bg-amber-100 text-amber-700'
                                        : u.status === 'deleted' ? 'bg-slate-200 text-slate-600'
                                        : 'bg-rose-100 text-rose-700'
                                      }`}>
                                        <span className={`w-1.5 h-1.5 rounded-full ${
                                          u.status === 'active' ? 'bg-emerald-500'
                                          : u.status === 'pending' ? 'bg-amber-500'
                                          : 'bg-slate-400'
                                        }`} />
                                        {u.status.toUpperCase()}
                                      </span>
                                    </td>
                                    {/* Page Access Permissions Controls */}
                                    <td className="py-3 px-4">
                                      {u.id === user?.id ? (
                                        <span className="text-[10px] font-extrabold px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700 inline-flex items-center gap-1">
                                          ⚙️ Primary Admin (You)
                                        </span>
                                      ) : (
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                          <button
                                            type="button"
                                            disabled={adminActionId === u.id}
                                            onClick={() => handleTogglePermission(u, 'feedback')}
                                            title={isFeedbackActive ? 'Click to toggle Feedback access OFF' : 'Click to toggle Feedback access ON'}
                                            className={`text-[10px] font-extrabold px-2.5 py-1 rounded-lg border transition-all flex items-center gap-1 cursor-pointer ${
                                              isFeedbackActive
                                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-200'
                                                : 'bg-slate-100 text-slate-400 border-slate-200 hover:bg-emerald-50 hover:text-emerald-700'
                                            }`}
                                          >
                                            <span>💬 Feedback</span>
                                            <span className="text-[9px] font-extrabold">{isFeedbackActive ? '✓' : '✕'}</span>
                                          </button>

                                          <button
                                            type="button"
                                            disabled={adminActionId === u.id}
                                            onClick={() => handleTogglePermission(u, 'observability')}
                                            title={isObservabilityActive ? 'Click to toggle Observability access OFF' : 'Click to toggle Observability access ON'}
                                            className={`text-[10px] font-extrabold px-2.5 py-1 rounded-lg border transition-all flex items-center gap-1 cursor-pointer ${
                                              isObservabilityActive
                                                ? 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-200'
                                                : 'bg-slate-100 text-slate-400 border-slate-200 hover:bg-indigo-50 hover:text-indigo-700'
                                            }`}
                                          >
                                            <span>🛡️ Observability</span>
                                            <span className="text-[9px] font-extrabold">{isObservabilityActive ? '✓' : '✕'}</span>
                                          </button>
                                        </div>
                                      )}
                                    </td>
                                  <td className="py-3 px-4 text-slate-500 whitespace-nowrap">{u.requested_at ? new Date(u.requested_at).toLocaleDateString() : '—'}</td>
                                  <td className="py-3 px-4 text-right font-mono text-slate-700 font-semibold">{(u.tokens_consumed || 0).toLocaleString()}</td>
                                  <td className="py-3 px-4 text-right whitespace-nowrap">
                                    {!u.is_admin ? (
                                      <div className="flex items-center justify-end gap-2.5">
                                        {u.status === 'pending' && (
                                          <>
                                            <button type="button" disabled={adminActionId === u.id} onClick={() => handleAdminAction(u, 'approve')} className="text-xs font-bold text-emerald-600 hover:text-emerald-700 disabled:opacity-50">Approve</button>
                                            <button type="button" disabled={adminActionId === u.id} onClick={() => handleAdminAction(u, 'reject')} className="text-xs font-bold text-rose-600 hover:text-rose-700 disabled:opacity-50">Reject</button>
                                          </>
                                        )}
                                        {u.status === 'active' && (
                                          <button type="button" disabled={adminActionId === u.id} onClick={() => handleAdminAction(u, 'disable')} className="text-xs font-bold text-amber-600 hover:text-amber-700 disabled:opacity-50">Disable</button>
                                        )}
                                        {(u.status === 'disabled' || u.status === 'deleted') && (
                                          <button type="button" disabled={adminActionId === u.id} onClick={() => handleAdminAction(u, 'enable')} className="text-xs font-bold text-[var(--color-accent)] hover:underline disabled:opacity-50">
                                            {u.status === 'deleted' ? 'Restore' : 'Enable'}
                                          </button>
                                        )}
                                        {u.status !== 'deleted' && (
                                          <button type="button" disabled={adminActionId === u.id} onClick={() => handleAdminAction(u, 'delete')} className="text-xs font-bold text-slate-400 hover:text-rose-600 disabled:opacity-50">Delete</button>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-[11px] font-semibold text-slate-400 italic">Owner</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        {adminUsers.length === 0 && (
                          <div className="py-8 text-center text-xs text-slate-400">No matching users found in UAM directory.</div>
                        )}
                      </div>
                    )}
                  </section>
                </div>
              )}

              {/* ═══════════════════ 2️⃣ PAGE 2 / SUB-MENU 2: FEEDBACK & REVIEWS ═══════════════════ */}
              {(activeView === 'feedback' || (activeView === 'admin' && adminSubTab === 'feedback')) && (
                <div className="space-y-6">
                  <section className="bg-white rounded-[16px] p-6 shadow-[var(--color-card-shadow)] border border-[var(--color-border)]">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                      <div>
                        <h3 className="font-['Sora'] text-[18px] font-bold text-[var(--color-navy)] flex items-center gap-2">
                          <span>📊 Feedback Analytics & Output Reviews</span>
                        </h3>
                        <p className="text-[12px] text-slate-500 mt-0.5">
                          Real-time prompt satisfaction metrics, feature quality ratings, and detailed user reviews.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={loadAdminFeedbackStats}
                        disabled={adminFeedbackLoading}
                        className="self-start sm:self-auto px-4 py-2 rounded-xl text-xs font-bold text-[var(--color-navy)] border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors disabled:opacity-50 flex items-center gap-1.5 cursor-pointer"
                      >
                        <span>🔄</span>
                        <span>{adminFeedbackLoading ? 'Loading…' : 'Refresh Feedback'}</span>
                      </button>
                    </div>

                    {adminFeedbackLoading && !adminFeedbackStats ? (
                      <div className="py-12 flex justify-center">
                        <div className="animate-spin h-6 w-6 border-2 border-[var(--color-accent)] border-t-transparent rounded-full" />
                      </div>
                    ) : !adminFeedbackStats ? (
                      <div className="py-8 text-center text-xs text-slate-400">No feedback data recorded yet.</div>
                    ) : (
                      <div className="space-y-6">
                        {/* Top KPI Cards Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                          {/* Total Responses */}
                          <div className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 shadow-xs">
                            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">Total Feedback Responses</div>
                            <div className="text-2xl font-extrabold text-[var(--color-navy)]">{adminFeedbackStats.total_responses}</div>
                            <div className="text-[11px] text-slate-500 mt-1">across all reports & features</div>
                          </div>

                          {/* Overall Satisfaction Rate */}
                          <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50/50 shadow-xs">
                            <div className="text-[11px] font-bold text-emerald-800 uppercase tracking-wider mb-1">Satisfaction Rate</div>
                            <div className="text-2xl font-extrabold text-emerald-600 flex items-baseline gap-1">
                              {adminFeedbackStats.satisfaction_rate}%
                            </div>
                            <div className="w-full bg-emerald-200/60 rounded-full h-1.5 mt-2 overflow-hidden">
                              <div className="bg-emerald-500 h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(100, Math.max(0, adminFeedbackStats.satisfaction_rate))}%` }} />
                            </div>
                          </div>

                          {/* Thumbs Up (Positive) */}
                          <div className="p-4 rounded-xl border border-blue-200 bg-blue-50/50 shadow-xs">
                            <div className="text-[11px] font-bold text-blue-800 uppercase tracking-wider mb-1">Positive Outcomes (👍)</div>
                            <div className="text-2xl font-extrabold text-blue-600">{adminFeedbackStats.thumbs_up}</div>
                            <div className="text-[11px] text-blue-700/80 mt-1">
                              {adminFeedbackStats.total_responses > 0 ? Math.round((adminFeedbackStats.thumbs_up / adminFeedbackStats.total_responses) * 100) : 0}% of total responses
                            </div>
                          </div>

                          {/* Thumbs Down (Needs Improvement) */}
                          <div className="p-4 rounded-xl border border-rose-200 bg-rose-50/50 shadow-xs">
                            <div className="text-[11px] font-bold text-rose-800 uppercase tracking-wider mb-1">Needs Improvement (👎)</div>
                            <div className="text-2xl font-extrabold text-rose-600">{adminFeedbackStats.thumbs_down}</div>
                            <div className="text-[11px] text-rose-700/80 mt-1">
                              {adminFeedbackStats.total_responses > 0 ? Math.round((adminFeedbackStats.thumbs_down / adminFeedbackStats.total_responses) * 100) : 0}% of total responses
                            </div>
                          </div>
                        </div>

                        {/* Graphs Section */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {/* Graph 1: Outcome Breakdown by Feature */}
                          <div className="p-5 rounded-xl border border-slate-200 bg-white shadow-xs">
                            <h4 className="font-['Sora'] text-[14px] font-bold text-[var(--color-navy)] mb-4 flex items-center justify-between">
                              <span>Feedback Outcome by Feature</span>
                              <span className="text-[11px] font-normal text-slate-400">👍 Positive vs 👎 Negative</span>
                            </h4>

                            <div className="space-y-4">
                              {[
                                { key: 'risk_report', label: 'Risk Report Analysis' },
                                { key: 'test_cases', label: 'Generated Test Cases' },
                                { key: 'automation', label: 'Automation Code Scripts' }
                              ].map((feat) => {
                                const data = adminFeedbackStats.by_feature?.[feat.key] || { total: 0, up: 0, down: 0, satisfaction_rate: 0 };
                                const upPercent = data.total > 0 ? (data.up / data.total) * 100 : 0;
                                const downPercent = data.total > 0 ? (data.down / data.total) * 100 : 0;

                                return (
                                  <div key={feat.key} className="space-y-1.5">
                                    <div className="flex justify-between items-center text-xs">
                                      <span className="font-bold text-[var(--color-navy)]">{feat.label}</span>
                                      <div className="flex items-center gap-2">
                                        <span className="font-semibold text-slate-500">{data.total} total</span>
                                        <span className="font-bold px-2 py-0.5 rounded-full text-[10px] bg-emerald-100 text-emerald-700">
                                          {data.satisfaction_rate}% Satisfaction
                                        </span>
                                      </div>
                                    </div>
                                    <div className="h-4 w-full bg-slate-100 rounded-lg overflow-hidden flex shadow-inner">
                                      {data.total === 0 ? (
                                        <div className="w-full h-full bg-slate-100 text-[10px] text-slate-400 flex items-center justify-center font-medium">No feedback yet</div>
                                      ) : (
                                        <>
                                          <div
                                            className="bg-emerald-500 h-full transition-all duration-500 flex items-center justify-center text-[9px] font-bold text-white"
                                            style={{ width: `${upPercent}%` }}
                                            title={`Positive: ${data.up} (${Math.round(upPercent)}%)`}
                                          >
                                            {upPercent > 15 && `${data.up} 👍`}
                                          </div>
                                          <div
                                            className="bg-rose-500 h-full transition-all duration-500 flex items-center justify-center text-[9px] font-bold text-white"
                                            style={{ width: `${downPercent}%` }}
                                            title={`Negative: ${data.down} (${Math.round(downPercent)}%)`}
                                          >
                                            {downPercent > 15 && `${data.down} 👎`}
                                          </div>
                                        </>
                                      )}
                                    </div>
                                    <div className="flex justify-between text-[10px] font-semibold text-slate-500">
                                      <span className="text-emerald-600">👍 {data.up} Helpful</span>
                                      <span className="text-rose-600">👎 {data.down} Issues</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Graph 2: Negative Feedback Reasons Distribution */}
                          <div className="p-5 rounded-xl border border-slate-200 bg-white shadow-xs">
                            <h4 className="font-['Sora'] text-[14px] font-bold text-[var(--color-navy)] mb-4 flex items-center justify-between">
                              <span>Negative Feedback Reasons</span>
                              <span className="text-[11px] font-normal text-slate-400">Why users gave thumbs down</span>
                            </h4>

                            {adminFeedbackStats.thumbs_down === 0 ? (
                              <div className="py-12 text-center text-xs text-slate-400 font-medium bg-slate-50/50 rounded-xl border border-dashed border-slate-200">
                                🎉 No negative feedback reported yet!
                              </div>
                            ) : (
                              <div className="space-y-3.5">
                                {Object.entries(adminFeedbackStats.by_reason || {}).map(([reason, count]) => {
                                  const totalDown = adminFeedbackStats.thumbs_down || 1;
                                  const pct = Math.round((count / totalDown) * 100);
                                  return (
                                    <div key={reason} className="space-y-1">
                                      <div className="flex justify-between items-center text-xs">
                                        <span className="font-semibold text-slate-700">{reason}</span>
                                        <span className="font-bold text-slate-800">{count} <span className="font-normal text-slate-400">({pct}%)</span></span>
                                      </div>
                                      <div className="h-2.5 w-full bg-slate-100 rounded-full overflow-hidden">
                                        <div
                                          className="h-full bg-rose-400 rounded-full transition-all duration-500"
                                          style={{ width: `${pct}%` }}
                                        />
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Recent Responses Audit Table */}
                        <div className="pt-2">
                          <h4 className="font-['Sora'] text-[14px] font-bold text-[var(--color-navy)] mb-3">User Feedback Reviews Audit Log</h4>
                          <div className="overflow-x-auto border border-slate-200 rounded-xl">
                            <table className="w-full min-w-[800px] text-left text-xs">
                              <thead>
                                <tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-500">
                                  <th className="py-2.5 px-4 font-bold">User</th>
                                  <th className="py-2.5 px-4 font-bold">Feature</th>
                                  <th className="py-2.5 px-4 font-bold">Outcome</th>
                                  <th className="py-2.5 px-4 font-bold">Reason & Comments</th>
                                  <th className="py-2.5 px-4 font-bold text-right">Submitted</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {adminFeedbackStats.recent_feedback?.length > 0 ? (
                                  adminFeedbackStats.recent_feedback.map((item) => (
                                    <tr key={item.id} className="hover:bg-slate-50/60 transition-colors">
                                      <td className="py-2.5 px-4">
                                        <div className="font-bold text-[var(--color-navy)]">{item.user_name || '—'}</div>
                                        <div className="text-[11px] text-slate-400">{item.user_email}</div>
                                      </td>
                                      <td className="py-2.5 px-4 font-medium text-slate-700 capitalize">
                                        {item.feature?.replace('_', ' ')}
                                      </td>
                                      <td className="py-2.5 px-4">
                                        {item.verdict === 'up' ? (
                                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-700">
                                            👍 Positive
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-rose-100 text-rose-700">
                                            👎 Negative
                                          </span>
                                        )}
                                      </td>
                                      <td className="py-2.5 px-4 text-slate-700 max-w-xs">
                                        {item.reason && (
                                          <span className="inline-block mr-1.5 px-2 py-0.5 rounded text-[10px] font-bold bg-slate-200 text-slate-700">
                                            {item.reason}
                                          </span>
                                        )}
                                        <span className="italic">{item.comment ? `"${item.comment}"` : item.reason ? '' : '—'}</span>
                                      </td>
                                      <td className="py-2.5 px-4 text-right whitespace-nowrap text-slate-500">
                                        {item.created_at ? new Date(item.created_at).toLocaleString() : '—'}
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan={5} className="py-6 text-center text-slate-400 italic">No feedback responses recorded yet.</td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </section>
                </div>
              )}

              {/* ═══════════════════ 3️⃣ PAGE 3 / SUB-MENU 3: AI OBSERVABILITY ═══════════════════ */}
              {(activeView === 'observability' || (activeView === 'admin' && adminSubTab === 'observability')) && (
                <div className="space-y-6">
                  <section className="bg-white rounded-[16px] p-6 shadow-[var(--color-card-shadow)] border border-[var(--color-border)]">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                      <div>
                        <h3 className="font-['Sora'] text-[18px] font-bold text-[var(--color-navy)] flex items-center gap-2">
                          <span>🛡️ AI Guardrails Observability Dashboard</span>
                        </h3>
                        <p className="text-[12px] text-slate-500 mt-0.5">
                          Telemetrics & guardrail analytics: grounding accuracy, wall-clock latency, USD token cost, prompt injection safety, and output drift.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={loadAdminGuardrailsStats}
                        disabled={adminGuardrailsLoading}
                        className="self-start sm:self-auto px-4 py-2 rounded-xl text-xs font-bold text-[var(--color-navy)] border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors disabled:opacity-50 flex items-center gap-1.5 cursor-pointer"
                      >
                        <span>🔄</span>
                        <span>{adminGuardrailsLoading ? 'Loading…' : 'Refresh Metrics'}</span>
                      </button>
                    </div>

                    {adminGuardrailsLoading && !adminGuardrailsStats ? (
                      <div className="py-12 flex justify-center">
                        <div className="animate-spin h-6 w-6 border-2 border-[var(--color-accent)] border-t-transparent rounded-full" />
                      </div>
                    ) : !adminGuardrailsStats ? (
                      <div className="py-8 text-center text-xs text-slate-400">No observability data recorded yet. Run an analysis to start collecting guardrail metrics.</div>
                    ) : (
                      <div className="space-y-6">
                        {/* ── KPI Cards Grid (6 cards) ── */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                          {/* Avg Grounding Score */}
                          {(() => {
                            const gs = adminGuardrailsStats.summary?.avg_grounding_score;
                            const gsPct = gs != null ? Math.round(gs * 100) : null;
                            return (
                              <div className="p-3 rounded-xl border border-emerald-200 bg-emerald-50/50 shadow-xs relative group">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-[10px] font-bold text-emerald-800 uppercase tracking-wider">Grounding</span>
                                  <div className="relative cursor-pointer group/tip">
                                    <span className="text-[11px] text-emerald-600 bg-emerald-100 hover:bg-emerald-200 w-4 h-4 rounded-full flex items-center justify-center font-bold">?</span>
                                    <div className="hidden group-hover/tip:block absolute left-1/2 -translate-x-1/2 top-6 w-64 p-3 bg-slate-900 text-white text-[11px] rounded-xl shadow-xl z-50 pointer-events-none space-y-1.5 leading-tight">
                                      <div className="font-bold text-emerald-400">Grounding Score</div>
                                      <div>Measures how accurately LLM claims are backed by retrieved RAG documents. Prevents hallucinations.</div>
                                      <div className="pt-1 border-t border-slate-700 text-[10px] space-y-0.5">
                                        <div className="text-emerald-300">🟢 Good: &ge; 85% (High factual alignment)</div>
                                        <div className="text-amber-300">🟡 Avg: 70% – 84% (Minor unverified claims)</div>
                                        <div className="text-rose-300">🔴 Bad: &lt; 70% (High risk of hallucination)</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="text-xl font-extrabold text-emerald-600">{gsPct != null ? `${gsPct}%` : '—'}</div>
                                <div className="text-[10px] text-slate-500 mt-0.5">avg source match</div>
                              </div>
                            );
                          })()}

                          {/* Avg Latency */}
                          {(() => {
                            const lat = adminGuardrailsStats.summary?.avg_latency_ms;
                            const latSec = lat != null ? (lat / 1000).toFixed(1) : null;
                            return (
                              <div className="p-3 rounded-xl border border-blue-200 bg-blue-50/50 shadow-xs relative group">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-[10px] font-bold text-blue-800 uppercase tracking-wider">Latency</span>
                                  <div className="relative cursor-pointer group/tip">
                                    <span className="text-[11px] text-blue-600 bg-blue-100 hover:bg-blue-200 w-4 h-4 rounded-full flex items-center justify-center font-bold">?</span>
                                    <div className="hidden group-hover/tip:block absolute left-1/2 -translate-x-1/2 top-6 w-64 p-3 bg-slate-900 text-white text-[11px] rounded-xl shadow-xl z-50 pointer-events-none space-y-1.5 leading-tight">
                                      <div className="font-bold text-blue-400">Response Latency</div>
                                      <div>Measures wall-clock execution time for generation and retrieval.</div>
                                      <div className="pt-1 border-t border-slate-700 text-[10px] space-y-0.5">
                                        <div className="text-emerald-300">🟢 Good: &lt; 5.0s (Fast response)</div>
                                        <div className="text-amber-300">🟡 Avg: 5.0s – 15.0s (Acceptable delay)</div>
                                        <div className="text-rose-300">🔴 Bad: &gt; 15.0s (Potential bottleneck/timeout)</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="text-xl font-extrabold text-blue-600">{latSec != null ? `${latSec}s` : '—'}</div>
                                <div className="text-[10px] text-slate-500 mt-0.5">avg per generation</div>
                              </div>
                            );
                          })()}

                          {/* Total Cost */}
                          <div className="p-3 rounded-xl border border-slate-200 bg-slate-50/50 shadow-xs relative group">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[10px] font-bold text-slate-700 uppercase tracking-wider">Total Cost</span>
                              <div className="relative cursor-pointer group/tip">
                                <span className="text-[11px] text-slate-600 bg-slate-200 hover:bg-slate-300 w-4 h-4 rounded-full flex items-center justify-center font-bold">?</span>
                                <div className="hidden group-hover/tip:block absolute left-1/2 -translate-x-1/2 top-6 w-64 p-3 bg-slate-900 text-white text-[11px] rounded-xl shadow-xl z-50 pointer-events-none space-y-1.5 leading-tight">
                                  <div className="font-bold text-slate-300">Token & Cost Breakdown</div>
                                  <div>Tracks total input/output LLM tokens and estimated USD expenditure across all requests.</div>
                                  <div className="pt-1 border-t border-slate-700 text-[10px] space-y-0.5">
                                    <div className="text-emerald-300">🟢 Good: &lt; $0.01 / report (Cost-optimized)</div>
                                    <div className="text-amber-300">🟡 Avg: $0.01 – $0.05 / report</div>
                                    <div className="text-rose-300">🔴 Bad: &gt; $0.05 / report (High token utilization)</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="text-xl font-extrabold text-slate-800">${(adminGuardrailsStats.summary?.total_estimated_cost_usd || 0).toFixed(2)}</div>
                            <div className="text-[10px] text-slate-500 mt-0.5">{(adminGuardrailsStats.summary?.total_tokens_consumed || 0).toLocaleString()} tokens</div>
                          </div>

                          {/* Prompt Injections Blocked */}
                          {(() => {
                            const inj = adminGuardrailsStats.summary?.prompt_injections_blocked || 0;
                            return (
                              <div className={`p-3 rounded-xl border ${inj > 0 ? 'border-rose-200 bg-rose-50/50 text-rose-600' : 'border-emerald-200 bg-emerald-50/50 text-emerald-600'} shadow-xs relative group`}>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-[10px] font-bold uppercase tracking-wider">Injections</span>
                                  <div className="relative cursor-pointer group/tip">
                                    <span className="text-[11px] text-slate-600 bg-white/80 hover:bg-white w-4 h-4 rounded-full flex items-center justify-center font-bold border border-slate-200">?</span>
                                    <div className="hidden group-hover/tip:block absolute left-1/2 -translate-x-1/2 top-6 w-64 p-3 bg-slate-900 text-white text-[11px] rounded-xl shadow-xl z-50 pointer-events-none space-y-1.5 leading-tight">
                                      <div className="font-bold text-rose-400">Prompt Injection Blocked</div>
                                      <div>Input guardrail that intercepts jailbreak and system-override attempts before reaching LLM.</div>
                                      <div className="pt-1 border-t border-slate-700 text-[10px] space-y-0.5">
                                        <div className="text-emerald-300">🟢 Good: High block rate & 0 undetected bypasses</div>
                                        <div className="text-amber-300">🟡 Avg: Occasional malicious queries detected</div>
                                        <div className="text-rose-300">🔴 Bad: Security breaches or missed injections</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="text-xl font-extrabold">{inj}</div>
                                <div className="text-[10px] text-slate-500 mt-0.5">blocked attempts</div>
                              </div>
                            );
                          })()}

                          {/* PII Detections */}
                          {(() => {
                            const pii = adminGuardrailsStats.summary?.pii_detections || 0;
                            return (
                              <div className={`p-3 rounded-xl border ${pii > 0 ? 'border-amber-200 bg-amber-50/50 text-amber-600' : 'border-emerald-200 bg-emerald-50/50 text-emerald-600'} shadow-xs relative group`}>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-[10px] font-bold uppercase tracking-wider">PII Flags</span>
                                  <div className="relative cursor-pointer group/tip">
                                    <span className="text-[11px] text-slate-600 bg-white/80 hover:bg-white w-4 h-4 rounded-full flex items-center justify-center font-bold border border-slate-200">?</span>
                                    <div className="hidden group-hover/tip:block absolute left-1/2 -translate-x-1/2 top-6 w-64 p-3 bg-slate-900 text-white text-[11px] rounded-xl shadow-xl z-50 pointer-events-none space-y-1.5 leading-tight">
                                      <div className="font-bold text-amber-400">PII Leakage Redaction</div>
                                      <div>Output scanner detecting emails, phone numbers, and SSNs in LLM generations.</div>
                                      <div className="pt-1 border-t border-slate-700 text-[10px] space-y-0.5">
                                        <div className="text-emerald-300">🟢 Good: 0 unredacted PII leaks</div>
                                        <div className="text-amber-300">🟡 Avg: PII auto-redacted successfully</div>
                                        <div className="text-rose-300">🔴 Bad: Raw sensitive PII exposed in output</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="text-xl font-extrabold">{pii}</div>
                                <div className="text-[10px] text-slate-500 mt-0.5">detected & redacted</div>
                              </div>
                            );
                          })()}

                          {/* JSON Parse Failures */}
                          {(() => {
                            const parseFail = adminGuardrailsStats.summary?.json_parse_failures || 0;
                            return (
                              <div className={`p-3 rounded-xl border ${parseFail > 0 ? 'border-rose-200 bg-rose-50/50 text-rose-600' : 'border-emerald-200 bg-emerald-50/50 text-emerald-600'} shadow-xs relative group`}>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-[10px] font-bold uppercase tracking-wider">Parse Errors</span>
                                  <div className="relative cursor-pointer group/tip">
                                    <span className="text-[11px] text-slate-600 bg-white/80 hover:bg-white w-4 h-4 rounded-full flex items-center justify-center font-bold border border-slate-200">?</span>
                                    <div className="hidden group-hover/tip:block absolute left-1/2 -translate-x-1/2 top-6 w-64 p-3 bg-slate-900 text-white text-[11px] rounded-xl shadow-xl z-50 pointer-events-none space-y-1.5 leading-tight">
                                      <div className="font-bold text-rose-400">Structured JSON Output Integrity</div>
                                      <div>Tracks whether LLM returned valid JSON matching the risk radar schema.</div>
                                      <div className="pt-1 border-t border-slate-700 text-[10px] space-y-0.5">
                                        <div className="text-emerald-300">🟢 Good: 0% parse failures (100% structured)</div>
                                        <div className="text-amber-300">🟡 Avg: &lt; 2% failures (Auto-repaired)</div>
                                        <div className="text-rose-300">🔴 Bad: &gt; 5% failures (Malformed LLM JSON)</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="text-xl font-extrabold">{parseFail}</div>
                                <div className="text-[10px] text-slate-500 mt-0.5">fallback triggered</div>
                              </div>
                            );
                          })()}
                        </div>

                        {/* ── Charts Grid (2×2) ── */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {/* Chart 1: Latency Trend */}
                          <div className="p-5 rounded-xl border border-slate-200 bg-white shadow-xs">
                            <h4 className="font-['Sora'] text-[14px] font-bold text-[var(--color-navy)] mb-4 flex items-center justify-between">
                              <span>Latency Trend (14 days)</span>
                              <span className="text-[11px] font-normal text-slate-400">avg / p95</span>
                            </h4>
                            {adminGuardrailsStats.latency_trend?.length > 0 ? (
                              <div className="space-y-2">
                                {adminGuardrailsStats.latency_trend.map((day) => {
                                  const maxMs = Math.max(...adminGuardrailsStats.latency_trend.map(d => d.p95_ms || 0), 1);
                                  const avgPct = Math.min(100, (day.avg_ms / maxMs) * 100);
                                  const p95Pct = Math.min(100, (day.p95_ms / maxMs) * 100);
                                  return (
                                    <div key={day.date} className="space-y-0.5">
                                      <div className="flex justify-between text-[11px]">
                                        <span className="font-medium text-slate-600">{day.date.slice(5)}</span>
                                        <span className="font-semibold text-slate-700">{(day.avg_ms / 1000).toFixed(1)}s <span className="text-slate-400">/ {(day.p95_ms / 1000).toFixed(1)}s</span></span>
                                      </div>
                                      <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden relative">
                                        <div className="absolute h-full bg-blue-200 rounded-full transition-all duration-500" style={{ width: `${p95Pct}%` }} />
                                        <div className="absolute h-full bg-blue-500 rounded-full transition-all duration-500" style={{ width: `${avgPct}%` }} />
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="py-8 text-center text-xs text-slate-400 font-medium">No latency data yet</div>
                            )}
                          </div>

                          {/* Chart 2: Risk Level Distribution */}
                          <div className="p-5 rounded-xl border border-slate-200 bg-white shadow-xs">
                            <h4 className="font-['Sora'] text-[14px] font-bold text-[var(--color-navy)] mb-4 flex items-center justify-between">
                              <span>Risk Distribution (Drift)</span>
                              <span className="text-[11px] font-normal text-slate-400">all-time</span>
                            </h4>
                            {(() => {
                              const rd = adminGuardrailsStats.risk_distribution || {};
                              const total = (rd.high || 0) + (rd.medium || 0) + (rd.low || 0);
                              if (total === 0) return <div className="py-8 text-center text-xs text-slate-400 font-medium">No data</div>;
                              return (
                                <div className="space-y-3">
                                  {[{ key: 'high', label: 'High Risk', color: 'rose' }, { key: 'medium', label: 'Medium Risk', color: 'amber' }, { key: 'low', label: 'Low Risk', color: 'emerald' }].map(({ key, label, color }) => {
                                    const count = rd[key] || 0;
                                    const pct = Math.round((count / total) * 100);
                                    return (
                                      <div key={key} className="space-y-1">
                                        <div className="flex justify-between text-xs">
                                          <span className="font-semibold text-slate-700">{label}</span>
                                          <span className="font-bold text-slate-800">{count} <span className="font-normal text-slate-400">({pct}%)</span></span>
                                        </div>
                                        <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden">
                                          <div className={`h-full bg-${color}-500 rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            })()}
                          </div>

                          {/* Chart 3: Cost by User */}
                          <div className="p-5 rounded-xl border border-slate-200 bg-white shadow-xs">
                            <h4 className="font-['Sora'] text-[14px] font-bold text-[var(--color-navy)] mb-4 flex items-center justify-between">
                              <span>Token Cost by User</span>
                              <span className="text-[11px] font-normal text-slate-400">top 10</span>
                            </h4>
                            {adminGuardrailsStats.cost_by_user?.length > 0 ? (
                              <div className="space-y-2">
                                {adminGuardrailsStats.cost_by_user.map((u) => {
                                  const maxTokens = Math.max(...adminGuardrailsStats.cost_by_user.map(x => x.tokens || 0), 1);
                                  const pct = Math.min(100, (u.tokens / maxTokens) * 100);
                                  return (
                                    <div key={u.email} className="space-y-0.5">
                                      <div className="flex justify-between text-[11px]">
                                        <span className="font-medium text-slate-600 truncate max-w-[60%]">{u.email}</span>
                                        <span className="font-semibold text-slate-700">{u.tokens.toLocaleString()} <span className="text-slate-400">(${u.cost_usd.toFixed(2)})</span></span>
                                      </div>
                                      <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                                        <div className="h-full bg-indigo-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="py-8 text-center text-xs text-slate-400 font-medium">No cost data yet</div>
                            )}
                          </div>

                          {/* Chart 4: Grounding ↔ Feedback Correlation */}
                          <div className="p-5 rounded-xl border border-slate-200 bg-white shadow-xs">
                            <h4 className="font-['Sora'] text-[14px] font-bold text-[var(--color-navy)] mb-4 flex items-center justify-between">
                              <span>Grounding ↔ Satisfaction</span>
                              <span className="text-[11px] font-normal text-slate-400">does quality predict 👍?</span>
                            </h4>
                            {adminGuardrailsStats.grounding_vs_feedback?.some(b => b.count > 0) ? (
                              <div className="space-y-3">
                                {adminGuardrailsStats.grounding_vs_feedback.map((bucket) => (
                                  <div key={bucket.grounding_bucket} className="space-y-1">
                                    <div className="flex justify-between text-xs">
                                      <span className="font-semibold text-slate-700">Score {bucket.grounding_bucket}</span>
                                      <span className="font-bold text-slate-800">{bucket.thumbs_up_pct}% 👍 <span className="font-normal text-slate-400">({bucket.count} reviews)</span></span>
                                    </div>
                                    <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden flex">
                                      <div className="bg-emerald-500 h-full transition-all duration-500" style={{ width: `${bucket.thumbs_up_pct}%` }} />
                                      <div className="bg-rose-400 h-full transition-all duration-500" style={{ width: `${100 - bucket.thumbs_up_pct}%` }} />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="py-8 text-center text-xs text-slate-400 font-medium">Need feedback + grounding data to correlate</div>
                            )}
                          </div>
                        </div>

                        {/* ── Recent Safety Events Table ── */}
                        <div className="pt-2">
                          <div className="flex items-center gap-2 mb-3">
                            <h4 className="font-['Sora'] text-[14px] font-bold text-[var(--color-navy)]">Recent Safety Events Audit Log</h4>
                            <div className="relative cursor-pointer group/tip">
                              <span className="text-[11px] text-slate-600 bg-slate-200 hover:bg-slate-300 w-4 h-4 rounded-full flex items-center justify-center font-bold">?</span>
                              <div className="hidden group-hover/tip:block absolute left-0 top-6 w-72 p-3 bg-slate-900 text-white text-[11px] rounded-xl shadow-xl z-50 pointer-events-none space-y-1.5 leading-tight">
                                <div className="font-bold text-sky-400">Security & Operational Audit Log</div>
                                <div>Real-time audit log recording intercepted prompt injections, PII detections, high latency warnings, and schema parse errors.</div>
                                <div className="pt-1 border-t border-slate-700 text-[10px] space-y-0.5">
                                  <div className="text-emerald-300">🟢 Good: Zero critical injection attempts or PII leaks</div>
                                  <div className="text-amber-300">🟡 Avg: All prompt injections blocked & PII auto-redacted</div>
                                  <div className="text-rose-300">🔴 Bad: High frequency of high-latency alerts (&gt; 30s) or bypasses</div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="overflow-x-auto border border-slate-200 rounded-xl">
                            <table className="w-full min-w-[800px] text-left text-xs">
                              <thead>
                                <tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-500">
                                  <th className="py-2.5 px-4 font-bold">Event Type</th>
                                  <th className="py-2.5 px-4 font-bold">User</th>
                                  <th className="py-2.5 px-4 font-bold">Event Detail</th>
                                  <th className="py-2.5 px-4 font-bold text-right">Timestamp</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {adminGuardrailsStats.recent_safety_events?.length > 0 ? (
                                  adminGuardrailsStats.recent_safety_events.map((evt, idx) => (
                                    <tr key={idx} className="hover:bg-slate-50/60 transition-colors">
                                      <td className="py-2.5 px-4">
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                          evt.type === 'prompt_injection' ? 'bg-rose-100 text-rose-700'
                                          : evt.type === 'pii_detected' ? 'bg-orange-100 text-orange-700'
                                          : evt.type === 'high_latency' ? 'bg-amber-100 text-amber-700'
                                          : 'bg-slate-100 text-slate-700'
                                        }`}>
                                          {evt.type === 'prompt_injection' ? '🚫 Injection'
                                           : evt.type === 'pii_detected' ? '👁️ PII'
                                           : evt.type === 'high_latency' ? '⏱️ Slow'
                                           : '⚠️ Parse Fail'}
                                        </span>
                                      </td>
                                      <td className="py-2.5 px-4 text-slate-700">{evt.user_email}</td>
                                      <td className="py-2.5 px-4 text-slate-600 max-w-xs truncate">{evt.detail || '—'}</td>
                                      <td className="py-2.5 px-4 text-right whitespace-nowrap text-slate-500">{evt.timestamp ? new Date(evt.timestamp).toLocaleString() : '—'}</td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan={4} className="py-6 text-center text-slate-400 italic">✅ No safety events — all clear!</td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </section>
                </div>
              )}

              {/* ═══════════════════ ➕ ADD NEW USER MODAL ═══════════════════ */}
              {showAddUserModal && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4 z-50">
                  <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl border border-slate-200 animate-in fade-in zoom-in duration-200">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-['Sora'] text-lg font-bold text-slate-900 flex items-center gap-2">
                        <span>➕ Add New User (UAM)</span>
                      </h3>
                      <button
                        type="button"
                        onClick={() => { setShowAddUserModal(false); setAddUserMsg(null); }}
                        className="text-slate-400 hover:text-slate-600 font-bold"
                      >
                        ✕
                      </button>
                    </div>

                    {addUserMsg && (
                      <div className={`mb-4 p-3 rounded-xl text-xs font-bold ${
                        addUserMsg.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'
                      }`}>
                        {addUserMsg.text}
                      </div>
                    )}

                    <form onSubmit={handleCreateUser} className="space-y-3.5">
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">Full Name</label>
                        <input
                          type="text"
                          required
                          placeholder="Jane Doe"
                          value={addUserForm.name}
                          onChange={(e) => setAddUserForm({ ...addUserForm, name: e.target.value })}
                          className="w-full px-3 py-2 rounded-xl text-xs border border-slate-200 focus:outline-none focus:border-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">Email Address</label>
                        <input
                          type="email"
                          required
                          placeholder="jane@company.com"
                          value={addUserForm.email}
                          onChange={(e) => setAddUserForm({ ...addUserForm, email: e.target.value })}
                          className="w-full px-3 py-2 rounded-xl text-xs border border-slate-200 focus:outline-none focus:border-indigo-500"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-bold text-slate-700 mb-1">Organization</label>
                          <input
                            type="text"
                            placeholder="Acme Corp"
                            value={addUserForm.organization}
                            onChange={(e) => setAddUserForm({ ...addUserForm, organization: e.target.value })}
                            className="w-full px-3 py-2 rounded-xl text-xs border border-slate-200 focus:outline-none focus:border-indigo-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-slate-700 mb-1">Designation</label>
                          <input
                            type="text"
                            placeholder="QA Lead"
                            value={addUserForm.designation}
                            onChange={(e) => setAddUserForm({ ...addUserForm, designation: e.target.value })}
                            className="w-full px-3 py-2 rounded-xl text-xs border border-slate-200 focus:outline-none focus:border-indigo-500"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">Password</label>
                        <input
                          type="password"
                          required
                          placeholder="Minimum 8 characters"
                          value={addUserForm.password}
                          onChange={(e) => setAddUserForm({ ...addUserForm, password: e.target.value })}
                          className="w-full px-3 py-2 rounded-xl text-xs border border-slate-200 focus:outline-none focus:border-indigo-500"
                        />
                      </div>

                      <div className="pt-2 border-t border-slate-200/80">
                        <label className="block text-[11px] font-extrabold text-slate-700 uppercase tracking-wide mb-2">Page Access Permissions</label>
                        <div className="space-y-2 bg-slate-50 p-2.5 rounded-xl border border-slate-200/60">
                          <label className="flex items-center gap-2.5 text-xs font-semibold text-slate-800 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={addUserForm.can_access_feedback !== false}
                              onChange={(e) => setAddUserForm({ ...addUserForm, can_access_feedback: e.target.checked })}
                              className="rounded text-[var(--color-accent)] focus:ring-[var(--color-accent)] w-4 h-4 cursor-pointer"
                            />
                            <span>💬 Feedback & Reviews Access</span>
                          </label>
                          <label className="flex items-center gap-2.5 text-xs font-semibold text-slate-800 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={addUserForm.can_access_observability !== false}
                              onChange={(e) => setAddUserForm({ ...addUserForm, can_access_observability: e.target.checked })}
                              className="rounded text-[var(--color-accent)] focus:ring-[var(--color-accent)] w-4 h-4 cursor-pointer"
                            />
                            <span>🛡️ AI Observability Access</span>
                          </label>
                        </div>
                      </div>

                      <div className="flex justify-end gap-2 pt-2">
                        <button
                          type="button"
                          onClick={() => { setShowAddUserModal(false); setAddUserMsg(null); }}
                          className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 border border-slate-200 hover:bg-slate-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={addUserLoading}
                          className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-[var(--color-accent)] hover:bg-indigo-700 transition-colors disabled:opacity-50"
                        >
                          {addUserLoading ? 'Creating User…' : 'Create User'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Delete Confirmation Modal */}
        {categoryMismatchConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-[16px] shadow-2xl max-w-[420px] w-full p-6">
              <h2 className="text-[16px] font-bold text-[var(--color-text-primary)] mb-2">Wrong Category?</h2>
              <p className="text-[14px] text-[var(--color-text-secondary)] mb-4">
                <span className="font-bold text-[var(--color-navy)]">{categoryMismatchConfirm.file.name}</span> looks
                like it might be a <span className="font-bold">{CATEGORY_LABELS[categoryMismatchConfirm.suggestedType]}</span> file,
                but you're uploading it under <span className="font-bold">{CATEGORY_LABELS[selectedType]}</span>. The category
                you choose here is how it will be stored and used in analysis — it isn't detected from the file itself.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setCategoryMismatchConfirm(null)}
                  className="flex-1 px-4 py-3 rounded-xl border border-[var(--color-border)] text-[var(--color-text-primary)] font-bold text-[14px] hover:bg-[var(--color-bg-alt)] transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const { file, isPrevious } = categoryMismatchConfirm;
                    setCategoryMismatchConfirm(null);
                    stageOrUploadFile(file, isPrevious);
                  }}
                  className="flex-1 px-4 py-3 rounded-xl bg-[var(--color-orange)] text-white font-bold text-[14px] hover:bg-[#ea580c] transition-all"
                >
                  Upload Anyway
                </button>
              </div>
            </div>
          </div>
        )}

        {showHistoricalSelector && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-[16px] shadow-2xl max-w-[520px] w-full p-6 max-h-[85vh] flex flex-col">
              <h2 className="text-[16px] font-bold text-[var(--color-text-primary)] mb-1">Select Historical Documents</h2>
              <p className="text-[13px] text-[var(--color-text-secondary)] mb-4">
                Choose which Historical KB files should inform this analysis. All files are selected by default.
              </p>

              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setSelectedHistoricalIds(new Set(Object.values(previousFiles).flat().map(f => f.document_id)))}
                  className="text-[12px] font-bold text-[var(--color-accent)] hover:underline"
                >
                  Select All
                </button>
                <span className="text-[var(--color-border)]">|</span>
                <button
                  type="button"
                  onClick={() => setSelectedHistoricalIds(new Set())}
                  className="text-[12px] font-bold text-[var(--color-accent)] hover:underline"
                >
                  Select None
                </button>
              </div>

              <div className="flex-1 overflow-y-auto space-y-4 pr-1">
                {[
                  { key: 'requirement', label: 'Requirements' },
                  { key: 'bug_report', label: 'Bug Reports' },
                  { key: 'test_case', label: 'Test Cases' },
                ].map(({ key, label }) => (
                  previousFiles[key].length > 0 && (
                    <div key={key}>
                      <h3 className="text-[11px] font-bold text-[var(--color-text-muted)] uppercase tracking-wide mb-2">{label}</h3>
                      <div className="space-y-1.5">
                        {previousFiles[key].map((file) => (
                          <label
                            key={file.document_id}
                            className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-[var(--color-border)] hover:bg-[var(--color-bg-alt)] cursor-pointer transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={selectedHistoricalIds.has(file.document_id)}
                              onChange={() => toggleHistoricalSelection(file.document_id)}
                              className="rounded border-[var(--color-border)] text-[var(--color-accent)] focus:ring-[var(--color-accent)] w-4 h-4 flex-shrink-0"
                            />
                            <span className="text-[13px] text-[var(--color-text-primary)] font-medium truncate">{file.filename}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )
                ))}
              </div>

              <div className="flex gap-3 mt-5 pt-4 border-t border-[var(--color-border)]">
                <button
                  onClick={() => setShowHistoricalSelector(false)}
                  className="flex-1 px-4 py-3 rounded-xl border border-[var(--color-border)] text-[var(--color-text-primary)] font-bold text-[14px] hover:bg-[var(--color-bg-alt)] transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={() => executeAnalysis(Array.from(selectedHistoricalIds))}
                  className="flex-1 px-4 py-3 rounded-xl bg-[var(--color-orange)] text-white font-bold text-[14px] hover:bg-[#ea580c] transition-all"
                >
                  Run Analysis ({selectedHistoricalIds.size} selected)
                </button>
              </div>
            </div>
          </div>
        )}

        {deleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-[16px] shadow-2xl max-w-[400px] w-full p-6">
              <h2 className="text-[16px] font-bold text-[var(--color-text-primary)] mb-2">Delete File?</h2>
              <p className="text-[14px] text-[var(--color-text-secondary)] mb-4">
                Are you sure you want to permanently delete <span className="font-bold text-[var(--color-navy)]">{deleteConfirm.filename}</span>? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 px-4 py-3 rounded-xl border border-[var(--color-border)] text-[var(--color-text-primary)] font-bold text-[14px] hover:bg-[var(--color-bg-alt)] transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteFile}
                  className="flex-1 px-4 py-3 rounded-xl bg-red-600 text-white font-bold text-[14px] hover:bg-red-700 transition-all"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Document preview: extracted text of a stored file, so what was ingested can be
          checked against the source. */}
      {previewDoc && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={closePreview}
        >
          <div
            className="bg-white rounded-[16px] shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 p-5 border-b border-[var(--color-border)]">
              <div className="min-w-0">
                <h3 className="font-['Sora'] text-[15px] font-bold text-[var(--color-navy)] truncate">{previewDoc.filename}</h3>
                <div className="text-[11px] text-[var(--color-text-muted)] mt-0.5">
                  {previewLoading ? 'Loading…' : (
                    <>
                      {previewDoc.artifact_type && (
                        <span className="uppercase font-bold mr-2">{previewDoc.artifact_type.replace('_', ' ')}</span>
                      )}
                      {previewDoc.char_count != null && <>{previewDoc.char_count.toLocaleString()} characters</>}
                      {previewDoc.chunk_count != null && <> · {previewDoc.chunk_count} chunk{previewDoc.chunk_count === 1 ? '' : 's'}</>}
                      {previewDoc.truncated && <span className="text-[var(--color-orange)] font-bold"> · preview truncated</span>}
                    </>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={closePreview}
                className="flex-shrink-0 text-[var(--color-text-muted)] hover:text-[var(--color-navy)] text-[20px] leading-none px-1"
                aria-label="Close preview"
              >
                ×
              </button>
            </div>
            <div className="p-5 overflow-y-auto">
              {previewError ? (
                <div className="text-[13px] text-red-600 font-semibold">{previewError}</div>
              ) : previewLoading ? (
                <div className="py-10 flex justify-center">
                  <div className="animate-spin h-6 w-6 border-2 border-[var(--color-accent)] border-t-transparent rounded-full" />
                </div>
              ) : (
                <pre className="text-[12px] leading-relaxed text-[var(--color-text-secondary)] whitespace-pre-wrap break-words font-mono">
                  {previewDoc.text}
                </pre>
              )}
            </div>
            {previewDoc.truncated && !previewLoading && !previewError && (
              <div className="px-5 py-3 border-t border-[var(--color-border)] text-[11px] text-[var(--color-text-muted)]">
                Only the first {previewDoc.text.length.toLocaleString()} characters are shown. The full document is still indexed for analysis.
              </div>
            )}
          </div>
        </div>
      )}

      {/* ====================================================================
          KB STORAGE LIMIT MODAL — FLOW 1 (Pre-analysis auto-save warning)
          Shows when saveToHistorical is checked and ≥1 artifact type is full.
          Lets the user continue (skipping full types) or cancel.
          ==================================================================== */}
      {kbLimitModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
          onClick={kbLimitModal.onCancel}
        >
          <div
            className="bg-white rounded-[20px] shadow-2xl max-w-[500px] w-full p-7"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <div>
                <h2 className="text-[17px] font-bold text-[var(--color-navy)]">Knowledge Base Storage Limit Reached</h2>
                <p className="text-[13px] text-[var(--color-text-secondary)] mt-1">
                  One or more artifact types are full. Files of those types won't be saved after analysis.
                </p>
              </div>
            </div>

            {/* Usage table */}
            <div className="rounded-xl border border-[var(--color-border)] overflow-hidden mb-5">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="bg-[var(--color-bg-alt)] border-b border-[var(--color-border)]">
                    <th className="text-left px-4 py-2.5 font-bold text-[var(--color-text-secondary)] text-[11px] uppercase tracking-wide">Artifact Type</th>
                    <th className="text-right px-4 py-2.5 font-bold text-[var(--color-text-secondary)] text-[11px] uppercase tracking-wide">Usage</th>
                    <th className="text-right px-4 py-2.5 font-bold text-[var(--color-text-secondary)] text-[11px] uppercase tracking-wide">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {['requirement', 'bug_report', 'test_case'].map((type) => {
                    const info = kbLimitModal.capacityStatus[type];
                    const isFull = !info.insertion_allowed;
                    return (
                      <tr key={type} className={`border-b border-[var(--color-border)] last:border-0 ${isFull ? 'bg-red-50' : 'bg-white'}`}>
                        <td className="px-4 py-3 font-medium text-[var(--color-text-primary)]">{info.label}</td>
                        <td className={`px-4 py-3 text-right font-bold tabular-nums ${isFull ? 'text-red-600' : 'text-[var(--color-text-secondary)]'}`}>
                          {info.current_count}/{info.max_allowed}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {isFull ? (
                            <span className="inline-flex items-center gap-1 text-red-600 font-semibold text-[11px] bg-red-100 px-2 py-0.5 rounded-full">
                              <span className="w-1.5 h-1.5 rounded-full bg-red-500 inline-block" /> Full
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold text-[11px] bg-emerald-50 px-2 py-0.5 rounded-full">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" /> Available
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Guidance */}
            <div className="flex items-start gap-2 text-[12px] text-[var(--color-text-muted)] bg-[var(--color-bg-alt)] rounded-lg p-3 mb-6">
              <svg className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20A10 10 0 0012 2z" />
              </svg>
              <span>
                To save the <strong>full types</strong>, go to the <strong>Historical Knowledge Base</strong> tab, delete one or more files of that type, then run the analysis again.
                Types with available space will still be saved automatically.
              </span>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3">
              <button
                id="kb-limit-modal-cancel"
                onClick={kbLimitModal.onCancel}
                className="flex-1 px-4 py-3 rounded-xl border border-[var(--color-border)] text-[var(--color-text-primary)] font-bold text-[14px] hover:bg-[var(--color-bg-alt)] transition-all"
              >
                Cancel
              </button>
              <button
                id="kb-limit-modal-continue"
                onClick={kbLimitModal.onContinue}
                className="flex-[2] px-4 py-3 rounded-xl bg-[var(--color-navy)] text-white font-bold text-[14px] hover:bg-[#1a2352] transition-all"
              >
                Continue Analysis Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ====================================================================
          KB STORAGE LIMIT MODAL — FLOW 2 (Manual Historical KB upload warning)
          Shows when a user tries to add file(s) to a full artifact type category.
          Offers: Upload Allowed Files Only / Manage Knowledge Base / Cancel.
          ==================================================================== */}
      {kbManualLimitModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
          onClick={kbManualLimitModal.onCancel}
        >
          <div
            className="bg-white rounded-[20px] shadow-2xl max-w-[520px] w-full p-7"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <div>
                <h2 className="text-[17px] font-bold text-[var(--color-navy)]">Knowledge Base Storage Limit Reached</h2>
                <p className="text-[13px] text-[var(--color-text-secondary)] mt-1">
                  {kbManualLimitModal.blockedFiles.length === 1
                    ? `${kbManualLimitModal.capacityStatus[kbManualLimitModal.blockedFiles[0].type]?.label} is at capacity (10/10).`
                    : `${kbManualLimitModal.blockedFiles.length} files can't be added — their artifact type(s) are at capacity.`}
                  {' '}No more files of this type can be added until space is freed.
                </p>
              </div>
            </div>

            {/* Usage table */}
            <div className="rounded-xl border border-[var(--color-border)] overflow-hidden mb-4">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="bg-[var(--color-bg-alt)] border-b border-[var(--color-border)]">
                    <th className="text-left px-4 py-2.5 font-bold text-[var(--color-text-secondary)] text-[11px] uppercase tracking-wide">Artifact Type</th>
                    <th className="text-right px-4 py-2.5 font-bold text-[var(--color-text-secondary)] text-[11px] uppercase tracking-wide">Usage</th>
                    <th className="text-right px-4 py-2.5 font-bold text-[var(--color-text-secondary)] text-[11px] uppercase tracking-wide">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {['requirement', 'bug_report', 'test_case'].map((type) => {
                    const info = kbManualLimitModal.capacityStatus[type];
                    const isFull = !info.insertion_allowed;
                    return (
                      <tr key={type} className={`border-b border-[var(--color-border)] last:border-0 ${isFull ? 'bg-red-50' : 'bg-white'}`}>
                        <td className="px-4 py-3 font-medium text-[var(--color-text-primary)]">{info.label}</td>
                        <td className={`px-4 py-3 text-right font-bold tabular-nums ${isFull ? 'text-red-600' : 'text-[var(--color-text-secondary)]'}`}>
                          {info.current_count}/{info.max_allowed}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {isFull ? (
                            <span className="inline-flex items-center gap-1 text-red-600 font-semibold text-[11px] bg-red-100 px-2 py-0.5 rounded-full">
                              <span className="w-1.5 h-1.5 rounded-full bg-red-500 inline-block" /> Full
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold text-[11px] bg-emerald-50 px-2 py-0.5 rounded-full">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" /> Available
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Allowed / Blocked file lists (only shown when there are files in each bucket) */}
            {kbManualLimitModal.allowedFiles.length > 0 && (
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 mb-3">
                <p className="text-[11px] font-bold text-emerald-700 uppercase tracking-wide mb-1.5">✓ Can be uploaded now</p>
                <ul className="space-y-0.5">
                  {kbManualLimitModal.allowedFiles.map((item, i) => (
                    <li key={i} className="text-[12px] text-emerald-800 flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-emerald-500 flex-shrink-0" />
                      {item.file.name}
                      <span className="text-emerald-600 text-[10px]">({kbManualLimitModal.capacityStatus[item.type]?.label})</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {kbManualLimitModal.blockedFiles.length > 0 && (
              <div className="rounded-xl bg-red-50 border border-red-200 p-3 mb-5">
                <p className="text-[11px] font-bold text-red-700 uppercase tracking-wide mb-1.5">✗ Blocked by storage limit</p>
                <ul className="space-y-0.5">
                  {kbManualLimitModal.blockedFiles.map((item, i) => (
                    <li key={i} className="text-[12px] text-red-800 flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-red-500 flex-shrink-0" />
                      {item.file.name}
                      <span className="text-red-600 text-[10px]">({kbManualLimitModal.capacityStatus[item.type]?.label} — {kbManualLimitModal.capacityStatus[item.type]?.current_count}/{kbManualLimitModal.capacityStatus[item.type]?.max_allowed})</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-col gap-2.5">
              {kbManualLimitModal.onProceed && (
                <button
                  id="kb-manual-limit-proceed"
                  onClick={kbManualLimitModal.onProceed}
                  className="w-full px-4 py-3 rounded-xl bg-[var(--color-navy)] text-white font-bold text-[14px] hover:bg-[#1a2352] transition-all"
                >
                  Upload Allowed Files Only ({kbManualLimitModal.allowedFiles.length})
                </button>
              )}
              <button
                id="kb-manual-limit-manage"
                onClick={kbManualLimitModal.onManage}
                className="w-full px-4 py-3 rounded-xl border-2 border-[var(--color-accent)] text-[var(--color-accent)] font-bold text-[14px] hover:bg-[var(--color-accent)] hover:text-white transition-all"
              >
                Manage Knowledge Base →
              </button>
              <button
                id="kb-manual-limit-cancel"
                onClick={kbManualLimitModal.onCancel}
                className="w-full px-4 py-3 rounded-xl border border-[var(--color-border)] text-[var(--color-text-secondary)] font-semibold text-[14px] hover:bg-[var(--color-bg-alt)] transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ====================================================================
          KB SAVE SUMMARY TOAST — Post-analysis notification
          Shown when save_to_historical=true and the analysis response includes
          a kb_save_summary, summarising what was saved vs skipped.
          Auto-dismisses on click.
          ==================================================================== */}
      {kbSaveSummary && (
        <div
          className="fixed bottom-6 right-6 z-[70] max-w-[380px] w-full cursor-pointer"
          onClick={() => setKbSaveSummary(null)}
          role="alert"
          aria-live="polite"
        >
          <div className={`rounded-[16px] shadow-2xl border p-4 ${
            kbSaveSummary.skipped_limit?.length > 0
              ? 'bg-amber-50 border-amber-200'
              : 'bg-emerald-50 border-emerald-200'
          }`}>
            <div className="flex items-start gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                kbSaveSummary.skipped_limit?.length > 0 ? 'bg-amber-100' : 'bg-emerald-100'
              }`}>
                {kbSaveSummary.skipped_limit?.length > 0 ? (
                  <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-[13px] font-bold mb-1 ${
                  kbSaveSummary.skipped_limit?.length > 0 ? 'text-amber-800' : 'text-emerald-800'
                }`}>
                  {kbSaveSummary.skipped_limit?.length > 0
                    ? 'Partial save to Historical KB'
                    : kbSaveSummary.saved?.length > 0
                      ? 'Saved to Historical KB'
                      : 'No new files added to Historical KB'}
                </p>
                {kbSaveSummary.saved?.length > 0 && (
                  <p className="text-[12px] text-emerald-700 mb-0.5">
                    <strong>Saved:</strong> {kbSaveSummary.saved.join(', ')}
                  </p>
                )}
                {kbSaveSummary.skipped_limit?.length > 0 && (
                  <p className="text-[12px] text-amber-700 mb-0.5">
                    <strong>Skipped (limit reached):</strong> {kbSaveSummary.skipped_limit.join(', ')}
                  </p>
                )}
                {kbSaveSummary.skipped_duplicate?.length > 0 && (
                  <p className="text-[12px] text-[var(--color-text-muted)] mb-0.5">
                    <strong>Already in KB:</strong> {kbSaveSummary.skipped_duplicate.join(', ')}
                  </p>
                )}
                <p className="text-[10px] text-[var(--color-text-muted)] mt-1.5">Click to dismiss</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

}

export default App;
